import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getItemWithExpiration, setItemWithExpiration, clearLocalStorage } from '../../lib/utils';
import {
    RE_REGISTRATION_SUCCESS_NAMES,
    alignReRegistrationSuccessToMonth,
    canOpenReRegistrationSuccessEvidence,
    monthKeyOf,
    formatReRegistrationSuccessRatio,
    formatReRegistrationSuccessTooltip,
    reRegistrationSuccessMonthLabel,
    sortMeasuredFirst,
    toCenterReRegistrationSuccessFields,
    toTrainerReRegistrationSuccessFields,
} from './centerReRegistrationSuccess';
import {
    DEGRADED_LABEL,
    MISSING_VALUE_TEXT,
    NULLABLE_DEGRADED_FIELDS,
    avgPriceOrMissing,
    countOrMissing,
    isFieldDegraded,
    readDegraded,
} from './superAdminDegraded';
import {
    TrendPoint,
    buildLinePath,
    compareTrendValueDesc,
    lastPresentPoint,
    presentValues,
    toTrendPoints,
} from './superAdminTrend';
import SuperAdminReRegistrationEvidenceModal, { SuperAdminReRegistrationEvidenceTarget } from './SuperAdminReRegistrationEvidenceModal';
import { fetchSuperAdminOverview, fetchSuperAdminRevenueTrend, fetchSuperAdminCenterDetail, fetchSuperAdminCenterTrend, fetchSuperAdminRefundAnalysis } from '../../services/useSuperAdminDashboardQueryService';
import ModalSuperAdminPasswordChange from '../../components/modal/ModalSuperAdminPasswordChange';
import { getSuperAdminNewOpportunitySummaryAPI } from '../../api/newOpportunity';
import SuperAdminTeamAnalyticsPage from './SuperAdminTeamAnalyticsPage';
import { createCenterViewSessionAPI, endCenterViewSessionAPI } from '../../api/superAdminAuth';

/* ── Theme ── */
const T = {
    bg: '#F5F7FA',
    card: '#FFFFFF',
    cardBorder: '#E2E8F0',
    cardShadow: '0 1px 3px rgba(26,48,93,0.06), 0 1px 2px rgba(26,48,93,0.04)',
    cardShadowHover: '0 4px 12px rgba(26,48,93,0.08)',
    navy: '#1A305D',
    navyLight: '#223F7A',
    blue: '#3D73DD',
    blueBg: '#ECF1FC',
    blueBorder: '#C3D4F4',
    gold: '#C4963C',
    goldLight: '#FDF6EC',
    goldBorder: '#F0DFC0',
    text1: '#1A305D',
    text2: '#5E7093',
    text3: '#8896B0',
    text4: '#A5B1C5',
    border: '#E2E8F0',
    borderLight: '#F0F3F7',
    success: '#00A793',
    successBg: '#E5F8F3',
    error: '#FF3E4A',
    errorBg: '#FFE8E9',
    warn: '#FF8777',
    warnBg: '#FFF0ED',
    headerBg: '#FFFFFF',
    headerBorder: '#E8ECF1',
    inputBg: '#F8FAFC',
};

const COLORS = ['#3D73DD', '#00A793', '#C4963C', '#9E76F3', '#FF8777', '#17B5CB', '#FD8FE5', '#648FE4', '#34d399', '#fb923c'];
// 백엔드가 함께 배포되는 기능이므로 기본 활성화한다. 운영 중 긴급 차단이 필요할 때만 false를 지정한다.
const CENTER_VIEW_ENABLED = process.env.REACT_APP_CENTER_VIEW_ENABLED !== 'false';
const CENTER_VIEW_ALLOWED_SUPER_ADMIN_IDS = (process.env.REACT_APP_CENTER_VIEW_SUPER_ADMIN_IDS || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
const isCenterViewEnabledFor = (superAdminId: string) =>
    CENTER_VIEW_ENABLED &&
    (CENTER_VIEW_ALLOWED_SUPER_ADMIN_IDS.length === 0 || CENTER_VIEW_ALLOWED_SUPER_ADMIN_IDS.includes(superAdminId));

/* ── Skeleton Components ── */
const shimmerBg = `linear-gradient(90deg, ${T.borderLight} 25%, ${T.border} 50%, ${T.borderLight} 75%)`;
const SkeletonBox = ({ w, h, r = 6, mb = 0 }: { w: string | number; h: number; r?: number; mb?: number }) => (
    <div style={{
        width: w, height: h, borderRadius: r, marginBottom: mb,
        background: shimmerBg, backgroundSize: '800px 100%',
        animation: 'shimmer 1.5s infinite linear',
    }} />
);

const SkeletonKpiCards = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ background: T.card, borderRadius: 14, padding: '20px 22px', border: `1px solid ${T.cardBorder}`, overflow: 'hidden' }}>
                <SkeletonBox w={80} h={12} mb={14} />
                <SkeletonBox w={160} h={28} mb={10} />
                <SkeletonBox w={100} h={16} />
            </div>
        ))}
    </div>
);

const SkeletonChart = () => (
    <div style={{ background: T.card, borderRadius: 14, padding: '24px 28px', border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
            <SkeletonBox w={100} h={20} />
            <SkeletonBox w={180} h={30} r={8} />
        </div>
        <SkeletonBox w="100%" h={260} r={8} />
        <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
            {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonBox key={i} w={80} h={28} r={6} />)}
        </div>
    </div>
);

/**
 * 추이 그래프 빈 상태 — 서버 추이가 없을 때 가짜 숫자 대신 보여준다.
 * failed: 불러오기 실패(다시 불러오기 단추), 아니면 「추이 데이터가 없어요」.
 */
const TrendEmptyState = ({ failed, onRetry, loading }: { failed?: boolean; onRetry?: () => void; loading?: boolean }) => (
    <div
        style={{
            height: 260,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            background: T.inputBg,
            border: `1px dashed ${T.border}`,
            borderRadius: 10,
        }}
    >
        {loading ? (
            <span style={{ fontSize: 14, color: T.text3 }}>추이를 불러오는 중이에요</span>
        ) : failed ? (
            <>
                <span style={{ fontSize: 14, color: T.text2, fontWeight: 600 }}>추이를 불러오지 못했어요</span>
                {onRetry && (
                    <button
                        type="button"
                        onClick={onRetry}
                        style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.border}`, background: T.card, color: T.blue, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                        다시 불러오기
                    </button>
                )}
            </>
        ) : (
            <span style={{ fontSize: 14, color: T.text3 }}>추이 데이터가 없어요</span>
        )}
    </div>
);

const SkeletonRanking = () => (
    <div style={{ background: T.card, borderRadius: 14, padding: '24px 28px', border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <SkeletonBox w={120} h={20} />
            <SkeletonBox w={180} h={30} r={8} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[0, 1, 2].map((i) => <SkeletonBox key={i} w={70} h={30} r={8} />)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <SkeletonBox w={26} h={26} r={13} />
                    <SkeletonBox w={90} h={16} />
                    <div style={{ flex: 1 }}><SkeletonBox w="100%" h={28} r={6} /></div>
                </div>
            ))}
        </div>
    </div>
);

/* ── Ranking Metric Config ── */
type MetricUnit = 'money' | 'num' | 'pct';
interface MetricConfig {
    key: string;
    label: string;
    unit: MetricUnit;
    periodFilterable: boolean; // true: 기간별 필터 가능, false: 실시간 스냅샷
    monthOnly?: boolean; // 월 단위 baseDate 만 노출 (granularity 토글 숨김) — 센터 통계와 같은 뜻
}
interface RankCategory {
    cat: string;
    color: string;
    metrics: MetricConfig[];
}

// 지점 랭킹용 카테고리 (리뷰평점 없음 — 센터 단위에서 의미 없음)
const CENTER_RANK_CATEGORIES: RankCategory[] = [
    {
        cat: '매출',
        color: T.blue,
        metrics: [
            { key: 'revenue', label: '매출액', unit: 'money', periodFilterable: true },
            { key: 'undeliveredRevenue', label: '미수업금액(부채)', unit: 'money', periodFilterable: false },
            { key: 'avgPricePerClass', label: '평균 회당단가 (PT)', unit: 'money', periodFilterable: true },
        ],
    },
    {
        cat: '수업',
        color: T.success,
        metrics: [
            { key: 'totalClasses', label: '진행한 수업수', unit: 'num', periodFilterable: true },
            { key: 'noShowCount', label: '노쇼수', unit: 'num', periodFilterable: true },
            { key: 'sameDayCancelCount', label: '당일취소', unit: 'num', periodFilterable: true },
            { key: 'sameDayChangeCount', label: '당일변경', unit: 'num', periodFilterable: true },
            { key: 'journalCount', label: '일지작성수', unit: 'num', periodFilterable: true },
            { key: 'journalWriteRate', label: '일지 작성률', unit: 'pct', periodFilterable: true },
        ],
    },
    {
        cat: '결제',
        color: T.gold,
        metrics: [
            { key: 'totalMembers', label: '활성 회원', unit: 'num', periodFilterable: false },
            { key: 'newPayMembers', label: '신규결제', unit: 'num', periodFilterable: true },
            { key: 'reRegPayMembers', label: '재등록결제', unit: 'num', periodFilterable: true },
            { key: 'reRegistrationSuccessRate', label: '재등록 성공률', unit: 'pct', periodFilterable: false, monthOnly: true },
        ],
    },
];

// 강사 랭킹용 카테고리 (리뷰평점 포함) — 재등록 성공률이 없는 기본 목록.
// 지점 상세의 강사 랭킹(center-detail API — 재등록 성공률 필드 없음)과 차트는 이 목록을 쓴다.
const TRAINER_RANK_CATEGORIES_BASE: RankCategory[] = [
    {
        cat: '매출',
        color: T.blue,
        metrics: [
            { key: 'revenue', label: '매출액', unit: 'money', periodFilterable: true },
            { key: 'undeliveredRevenue', label: '미수업금액(부채)', unit: 'money', periodFilterable: false },
            { key: 'avgPricePerClass', label: '평균 회당단가 (PT)', unit: 'money', periodFilterable: true },
        ],
    },
    {
        cat: '수업',
        color: T.success,
        metrics: [
            { key: 'totalClasses', label: '진행한 수업수', unit: 'num', periodFilterable: true },
            { key: 'noShowCount', label: '노쇼수', unit: 'num', periodFilterable: true },
            { key: 'sameDayCancelCount', label: '당일취소', unit: 'num', periodFilterable: true },
            { key: 'sameDayChangeCount', label: '당일변경', unit: 'num', periodFilterable: true },
            { key: 'journalCount', label: '일지작성수', unit: 'num', periodFilterable: true },
            { key: 'journalWriteRate', label: '일지 작성률', unit: 'pct', periodFilterable: true },
            { key: 'reviewAvg', label: '리뷰평점', unit: 'num', periodFilterable: false },
        ],
    },
    {
        cat: '결제',
        color: T.gold,
        metrics: [
            { key: 'totalMembers', label: '활성 회원', unit: 'num', periodFilterable: false },
            { key: 'newPayMembers', label: '신규결제', unit: 'num', periodFilterable: true },
            { key: 'reRegPayMembers', label: '재등록결제', unit: 'num', periodFilterable: true },
        ],
    },
];

// 대시보드 강사 랭킹(overview API) — 「결제」에 재등록 성공률(월 단위)을 더한다. 지점 랭킹과 같은 설정.
const TRAINER_RANK_CATEGORIES: RankCategory[] = TRAINER_RANK_CATEGORIES_BASE.map((cat) =>
    cat.cat === '결제'
        ? {
              ...cat,
              metrics: [
                  ...cat.metrics,
                  { key: 'reRegistrationSuccessRate', label: '재등록 성공률', unit: 'pct', periodFilterable: false, monthOnly: true },
              ],
          }
        : cat
);

const REFUND_RANK_CATEGORIES: RankCategory[] = [
    {
        cat: '환불',
        color: T.error,
        metrics: [
            { key: 'refundAmount', label: '환불금액', unit: 'money', periodFilterable: true },
            { key: 'refundCount', label: '환불건수', unit: 'num', periodFilterable: true },
            { key: 'refundMemberCount', label: '환불회원', unit: 'num', periodFilterable: true },
            { key: 'refundRate', label: '환불율', unit: 'pct', periodFilterable: true },
        ],
    },
    {
        cat: '유형',
        color: T.gold,
        metrics: [
            { key: 'fullRefundAmount', label: '전체환불액', unit: 'money', periodFilterable: true },
            { key: 'fullRefundCount', label: '전체환불건수', unit: 'num', periodFilterable: true },
            { key: 'partialRefundAmount', label: '부분환불액', unit: 'money', periodFilterable: true },
            { key: 'partialRefundCount', label: '부분환불건수', unit: 'num', periodFilterable: true },
        ],
    },
    {
        cat: '매출',
        color: T.blue,
        metrics: [
            { key: 'grossSales', label: '총판매액', unit: 'money', periodFilterable: true },
            { key: 'netSales', label: '순매출', unit: 'money', periodFilterable: true },
        ],
    },
];

// 차트용 카테고리 (periodFilterable만, 스냅샷 메트릭 제외)
const CHART_CATEGORIES: RankCategory[] = TRAINER_RANK_CATEGORIES_BASE.map(cat => ({
    ...cat,
    metrics: cat.metrics.filter(m => m.periodFilterable),
})).filter(cat => cat.metrics.length > 0);

// 기본값 (하위 호환)
const RANK_CATEGORIES = TRAINER_RANK_CATEGORIES_BASE;

/* ── Helpers ── */
const fmt = (n: number) => {
    const v = n ?? 0;
    if (v >= 100000000) return (Math.floor((v / 100000000) * 10) / 10).toFixed(1) + '억';
    if (v >= 10000) return (v / 10000).toLocaleString('ko-KR', { maximumFractionDigits: 0 }) + '만';
    return v.toLocaleString('ko-KR');
};
const normalizeOpportunityRatePercent = (
    value: unknown,
    rateUnit?: string
): number | null => {
    const numeric = Number(value);
    if (value === null || value === undefined || !Number.isFinite(numeric)) {
        return null;
    }
    return rateUnit === 'PERCENT'
        ? numeric
        : Math.abs(numeric) <= 1
        ? numeric * 100
        : numeric;
};
const formatOpportunityRate = (value: unknown, rateUnit?: string) => {
    const percent = normalizeOpportunityRatePercent(value, rateUnit);
    return percent === null ? '-' : `${Math.round(percent * 10) / 10}%`;
};
const fmtFull = (n: number) => (n ?? 0).toLocaleString('ko-KR') + '원';
const pctChange = (c: number, p: number) => (p ? ((c - p) / p) * 100 : 0);
const toNumber = (v: unknown) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
};
const toNullableNumber = (v: unknown) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const fmtVal = (v: unknown, unit?: string, key?: string) => {
    const n = toNullableNumber(v);
    if (key === 'reviewAvg') return n ? `★ ${n.toFixed(1)}` : '-';
    if (unit === 'pct') return n != null ? `${n.toFixed(1)}%` : '-';
    if (key === 'avgPricePerClass') return n ? `${n.toLocaleString('ko-KR')}원` : '-';
    if (unit === 'money') return fmt(n ?? 0);
    return n ?? 0;
};

/* ── 집계 지연(서버 degraded) 표시 — 규칙은 superAdminDegraded.ts ── */
const DegradedBadge = () => (
    <span
        title="서버 집계가 늦어 이 숫자는 아직 확정되지 않았어요"
        style={{ fontSize: 11, fontWeight: 600, color: T.gold, background: '#FBF5EA', padding: '1px 5px', borderRadius: 3, marginLeft: 4, whiteSpace: 'nowrap', verticalAlign: 'middle' }}
    >
        {DEGRADED_LABEL}
    </span>
);
/** 집계 지연이면 숫자 옆에 표시를 붙인다. 아니면 node 를 그대로 돌려준다(옛 응답 화면 그대로) */
const withDegraded = (node: React.ReactNode, degraded: readonly string[] | undefined, field: string): React.ReactNode =>
    isFieldDegraded(degraded, field) ? (
        <>
            {node}
            <DegradedBadge />
        </>
    ) : (
        node
    );
/** 빈 값(null)이면 「—」(+집계 지연), 아니면 render(v) */
const missingOr = (v: unknown, degraded: readonly string[] | undefined, field: string, render: () => React.ReactNode): React.ReactNode =>
    v === null ? withDegraded(<span style={{ color: T.text4 }}>{MISSING_VALUE_TEXT}</span>, degraded, field) : withDegraded(render(), degraded, field);

type NewOpportunityCoachSortKey =
    | 'centerName'
    | 'coachName'
    | 'opportunityCount'
    | 'leadMemberCount'
    | 'closedMemberCount'
    | 'closingRate';
type SortDirection = 'asc' | 'desc';

/* ── Badge ── */
const Badge = ({ value, suffix = '%' }: { value: number; suffix?: string }) => {
    const pos = value > 0;
    const zero = Math.abs(value) < 0.1;
    const abs = Math.abs(value);
    const display = abs >= 100 ? Math.round(abs).toString() : abs.toFixed(1);
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                padding: '2px 8px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                background: zero ? T.borderLight : pos ? T.successBg : T.errorBg,
                color: zero ? T.text3 : pos ? T.success : T.error,
            }}
        >
            {zero ? '—' : pos ? '▲' : '▼'} {display}{suffix}
        </span>
    );
};

/* ── Rank Badge ── */
const RankBadge = ({ rank }: { rank: number }) => {
    const styles: Record<number, { bg: string; color: string }> = {
        1: { bg: 'linear-gradient(135deg,#C4963C,#A67B2B)', color: '#fff' },
        2: { bg: 'linear-gradient(135deg,#8896B0,#5E7093)', color: '#fff' },
        3: { bg: 'linear-gradient(135deg,#C4863C,#8B6028)', color: '#fff' },
    };
    const s = styles[rank];
    if (!s)
        return (
            <span style={{ color: T.text3, fontSize: 13, fontWeight: 500, width: 28, textAlign: 'center', display: 'inline-block' }}>
                {rank}
            </span>
        );
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: '50%',
                fontSize: 13,
                fontWeight: 800,
                background: s.bg,
                color: s.color,
                boxShadow: '0 2px 6px rgba(196,150,60,0.25)',
            }}
        >
            {rank}
        </span>
    );
};

/* ── Period Toggle ── */
const PERIODS = [
    { key: 'yearly', label: '연별' },
    { key: 'monthly', label: '월별' },
    { key: 'weekly', label: '주별' },
    { key: 'daily', label: '일별' },
];

const getDateLabelFromBase = (period: string, baseDate: Date): string => {
    const y = baseDate.getFullYear();
    const m = baseDate.getMonth() + 1;
    const d = baseDate.getDate();
    const day = baseDate.getDay();
    if (period === 'yearly') return `${y}년`;
    if (period === 'monthly') return `${y}년 ${m}월`;
    if (period === 'weekly') {
        const mon = new Date(baseDate);
        mon.setDate(d - ((day + 6) % 7));
        const sun = new Date(mon);
        sun.setDate(mon.getDate() + 6);
        return `${mon.getMonth() + 1}/${mon.getDate()} ~ ${sun.getMonth() + 1}/${sun.getDate()}`;
    }
    return `${m}/${d} (${['일','월','화','수','목','금','토'][day]})`;
};

const getPeriodDateLabel = (period: string): string => getDateLabelFromBase(period, new Date());

// 해당 월의 주 목록 계산 (월요일 시작)
// 해당 월 내 주차 계산 (일~토 기준, 월 경계로 잘림)
const getWeeksOfMonth = (year: number, month: number): { week: number; start: Date; end: Date; label: string }[] => {
    const weeks: { week: number; start: Date; end: Date; label: string }[] = [];
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    let cursor = new Date(firstDay);
    let weekNum = 1;
    while (cursor <= lastDay) {
        const start = new Date(cursor);
        const daysToSat = 6 - cursor.getDay(); // 일(0)→6, 월(1)→5, ..., 토(6)→0
        const end = new Date(cursor);
        end.setDate(cursor.getDate() + daysToSat);
        if (end > lastDay) end.setTime(lastDay.getTime());
        const label = `${weekNum}주(${start.getMonth()+1}/${start.getDate()}~${end.getMonth()+1}/${end.getDate()})`;
        weeks.push({ week: weekNum, start, end, label });
        weekNum++;
        cursor = new Date(end);
        cursor.setDate(cursor.getDate() + 1);
    }
    return weeks;
};

// ◀▶는 항상 월 단위로만 이동 (period와 무관)
const shiftBaseDate = (current: Date, period: string, direction: -1 | 1): Date => {
    const d = new Date(current);
    if (period === 'yearly') d.setFullYear(d.getFullYear() + direction);
    else d.setMonth(d.getMonth() + direction);
    return d;
};

// YYYY-MM-DD 전체 (주별/일별 구분용), YYYY-MM (백엔드 호환)
const baseDateToParamFull = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};
const baseDateToParamMonth = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
};
// 백엔드가 YYYY-MM-DD 지원하면 baseDateToParamFull로 교체
const baseDateToParam = baseDateToParamMonth;

const isSamePeriod = (d: Date, period: string): boolean => {
    const now = new Date();
    if (period === 'yearly') return d.getFullYear() === now.getFullYear();
    if (period === 'daily') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
    if (period === 'weekly') {
        const getWeekStart = (dt: Date) => { const r = new Date(dt); r.setDate(r.getDate() - ((r.getDay() + 6) % 7)); r.setHours(0,0,0,0); return r.getTime(); };
        return getWeekStart(d) === getWeekStart(now);
    }
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

// 하위 호환
const isSameMonth = (d: Date): boolean => isSamePeriod(d, 'monthly');

const navBtnStyle: React.CSSProperties = {
    background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 5,
    cursor: 'pointer', padding: '4px 8px', fontSize: 14, color: T.text2, fontWeight: 700, lineHeight: 1,
};

const PeriodToggle = ({ value: rawValue, onChange, loading = false, baseDate, onBaseDateChange, monthOnly = false }: {
    value: string; onChange: (v: string) => void; loading?: boolean;
    baseDate?: Date; onBaseDateChange?: (d: Date) => void;
    /** 월 단위 지표 — period 가 무엇이든 ◀▶ 를 월 단위로 움직이고 기간 토글을 숨긴다 (센터 통계와 같음) */
    monthOnly?: boolean;
}) => {
    const value = monthOnly ? 'monthly' : rawValue;
    const hasNav = !!baseDate && !!onBaseDateChange;
    const label = hasNav ? (value === 'yearly' ? `${baseDate.getFullYear()}년` : `${baseDate.getFullYear()}년 ${baseDate.getMonth() + 1}월`) : getPeriodDateLabel(value);
    const isCurrent = hasNav ? (value === 'yearly' ? baseDate.getFullYear() === new Date().getFullYear() : isSamePeriod(baseDate, 'monthly')) : true;

    return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* 날짜 네비게이션 */}
        {hasNav ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', minWidth: 160, flexShrink: 0 }}>
                {!isCurrent ? (
                    <button onClick={() => onBaseDateChange!(new Date())} style={{ ...navBtnStyle, fontSize: 13, padding: '2px 8px', color: T.blue, borderColor: T.blueBorder, marginRight: 2 }}>
                        오늘
                    </button>
                ) : <div style={{ width: 34 }} />}
                <button style={navBtnStyle} onClick={() => onBaseDateChange!(shiftBaseDate(baseDate, value, -1))}>◀</button>
                <span style={{ fontSize: 14, color: isCurrent ? T.text3 : T.blue, fontWeight: 600, minWidth: 90, textAlign: 'center' }}>{label}</span>
                <button style={{ ...navBtnStyle, opacity: isCurrent ? 0.3 : 1 }} disabled={isCurrent} onClick={() => onBaseDateChange!(shiftBaseDate(baseDate, value, 1))}>▶</button>
            </div>
        ) : (
            <span style={{ fontSize: 13, color: T.text3, fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</span>
        )}
        {/* 기간 토글 */}
        {!monthOnly && (
        <div style={{ display: 'flex', gap: 2, background: T.inputBg, borderRadius: 8, padding: 2, border: `1px solid ${T.border}`, flexShrink: 0 }}>
        {PERIODS.map((p) => {
            const isActive = value === p.key;
            const isLoading = isActive && loading;
            return (
                <button
                    key={p.key}
                    onClick={() => onChange(p.key)}
                    style={{
                        padding: '7px 18px',
                        borderRadius: 7,
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                        background: isActive ? T.card : 'transparent',
                        color: isActive ? T.navy : T.text3,
                        boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                    }}
                >
                    {isLoading && (
                        <div style={{
                            width: 10, height: 10,
                            border: `1.5px solid ${T.blueBorder}`, borderTopColor: T.blue,
                            borderRadius: '50%', animation: 'spin 0.7s linear infinite',
                            flexShrink: 0,
                        }} />
                    )}
                    {p.label}
                </button>
            );
        })}
        </div>
        )}
    </div>
    );
};

/* ── MultiLine Chart ── */
const MultiLineChart = ({
    centers,
    visibleCenters,
    showTotal,
    trendData,
    failed,
    onRetry,
}: {
    centers: any[];
    visibleCenters: Set<number>;
    showTotal: boolean;
    trendData?: { labels: string[]; series: { centerId: string; centerName: string; data: TrendPoint[] }[]; totalData: TrendPoint[] } | null;
    /** 추이 조회 실패 — 빈 상태에 다시 불러오기 */
    failed?: boolean;
    onRetry?: () => void;
}) => {
    const [hoverIdx, setHoverIdx] = useState<number | null>(null);

    // 서버 추이만 그린다. 없으면 빈 상태(예전의 임의 배율 가짜 추이는 없앴다)
    const labels: string[] = trendData?.labels?.length ? trendData.labels : [];

    // 없는 점(null)·시리즈에 없는 센터는 0 으로 채우지 않는다 — 선을 끊는다
    const getData = (c: any): TrendPoint[] =>
        toTrendPoints(trendData?.series?.find((s: any) => s.centerId === c.centerId)?.data, labels.length);

    const totalD: TrendPoint[] = toTrendPoints(trendData?.totalData, labels.length);
    let allV: number[] = [];
    if (showTotal) allV.push(...presentValues(totalD));
    centers.forEach((c) => {
        if (visibleCenters.has(c.id)) allV.push(...presentValues(getData(c)));
    });
    if (!allV.length) allV = presentValues(totalD);
    if (!labels.length || !allV.length) return <TrendEmptyState failed={failed && !labels.length} onRetry={onRetry} />;
    const maxR = Math.max(...allV),
        minR = Math.min(...allV),
        pad = (maxR - minR) * 0.18 || maxR * 0.1;
    const yMax = maxR + pad,
        yMin = Math.max(0, minR - pad);
    const W = 1100,
        H = 340,
        px = 64,
        py = 24,
        pb = 40,
        cW = W - px - 100,
        cH = H - py - pb;
    const getY = (v: number) => (yMax === yMin ? py + cH / 2 : py + cH - ((v - yMin) / (yMax - yMin)) * cH);
    const getX = (i: number) => px + (i / Math.max(labels.length - 1, 1)) * cW;
    const gridN = 5;
    const gridV = Array.from({ length: gridN + 1 }, (_, i) => yMin + (i / gridN) * (yMax - yMin));
    const pathD = (d: TrendPoint[]) => buildLinePath(d, getX, getY);
    const totalLast = lastPresentPoint(totalD);

    // 호버 시 표시할 데이터 목록 — 없는 점은 0원이 아니라 「—」
    const getTooltipItems = (idx: number) => {
        const total: { name: string; value: TrendPoint; color: string }[] = [];
        const centerItems: { name: string; value: TrendPoint; color: string }[] = [];
        if (showTotal) {
            total.push({ name: '전체 합산', value: totalD[idx], color: T.navy });
        }
        centers.forEach((c, ci) => {
            if (visibleCenters.has(c.id)) {
                const d = getData(c);
                centerItems.push({ name: c.name, value: d[idx], color: COLORS[ci % COLORS.length] });
            }
        });
        centerItems.sort((a, b) => compareTrendValueDesc(a.value, b.value));
        return [...total, ...centerItems];
    };

    // 호버 영역 너비 계산 (각 데이터 포인트별)
    const colW = labels.length > 1 ? cW / (labels.length - 1) : cW;

    return (
        <div style={{ position: 'relative' }}>
            <svg
                viewBox={`0 0 ${W} ${H}`}
                style={{ width: '100%', display: 'block' }}
                onMouseLeave={() => setHoverIdx(null)}
            >
                {/* 그리드 */}
                {gridV.map((v, i) => {
                    const y = getY(v);
                    return (
                        <g key={i}>
                            <line x1={px} x2={px + cW} y1={y} y2={y} stroke={T.borderLight} strokeWidth={1} />
                            <text x={px - 10} y={y + 4} textAnchor="end" fill={T.text3} fontSize={11} fontFamily="inherit">
                                {fmt(v)}
                            </text>
                        </g>
                    );
                })}
                {/* X축 라벨 */}
                {labels.map((l: string, i: number) => (
                    <text
                        key={i}
                        x={getX(i)}
                        y={H - 10}
                        textAnchor="middle"
                        fill={hoverIdx === i ? T.navy : T.text3}
                        fontSize={11}
                        fontWeight={hoverIdx === i ? 700 : 400}
                        fontFamily="inherit"
                    >
                        {l}
                    </text>
                ))}
                {/* 호버 세로선 */}
                {hoverIdx !== null && (
                    <line
                        x1={getX(hoverIdx)}
                        x2={getX(hoverIdx)}
                        y1={py}
                        y2={py + cH}
                        stroke={T.blue}
                        strokeWidth={1}
                        strokeDasharray="4 3"
                        opacity={0.5}
                    />
                )}
                {/* 전체 합산 라인 */}
                {showTotal && (
                    <g>
                        <path d={pathD(totalD)} fill="none" stroke={T.navy} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
                        {totalD.map((v: TrendPoint, i: number) =>
                            v === null ? null : (
                                <circle
                                    key={i}
                                    cx={getX(i)}
                                    cy={getY(v)}
                                    r={hoverIdx === i ? 5 : 3.5}
                                    fill={hoverIdx === i ? T.navy : T.card}
                                    stroke={T.navy}
                                    strokeWidth={2}
                                    style={{ transition: 'r 0.15s' }}
                                />
                            )
                        )}
                        {totalLast && (
                            <text x={px + cW + 10} y={getY(totalLast.value) + 4} fill={T.navy} fontSize={11} fontWeight={700} fontFamily="inherit">
                                전체 {fmt(totalLast.value)}
                            </text>
                        )}
                    </g>
                )}
                {/* 센터별 라인 */}
                {centers.map((c, ci) => {
                    if (!visibleCenters.has(c.id)) return null;
                    const cl = COLORS[ci % COLORS.length];
                    const d = getData(c);
                    const last = lastPresentPoint(d);
                    if (!last) return null;
                    return (
                        <g key={c.id}>
                            <path d={pathD(d)} fill="none" stroke={cl} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
                            {d.map((v: TrendPoint, i: number) =>
                                v === null ? null : (
                                    <circle
                                        key={i}
                                        cx={getX(i)}
                                        cy={getY(v)}
                                        r={hoverIdx === i ? 4.5 : 3}
                                        fill={hoverIdx === i ? cl : T.card}
                                        stroke={cl}
                                        strokeWidth={2}
                                        style={{ transition: 'r 0.15s' }}
                                    />
                                )
                            )}
                            <text x={px + cW + 10} y={getY(last.value) + 4} fill={cl} fontSize={10} fontWeight={600} fontFamily="inherit">
                                {c.name}
                            </text>
                        </g>
                    );
                })}
                {/* 호버 감지 영역 (투명 rect) */}
                {labels.map((_: string, i: number) => (
                    <rect
                        key={i}
                        x={getX(i) - colW / 2}
                        y={py}
                        width={colW}
                        height={cH}
                        fill="transparent"
                        onMouseEnter={() => setHoverIdx(i)}
                        style={{ cursor: 'pointer' }}
                    />
                ))}
            </svg>
            {/* 툴팁 (SVG 외부에 HTML로 렌더링) */}
            {hoverIdx !== null && (() => {
                const items = getTooltipItems(hoverIdx);
                // 차트 영역 대비 비율로 위치 계산
                const xPct = ((getX(hoverIdx) - 0) / W) * 100;
                const isRight = xPct > 60;
                return (
                    <div
                        style={{
                            position: 'absolute',
                            top: 20,
                            left: isRight ? undefined : `${xPct + 2}%`,
                            right: isRight ? `${100 - xPct + 2}%` : undefined,
                            background: '#fff',
                            border: `1px solid ${T.border}`,
                            borderRadius: 10,
                            padding: '12px 16px',
                            boxShadow: '0 4px 16px rgba(26,48,93,0.12)',
                            zIndex: 10,
                            minWidth: 200,
                            pointerEvents: 'none',
                        }}
                    >
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.navy, marginBottom: 8, borderBottom: `1px solid ${T.borderLight}`, paddingBottom: 6 }}>
                            {labels[hoverIdx]}
                        </div>
                        {items.slice(0, 15).map((item, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                                    <span style={{ fontSize: 13, color: T.text2, whiteSpace: 'nowrap' }}>{item.name}</span>
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 700, color: T.navy, fontVariantNumeric: 'tabular-nums', marginLeft: 16 }}>
                                    {item.value === null ? MISSING_VALUE_TEXT : fmtFull(item.value)}
                                </span>
                            </div>
                        ))}
                        {items.length > 15 && (
                            <div style={{ fontSize: 12, color: T.text4, textAlign: 'center', marginTop: 6, paddingTop: 6, borderTop: `1px solid ${T.borderLight}` }}>
                                외 {items.length - 15}개 센터
                            </div>
                        )}
                    </div>
                );
            })()}
        </div>
    );
};

/* ── Multi-Select Dropdown ── */
const CenterMultiSelect = ({
    centers,
    selected,
    onChange,
}: {
    centers: any[];
    selected: Set<string>;
    onChange: (s: Set<string>) => void;
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const filtered = centers.filter((c: any) => c.name.includes(search));
    const selectedCount = centers.filter((c: any) => selected.has(c.name)).length;
    const allSelected = centers.length > 0 && selectedCount === centers.length;
    const noneSelected = selectedCount === 0;
    const count = selectedCount;

    const toggle = (name: string) => {
        const next = new Set(selected);
        next.has(name) ? next.delete(name) : next.add(name);
        onChange(next);
    };
    const selectAll = () => onChange(new Set(centers.map((c: any) => c.name)));
    const clearAll = () => onChange(new Set());

    return (
        <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
                onClick={() => setOpen((o) => !o)}
                style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    border: `1px solid ${T.border}`,
                    background: T.card,
                    color: T.text2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 140,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                <span style={{ whiteSpace: 'nowrap' }}>{allSelected ? '전체 지점' : noneSelected ? '지점 선택' : `${count}개 지점 선택`}</span>
                <span style={{ marginLeft: 'auto', fontSize: 13, color: T.text4 }}>{open ? '▲' : '▼'}</span>
            </button>
            {open && (
                <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }} onClick={() => setOpen(false)} />
                    <div
                        style={{
                            position: 'absolute',
                            top: 'calc(100% + 6px)',
                            right: 0,
                            zIndex: 201,
                            width: 240,
                            maxHeight: 340,
                            background: T.card,
                            border: `1px solid ${T.border}`,
                            borderRadius: 12,
                            boxShadow: '0 12px 40px rgba(26,48,93,0.12)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div style={{ padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}` }}>
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="지점 검색..."
                                style={{
                                    width: '100%',
                                    padding: '7px 10px',
                                    borderRadius: 6,
                                    border: `1px solid ${T.border}`,
                                    background: T.inputBg,
                                    color: T.text1,
                                    fontSize: 13,
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        <div style={{ padding: '8px 12px', display: 'flex', gap: 6, borderBottom: `1px solid ${T.borderLight}` }}>
                            <button
                                onClick={selectAll}
                                style={{
                                    flex: 1,
                                    padding: '5px 0',
                                    borderRadius: 6,
                                    border: `1px solid ${T.border}`,
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    background: allSelected ? T.blueBg : 'transparent',
                                    color: allSelected ? T.blue : T.text3,
                                }}
                            >
                                전체
                            </button>
                            <button
                                onClick={clearAll}
                                style={{
                                    flex: 1,
                                    padding: '5px 0',
                                    borderRadius: 6,
                                    border: `1px solid ${T.border}`,
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    background: 'transparent',
                                    color: T.text3,
                                }}
                            >
                                초기화
                            </button>
                        </div>
                        <div style={{ overflowY: 'auto', maxHeight: 220, padding: '4px 0' }}>
                            {filtered.map((c: any) => {
                                const isChecked = selected.has(c.name);
                                return (
                                    <div
                                        key={c.name}
                                        onClick={() => toggle(c.name)}
                                        style={{
                                            padding: '8px 14px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            background: isChecked ? T.blueBg + '40' : 'transparent',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = T.borderLight)}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = isChecked ? T.blueBg + '40' : 'transparent')}
                                    >
                                        <div
                                            style={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: 4,
                                                flexShrink: 0,
                                                border: isChecked ? 'none' : `1.5px solid ${T.text4}`,
                                                background: isChecked ? T.blue : 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {isChecked && <span style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>✓</span>}
                                        </div>
                                        <span style={{ fontSize: 13, color: isChecked ? T.text1 : T.text3, fontWeight: isChecked ? 600 : 400 }}>
                                            {c.name}
                                        </span>
                                    </div>
                                );
                            })}
                            {filtered.length === 0 && (
                                <div style={{ padding: 16, textAlign: 'center', color: T.text4, fontSize: 12 }}>검색 결과가 없습니다</div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

/* ── Ranking Section ── */
const RankingSection = ({
    title,
    data,
    nameKey = 'name',
    subKey,
    filterComponent,
    rankingPeriod,
    onRankingPeriodChange,
    colorFn,
    onNameClick,
    categories = RANK_CATEGORIES,
    loading = false,
    baseDate,
    onBaseDateChange,
    selectedWeek,
    onWeekChange,
    selectedDay,
    onDayChange,
    onReRegistrationSuccessEvidence,
}: {
    title: string;
    data: any[];
    nameKey?: string;
    subKey?: string;
    filterComponent?: React.ReactNode;
    rankingPeriod?: string;
    onRankingPeriodChange?: (v: string) => void;
    colorFn?: (item: any, i: number) => string;
    onNameClick?: (item: any) => void;
    categories?: RankCategory[];
    baseDate?: Date;
    onBaseDateChange?: (d: Date) => void;
    loading?: boolean;
    selectedWeek?: number;
    onWeekChange?: (w: number | undefined) => void;
    selectedDay?: number;
    onDayChange?: (d: number | undefined) => void;
    /** 재등록 성공률 막대 클릭 → 근거 모달. 없으면 막대는 클릭되지 않는다(기존 동작) */
    onReRegistrationSuccessEvidence?: (item: any) => void;
}) => {
    const [activeCat, setActiveCat] = useState(categories[0].cat);
    const [activeMetric, setActiveMetric] = useState(categories[0].metrics[0].key);
    const [expanded, setExpanded] = useState(false);

    // 주별 서브탭 데이터
    const weeks = baseDate && rankingPeriod === 'weekly' ? getWeeksOfMonth(baseDate.getFullYear(), baseDate.getMonth() + 1) : [];
    const daysInMonth = baseDate ? new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0).getDate() : 31;

    const currentCat = categories.find((c) => c.cat === activeCat);
    const currentMetricConf = currentCat?.metrics.find((m) => m.key === activeMetric);
    const isCurrentMetricFilterable = currentMetricConf?.periodFilterable ?? false;
    const isMonthOnly = currentMetricConf?.monthOnly ?? false;
    const isReRegSuccess = activeMetric === 'reRegistrationSuccessRate';

    const handleCatChange = (cat: string) => {
        setActiveCat(cat);
        const first = categories.find((c) => c.cat === cat)?.metrics[0];
        if (first) setActiveMetric(first.key);
    };

    /*
     * 🔴 월 단위 지표(재등록 성공률)는 값이 없는 행(null — 구 서버·집계 실패)을 **맨 뒤**로 보내고
     *    등수도 붙이지 않는다. 0 으로 세면 미측정 지점이 실측 0% 지점과 같은 줄에 선다.
     *    다른 지표의 기존 정렬(`toNumber` 내림차순)은 그대로 둔다.
     */
    /*
     * 🔴 재등록 성공률은 행의 cohortMonth 가 이 랭킹이 표시 중인 달(baseDate)과 다르면 값 없음으로 본다.
     *    keepPreviousData·지점 조회 폴백으로 다른 달 데이터가 섞여 들어올 수 있다. 다른 지표는 data 그대로.
     */
    const baseMonthKey = baseDate ? monthKeyOf(baseDate) : '';
    const rows = useMemo(
        () => (isReRegSuccess ? data.map((row) => alignReRegistrationSuccessToMonth(row, baseMonthKey)) : data),
        [data, isReRegSuccess, baseMonthKey]
    );
    /*
     * 🔴 서버 집계가 실패해 빈 값(null)으로 온 행(회원 수·당일 취소/변경·평균 단가)도 같은 규칙 — 맨 뒤, 등수 없음.
     *    0 으로 세면 집계 못 한 지점이 실제 0 인 지점과 같은 줄에 선다.
     *    옛 서버는 이 필드에 빈 값을 보내지 않으므로 옛 응답의 정렬은 그대로다.
     */
    const hasDegradedMissing = useMemo(
        () => NULLABLE_DEGRADED_FIELDS.has(activeMetric) && rows.some((row) => row?.[activeMetric] === null),
        [rows, activeMetric]
    );
    const measuredRanking = useMemo(
        () => (isMonthOnly || hasDegradedMissing ? sortMeasuredFirst(rows, activeMetric) : null),
        [rows, activeMetric, isMonthOnly, hasDegradedMissing]
    );
    const sorted = useMemo(
        () => measuredRanking?.sorted ?? [...rows].sort((a, b) => toNumber(b[activeMetric]) - toNumber(a[activeMetric])),
        [measuredRanking, rows, activeMetric]
    );
    const displayed = expanded ? sorted : sorted.slice(0, 5);
    const maxV = sorted.length ? (toNumber(sorted[0][activeMetric]) || 1) : 1;
    const hasReRegSuccessFailure = isReRegSuccess && rows.some((row) => row?.reRegistrationSuccessFailed === true);
    const reRegEvidenceEnabled = isReRegSuccess && !!onReRegistrationSuccessEvidence;

    return (
        <div style={{ background: T.card, borderRadius: 14, padding: '24px 28px', border: `1px solid ${T.cardBorder}`, borderTop: `3px solid ${title.includes('강사') ? T.success : T.blue}`, boxShadow: T.cardShadow, marginBottom: 28, position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', rowGap: 10, columnGap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    {/* 섹션 아이콘 */}
                    {title.includes('강사') ? (
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="8" r="4" stroke={T.success} strokeWidth="1.8" fill="none"/><path d="M4 19c0-3.5 3.1-6 7-6s7 2.5 7 6" stroke={T.success} strokeWidth="1.8" fill="none" strokeLinecap="round"/></svg>
                    ) : (
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="3" y="3" width="16" height="16" rx="2" stroke={T.blue} strokeWidth="1.8" fill="none"/><rect x="6" y="7" width="3" height="3" rx="0.5" fill={T.blue} opacity="0.6"/><rect x="13" y="7" width="3" height="3" rx="0.5" fill={T.blue} opacity="0.6"/><rect x="6" y="13" width="3" height="3" rx="0.5" fill={T.blue} opacity="0.6"/><rect x="13" y="13" width="3" height="3" rx="0.5" fill={T.blue} opacity="0.6"/></svg>
                    )}
                    <span style={{ fontSize: 18, fontWeight: 700, color: T.navy, whiteSpace: 'nowrap' }}>{title}</span>
                    {isCurrentMetricFilterable && rankingPeriod && baseDate && (
                        <span style={{ fontSize: 13, color: T.blue, fontWeight: 600, background: T.blueBg, padding: '3px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                            {rankingPeriod === 'yearly' ? `${baseDate.getFullYear()}년 · 연간` : `${baseDate.getFullYear()}년 ${baseDate.getMonth()+1}월 · ${{ monthly:'월간',weekly:'주간',daily:'일간' }[rankingPeriod] || '월간'}`}
                            {rankingPeriod === 'weekly' && selectedWeek != null && ` ${selectedWeek}주차`}
                            {rankingPeriod === 'daily' && selectedDay != null && ` ${selectedDay}일`}
                        </span>
                    )}
                    {isMonthOnly && baseDate && (
                        <span style={{ fontSize: 13, color: T.blue, fontWeight: 600, background: T.blueBg, padding: '3px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                            {`${baseDate.getFullYear()}년 ${baseDate.getMonth() + 1}월 기준`}
                            {isSameMonth(baseDate) && ' · 실시간'}
                        </span>
                    )}
                    {!isCurrentMetricFilterable && !isMonthOnly && (
                        <span style={{ fontSize: 13, color: T.text4, fontWeight: 500, background: T.inputBg, padding: '3px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>실시간</span>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap', rowGap: 10 }}>
                    {(isCurrentMetricFilterable || isMonthOnly) && rankingPeriod && onRankingPeriodChange && (
                        <PeriodToggle value={rankingPeriod} onChange={onRankingPeriodChange} loading={loading} baseDate={baseDate} onBaseDateChange={onBaseDateChange} monthOnly={isMonthOnly} />
                    )}
                    {filterComponent}
                </div>
            </div>
            {/* Category tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {categories.map((cat) => (
                    <button
                        key={cat.cat}
                        onClick={() => handleCatChange(cat.cat)}
                        style={{
                            padding: '6px 14px',
                            borderRadius: 6,
                            border: activeCat === cat.cat ? `1px solid ${T.border}` : '1px solid transparent',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: activeCat === cat.cat ? 700 : 500,
                            background: activeCat === cat.cat ? T.card : 'transparent',
                            color: activeCat === cat.cat ? T.navy : T.text3,
                        }}
                    >
                        {cat.cat}
                    </button>
                ))}
            </div>
            {/* Metric buttons + Period toggle (기간별 필터 가능 메트릭 선택 시에만 표시) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 2, background: T.inputBg, borderRadius: 8, padding: 2, border: `1px solid ${T.borderLight}` }}>
                    {currentCat?.metrics.map((m) => (
                        <button
                            key={m.key}
                            onClick={() => setActiveMetric(m.key)}
                            style={{
                                padding: '5px 14px',
                                borderRadius: 6,
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                                background: activeMetric === m.key ? T.card : 'transparent',
                                color: activeMetric === m.key ? T.navy : T.text3,
                                boxShadow: activeMetric === m.key ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                            }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
                {/* 스냅샷 메트릭일 때 안내 표시 */}
                {!isCurrentMetricFilterable && !isMonthOnly && (
                    <span style={{ fontSize: 13, color: T.text4, fontWeight: 500 }}>실시간 기준</span>
                )}
            </div>

            {/* 주별 서브탭 */}
            {isCurrentMetricFilterable && rankingPeriod === 'weekly' && weeks.length > 0 && onWeekChange && (
                <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
                    {weeks.map((w) => {
                        const isActive = selectedWeek === w.week;
                        const isLoading = isActive && loading;
                        return (
                            <button
                                key={w.week}
                                onClick={() => onWeekChange(w.week)}
                                style={{
                                    padding: '5px 12px', borderRadius: 6, border: `1px solid ${isActive ? T.blue : T.border}`,
                                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                    background: isActive ? T.blueBg : 'transparent',
                                    color: isActive ? T.blue : T.text3,
                                    display: 'flex', alignItems: 'center', gap: 5, position: 'relative',
                                }}
                            >
                                {isLoading && <div style={{ width: 10, height: 10, border: `1.5px solid ${T.blueBorder}`, borderTopColor: T.blue, borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />}
                                {w.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* 일별 서브탭 */}
            {isCurrentMetricFilterable && rankingPeriod === 'daily' && baseDate && onDayChange && (
                <div style={{ display: 'flex', gap: 3, marginBottom: 14, flexWrap: 'wrap' }}>
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
                        const isActive = selectedDay === d;
                        const isLoading = isActive && loading;
                        return (
                            <button
                                key={d}
                                onClick={() => onDayChange(d)}
                                style={{
                                    padding: '4px 8px', borderRadius: 6, border: `1px solid ${isActive ? T.blue : T.border}`,
                                    cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 500,
                                    background: isActive ? T.blueBg : 'transparent',
                                    color: isActive ? T.blue : T.text3, minWidth: 28, textAlign: 'center',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                                }}
                            >
                                {isLoading && <div style={{ width: 8, height: 8, border: `1.5px solid ${T.blueBorder}`, borderTopColor: T.blue, borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />}
                                {d}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Bars — grid로 이름 열 자동 정렬 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: '8px 10px', alignItems: 'center' }}>
                {displayed.map((item, i) => {
                    const rawVal = item[activeMetric];
                    const val = toNumber(rawVal);
                    const isDegradedMissing = rawVal === null && NULLABLE_DEGRADED_FIELDS.has(activeMetric);
                    const displayVal = isDegradedMissing ? MISSING_VALUE_TEXT : fmtVal(rawVal, currentMetricConf?.unit, activeMetric);
                    const color = colorFn ? colorFn(item, i) : COLORS[i % COLORS.length];
                    // 값이 없는 행(구 서버·집계 실패·분모 0)은 근거를 열지 않는다 — 관리자웹 canOpenEvidence 와 같은 뜻
                    const canOpenEvidence = reRegEvidenceEnabled && canOpenReRegistrationSuccessEvidence(item, { isFetching: loading });
                    const barTooltip: string | undefined = isReRegSuccess
                        ? `${formatReRegistrationSuccessTooltip(item, displayVal)}${canOpenEvidence ? ' · 클릭해 산정 근거 보기' : ''}`
                        : undefined;
                    const ratio = isReRegSuccess ? formatReRegistrationSuccessRatio(item) : null;
                    const rank = measuredRanking ? measuredRanking.rankOf(item) : i + 1;
                    return (
                        <React.Fragment key={item.id}>
                            {/* 값이 없는 행에는 등수를 붙이지 않는다. 자리는 남겨 격자를 지킨다 */}
                            {rank != null ? <RankBadge rank={rank} /> : <span style={{ width: 28, display: 'inline-block' }} />}
                            <div
                                style={{ cursor: onNameClick ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                                onClick={() => onNameClick && onNameClick(item)}
                            >
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                    <span
                                        style={{
                                            fontSize: 13,
                                            color: onNameClick ? T.blue : T.text1,
                                            fontWeight: 600,
                                            textDecoration: onNameClick ? 'underline' : 'none',
                                            textUnderlineOffset: 2,
                                        }}
                                    >
                                        {item[nameKey]}
                                    </span>
                                    {item.trainers != null && (
                                        <span style={{ fontSize: 13, color: T.text4, fontWeight: 500 }}>{item.trainers}명</span>
                                    )}
                                </div>
                                {subKey && <div style={{ fontSize: 13, color: T.text3, marginTop: 1 }}>{item[subKey]}</div>}
                            </div>
                            <div
                                role={canOpenEvidence ? 'button' : undefined}
                                tabIndex={canOpenEvidence ? 0 : undefined}
                                aria-label={canOpenEvidence ? `${item[nameKey]} ${RE_REGISTRATION_SUCCESS_NAMES.label} 산정 근거 보기` : undefined}
                                onClick={canOpenEvidence ? () => onReRegistrationSuccessEvidence?.(item) : undefined}
                                onKeyDown={
                                    canOpenEvidence
                                        ? (e) => {
                                              if (e.key !== 'Enter' && e.key !== ' ') return;
                                              e.preventDefault();
                                              onReRegistrationSuccessEvidence?.(item);
                                          }
                                        : undefined
                                }
                                style={{ height: 28, background: T.inputBg, borderRadius: 6, position: 'relative', border: `1px solid ${T.borderLight}`, cursor: canOpenEvidence ? 'pointer' : barTooltip ? 'help' : 'default' }}
                                onMouseEnter={(e) => { if (canOpenEvidence) e.currentTarget.style.borderColor = T.blueBorder; const tip = e.currentTarget.querySelector('[data-bar-tip]') as HTMLElement; if (tip) tip.style.display = 'block'; }}
                                onMouseLeave={(e) => { if (canOpenEvidence) e.currentTarget.style.borderColor = T.borderLight; const tip = e.currentTarget.querySelector('[data-bar-tip]') as HTMLElement; if (tip) tip.style.display = 'none'; }}
                            >
                                <div
                                    style={{
                                        height: '100%',
                                        borderRadius: 6,
                                        width: `${maxV ? (val / maxV) * 100 : 0}%`,
                                        background: `linear-gradient(90deg,${color}30,${color}90)`,
                                        transition: 'width 0.4s ease',
                                    }}
                                />
                                <span
                                    style={{
                                        position: 'absolute',
                                        right: 12,
                                        top: 5,
                                        fontSize: 13,
                                        color: T.text1,
                                        fontWeight: 700,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {ratio && (
                                        <span style={{ fontSize: 11, color: T.text3, fontWeight: 600, marginRight: 7 }}>{ratio}</span>
                                    )}
                                    {withDegraded(displayVal, item.degraded, activeMetric)}
                                </span>
                                {barTooltip && (
                                    <div
                                        data-bar-tip=""
                                        style={{
                                            display: 'none',
                                            position: 'absolute',
                                            bottom: 'calc(100% + 6px)',
                                            right: 0,
                                            background: T.navy,
                                            color: '#fff',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            padding: '6px 12px',
                                            borderRadius: 6,
                                            whiteSpace: 'nowrap',
                                            zIndex: 50,
                                            pointerEvents: 'none',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        }}
                                    >
                                        {barTooltip}
                                    </div>
                                )}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
            {sorted.length > 5 && (
                <button
                    onClick={() => setExpanded((e) => !e)}
                    style={{
                        width: '100%',
                        marginTop: 14,
                        padding: '10px 0',
                        background: 'transparent',
                        border: `1px solid ${T.border}`,
                        borderRadius: 8,
                        color: T.text3,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    {expanded ? '접기 ▲' : `전체 ${sorted.length}개 펼치기 ▼`}
                </button>
            )}
            {/*
              🔴 값이 비어 있는 이유를 밝힌다 — 센터 통계와 같은 문구.
                 아무 말 없이 '-' 만 있으면 0% 로 읽히거나 화면이 고장 난 줄 안다.
            */}
            {sorted.length > 0 && hasReRegSuccessFailure && (
                <p style={{ marginTop: 12, fontSize: 12, color: T.text4 }}>
                    이 달 {RE_REGISTRATION_SUCCESS_NAMES.label} 집계가 <b>실패했어요.</b> 값과 순위를 낼 수
                    없어 비워 뒀습니다 — 0% 가 아닙니다. 잠시 후 다시 불러와 주세요.
                </p>
            )}
            {reRegEvidenceEnabled && sorted.some((row) => canOpenReRegistrationSuccessEvidence(row, { isFetching: loading })) && (
                <p style={{ marginTop: 12, fontSize: 12, color: T.text4 }}>
                    막대를 클릭하면 이 숫자를 이루는 회원을 하나씩 볼 수 있어요.
                </p>
            )}
        </div>
    );
};

/* ── Sortable Table ── */
const thStyle: React.CSSProperties = {
    padding: '14px 16px',
    textAlign: 'left',
    fontSize: 13,
    fontWeight: 700,
    color: T.text3,
    letterSpacing: 0.5,
    background: T.inputBg,
    borderBottom: `1px solid ${T.border}`,
    position: 'sticky',
    top: 0,
};
const tdStyle: React.CSSProperties = {
    padding: '13px 16px',
    color: T.text1,
};

const SortableTable = ({ columns, data, defaultSort }: { columns: any[]; data: any[]; defaultSort?: string }) => {
    const [sortKey, setSortKey] = useState(defaultSort || columns[1]?.key);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const sorted = useMemo(
        () =>
            [...data].sort((a, b) => {
                const col = columns.find((c: any) => c.key === sortKey);
                const va = col?.sortValue ? col.sortValue(a) : a[sortKey];
                const vb = col?.sortValue ? col.sortValue(b) : b[sortKey];
                if (va == null && vb == null) return 0;
                if (va == null) return 1;
                if (vb == null) return -1;
                if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
                return sortDir === 'asc' ? va - vb : vb - va;
            }),
        [data, sortKey, sortDir, columns],
    );
    const handleSort = (k: string) => {
        if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else {
            setSortKey(k);
            setSortDir('desc');
        }
    };
    return (
        <div style={{ overflowX: 'auto', borderRadius: 12, border: `1px solid ${T.border}`, boxShadow: T.cardShadow }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, background: T.card }}>
                <thead>
                    <tr>
                        <th style={{ ...thStyle, width: 36, textAlign: 'center' }}>#</th>
                        {columns.map((c: any) => (
                            <th
                                key={c.key}
                                style={{ ...thStyle, cursor: 'pointer', textAlign: c.align || 'left', whiteSpace: 'nowrap' }}
                                onClick={() => handleSort(c.key)}
                            >
                                {c.label}
                                {sortKey === c.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((r: any, i: number) => (
                        <tr key={r.id || i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                <RankBadge rank={i + 1} />
                            </td>
                            {columns.map((c: any) => (
                                <td key={c.key} style={{ ...tdStyle, textAlign: c.align || 'left', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                    {c.render ? c.render(r[c.key], r) : r[c.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const RefundAnalysisView = ({
    summary,
    centers,
    trainers,
    loading,
    period,
    onPeriodChange,
    baseDate,
    onBaseDateChange,
    selectedWeek,
    onWeekChange,
    selectedDay,
    onDayChange,
    degraded,
}: {
    summary: any;
    centers: any[];
    trainers: any[];
    /** 서버 degraded(다른 회사 연결 판정 실패 등). 있으면 「집계 지연」 한 줄 */
    degraded?: string[];
    loading: boolean;
    period: string;
    onPeriodChange: (v: string) => void;
    baseDate: Date;
    onBaseDateChange: (d: Date) => void;
    selectedWeek?: number;
    onWeekChange: (w: number | undefined) => void;
    selectedDay?: number;
    onDayChange: (d: number | undefined) => void;
}) => {
    const [tab, setTab] = useState<'center' | 'trainer'>('center');
    const [search, setSearch] = useState('');
    const [centerFilter, setCenterFilter] = useState<Set<string>>(new Set());
    const centerNamesRef = useRef<string[]>([]);

    useEffect(() => {
        if (centers.length === 0) return;
        const previousNames = centerNamesRef.current;
        const nextNames = centers.map((c: any) => c.name);
        centerNamesRef.current = nextNames;

        setCenterFilter((prev) => {
            const nextSet = new Set(nextNames);
            const wasAllSelected = previousNames.length === 0 || previousNames.every((name: string) => prev.has(name));
            if (wasAllSelected) return nextSet;
            return new Set(nextNames.filter((name: string) => prev.has(name)));
        });
    }, [centers]);

    useEffect(() => {
        if (period === 'weekly') {
            const weeks = getWeeksOfMonth(baseDate.getFullYear(), baseDate.getMonth() + 1);
            if (weeks.length > 0 && (selectedWeek == null || selectedWeek > weeks.length)) {
                onWeekChange(weeks.length);
            }
        }
        if (period === 'daily') {
            const days = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0).getDate();
            if (selectedDay == null || selectedDay > days) onDayChange(days);
        }
    }, [baseDate, period, selectedWeek, selectedDay, onWeekChange, onDayChange]);

    const filteredCenters = useMemo(() => {
        let filtered = centerFilter.size === 0 ? [] : centers.filter((c: any) => centerFilter.has(c.name));
        if (search) filtered = filtered.filter((c: any) => c.name.toLowerCase().includes(search.toLowerCase()));
        return filtered;
    }, [centers, centerFilter, search]);

    const filteredTrainers = useMemo(() => {
        let filtered = centerFilter.size === 0 ? [] : trainers.filter((t: any) => centerFilter.has(t.center));
        if (search) filtered = filtered.filter((t: any) => t.name.toLowerCase().includes(search.toLowerCase()) || t.center?.toLowerCase().includes(search.toLowerCase()));
        return filtered;
    }, [trainers, centerFilter, search]);

    const periodShortLabel = ({ yearly: '연', monthly: '월', weekly: '주', daily: '일' } as Record<string, string>)[period] || '월';
    const periodDisplay = period === 'yearly'
        ? `${baseDate.getFullYear()}년 · 연간`
        : `${baseDate.getFullYear()}년 ${baseDate.getMonth() + 1}월 · ${periodShortLabel}간`;
    const summaryRefundRate = toNullableNumber(summary?.refundRate);
    const summaryItems = [
        { label: '환불금액', value: fmtFull(toNumber(summary?.refundAmount)), color: T.error },
        { label: '환불율', value: summaryRefundRate != null ? `${summaryRefundRate.toFixed(1)}%` : '-', color: T.warn },
        { label: '환불건수', value: `${fmt(toNumber(summary?.refundCount))}건`, color: T.gold },
        { label: '환불회원', value: `${fmt(toNumber(summary?.refundMemberCount))}명`, color: '#9E76F3' },
        { label: '전체환불', value: `${fmtFull(toNumber(summary?.fullRefundAmount))} · ${fmt(toNumber(summary?.fullRefundCount))}건`, color: T.navy },
        { label: '부분환불', value: `${fmtFull(toNumber(summary?.partialRefundAmount))} · ${fmt(toNumber(summary?.partialRefundCount))}건`, color: T.blue },
        { label: '총판매액', value: fmtFull(toNumber(summary?.grossSales)), color: T.success },
        { label: '순매출', value: fmtFull(toNumber(summary?.netSales)), color: T.success },
    ];

    const moneyRender = (v: number) => v ? <span style={{ color: T.error, fontWeight: 700 }}>{fmt(v)}</span> : <span style={{ color: T.text4 }}>-</span>;
    const pctRender = (v: number | null | undefined) => {
        const n = toNullableNumber(v);
        return <span style={{ color: n == null ? T.text4 : n >= 10 ? T.error : n > 0 ? T.gold : T.text4, fontWeight: 700 }}>{n != null ? `${n.toFixed(1)}%` : '-'}</span>;
    };
    const centerColumns = [
        { key: 'name', label: '지점명', render: (v: string) => <span style={{ fontWeight: 700, color: T.navy }}>{v}</span> },
        { key: 'refundAmount', label: '환불금액', align: 'right', render: moneyRender },
        { key: 'refundRate', label: '환불율', align: 'right', render: pctRender },
        { key: 'refundCount', label: '환불건수', align: 'right', render: (v: number) => `${v || 0}건` },
        { key: 'refundMemberCount', label: '환불회원', align: 'right', render: (v: number) => `${v || 0}명` },
        { key: 'fullRefundAmount', label: '전체환불액', align: 'right', render: moneyRender },
        { key: 'partialRefundAmount', label: '부분환불액', align: 'right', render: moneyRender },
        { key: 'grossSales', label: '총판매액', align: 'right', render: (v: number) => fmt(v || 0) },
        { key: 'netSales', label: '순매출', align: 'right', render: (v: number) => fmt(v || 0) },
        { key: 'ptRefundAmount', label: 'PT 환불', align: 'right', render: moneyRender },
        { key: 'groupRefundAmount', label: '그룹 환불', align: 'right', render: moneyRender },
        { key: 'trainers', label: '강사수', align: 'right' },
        { key: 'totalMembers', label: '활성 회원', align: 'right' },
    ];
    const trainerColumns = [
        { key: 'name', label: '강사명', render: (v: string) => <span style={{ fontWeight: 700, color: T.navy }}>{v}</span> },
        { key: 'center', label: '소속' },
        { key: 'refundAmount', label: '환불금액', align: 'right', render: moneyRender },
        { key: 'refundRate', label: '환불율', align: 'right', render: pctRender },
        { key: 'refundCount', label: '환불건수', align: 'right', render: (v: number) => `${v || 0}건` },
        { key: 'refundMemberCount', label: '환불회원', align: 'right', render: (v: number) => `${v || 0}명` },
        { key: 'fullRefundAmount', label: '전체환불액', align: 'right', render: moneyRender },
        { key: 'partialRefundAmount', label: '부분환불액', align: 'right', render: moneyRender },
        { key: 'grossSales', label: '총판매액', align: 'right', render: (v: number) => fmt(v || 0) },
        { key: 'netSales', label: '순매출', align: 'right', render: (v: number) => fmt(v || 0) },
        { key: 'ptRefundAmount', label: 'PT 환불', align: 'right', render: moneyRender },
        { key: 'groupRefundAmount', label: '그룹 환불', align: 'right', render: moneyRender },
        { key: 'totalMembers', label: '담당회원', align: 'right' },
    ];

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', rowGap: 12, columnGap: 12 }}>
                <div style={{ flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', rowGap: 6 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <path d="M5 6h12M7 10h8M9 14h5" stroke={T.error} strokeWidth="2" strokeLinecap="round"/>
                            <path d="M6 3h10a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z" stroke={T.error} strokeWidth="1.7"/>
                        </svg>
                        <span style={{ fontSize: 20, fontWeight: 800, color: T.navy, whiteSpace: 'nowrap' }}>환불 분석</span>
                        <span style={{ fontSize: 13, color: T.blue, fontWeight: 700, background: T.blueBg, padding: '4px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                            {periodDisplay}
                            {period === 'weekly' && selectedWeek != null && ` ${selectedWeek}주차`}
                            {period === 'daily' && selectedDay != null && ` ${selectedDay}일`}
                        </span>
                    </div>
                    <div style={{ fontSize: 13, color: T.text3, marginTop: 5 }}>환불 발생일 기준 집계 · 그룹수업 토글은 상단 설정을 따릅니다</div>
                </div>
                <PeriodToggle value={period} onChange={onPeriodChange} loading={loading} baseDate={baseDate} onBaseDateChange={onBaseDateChange} />
            </div>

            {period === 'weekly' && (() => {
                const weeks = getWeeksOfMonth(baseDate.getFullYear(), baseDate.getMonth() + 1);
                return (
                    <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
                        {weeks.map((w) => {
                            const isActive = selectedWeek === w.week;
                            return (
                                <button key={w.week} onClick={() => onWeekChange(w.week)} style={{
                                    padding: '5px 12px', borderRadius: 6, border: `1px solid ${isActive ? T.blue : T.border}`,
                                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                    background: isActive ? T.blueBg : 'transparent',
                                    color: isActive ? T.blue : T.text3,
                                }}>
                                    {w.label}
                                </button>
                            );
                        })}
                    </div>
                );
            })()}

            {period === 'daily' && (() => {
                const days = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0).getDate();
                return (
                    <div style={{ display: 'flex', gap: 3, marginBottom: 14, flexWrap: 'wrap' }}>
                        {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                            const isActive = selectedDay === d;
                            return (
                                <button key={d} onClick={() => onDayChange(d)} style={{
                                    padding: '4px 8px', borderRadius: 6, border: `1px solid ${isActive ? T.blue : T.border}`,
                                    cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 500,
                                    background: isActive ? T.blueBg : 'transparent',
                                    color: isActive ? T.blue : T.text3, minWidth: 28,
                                }}>
                                    {d}
                                </button>
                            );
                        })}
                    </div>
                );
            })()}

            {loading && centers.length === 0 && trainers.length === 0 ? (
                <>
                    <SkeletonKpiCards />
                    <SkeletonRanking />
                    <SkeletonRanking />
                </>
            ) : (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
                        {summaryItems.map((item) => (
                            <div key={item.label} style={{ background: T.card, borderRadius: 12, border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow, padding: '15px 17px', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: item.color }} />
                                <div style={{ fontSize: 13, color: T.text3, fontWeight: 700, marginBottom: 8 }}>{item.label}</div>
                                <div style={{ fontSize: 21, color: item.color === T.error ? T.error : T.navy, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>{item.value}</div>
                            </div>
                        ))}
                    </div>

                    {centers.length === 0 && trainers.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '56px 0', color: T.text3, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, boxShadow: T.cardShadow }}>
                            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>환불 분석 데이터가 없습니다</div>
                            <div style={{ fontSize: 13 }}>선택한 기간에 집계할 환불 내역이 없습니다.</div>
                        </div>
                    ) : (
                        <>
                            <RankingSection
                                title="센터별 환불 랭킹"
                                data={filteredCenters}
                                nameKey="name"
                                categories={REFUND_RANK_CATEGORIES}
                                rankingPeriod={period}
                                onRankingPeriodChange={onPeriodChange}
                                loading={loading}
                                baseDate={baseDate}
                                onBaseDateChange={onBaseDateChange}
                                selectedWeek={selectedWeek}
                                selectedDay={selectedDay}
                            />
                            <RankingSection
                                title="강사별 환불 랭킹"
                                data={filteredTrainers}
                                nameKey="name"
                                subKey="center"
                                categories={REFUND_RANK_CATEGORIES}
                                rankingPeriod={period}
                                onRankingPeriodChange={onPeriodChange}
                                loading={loading}
                                baseDate={baseDate}
                                onBaseDateChange={onBaseDateChange}
                                selectedWeek={selectedWeek}
                                selectedDay={selectedDay}
                                filterComponent={<CenterMultiSelect centers={centers} selected={centerFilter} onChange={setCenterFilter} />}
                            />

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ display: 'flex', gap: 2, background: T.card, borderRadius: 8, padding: 3, border: `1px solid ${T.border}` }}>
                                        {[
                                            { key: 'center', label: '센터별' },
                                            { key: 'trainer', label: '강사별' },
                                        ].map((t) => (
                                            <button
                                                key={t.key}
                                                onClick={() => setTab(t.key as 'center' | 'trainer')}
                                                style={{
                                                    padding: '7px 20px',
                                                    borderRadius: 6,
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    background: tab === t.key ? T.blue : 'transparent',
                                                    color: tab === t.key ? '#fff' : T.text3,
                                                }}
                                            >
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>
                                    <CenterMultiSelect centers={centers} selected={centerFilter} onChange={setCenterFilter} />
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={tab === 'center' ? '센터명 검색' : '센터명 · 강사명 검색'}
                                        style={{
                                            padding: '7px 14px', borderRadius: 8, border: `1px solid ${T.border}`,
                                            fontSize: 14, color: T.text1, background: T.card, outline: 'none', width: 220,
                                        }}
                                    />
                                </div>
                                <span style={{ fontSize: 13, color: T.text4 }}>컬럼 클릭으로 정렬 · 환불액은 양수 표기</span>
                                {degraded && degraded.length > 0 && <DegradedBadge />}
                            </div>
                            {tab === 'center' ? (
                                <SortableTable columns={centerColumns} data={filteredCenters} defaultSort="refundAmount" />
                            ) : (
                                <SortableTable columns={trainerColumns} data={filteredTrainers} defaultSort="refundAmount" />
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
};

/* ── Center Detail View ── */
const TRAINER_COLORS = ['#3D73DD', '#C4963C', '#00A793', '#9E76F3', '#FF8777', '#17B5CB', '#FD8FE5', '#648FE4', '#34d399', '#fb923c'];

const CenterDetailView = ({
    center,
    trainers,
    onBack,
    backLabel = '총괄 대시보드',
    rankingPeriod,
    onRankingPeriodChange,
    baseDate,
    onBaseDateChange,
    selectedWeek,
    onWeekChange,
    selectedDay,
    onDayChange,
    includeGroup,
    canOpenCenterView,
    onOpenCenterView,
    isOpeningCenterView = false,
}: {
    center: any;
    trainers: any[];
    onBack: () => void;
    backLabel?: string;
    rankingPeriod: string;
    onRankingPeriodChange: (p: string) => void;
    baseDate: Date;
    onBaseDateChange: (d: Date) => void;
    selectedWeek?: number;
    onWeekChange?: (w: number | undefined) => void;
    selectedDay?: number;
    onDayChange?: (d: number | undefined) => void;
    includeGroup?: boolean;
    canOpenCenterView: boolean;
    onOpenCenterView: (center: any) => void;
    isOpeningCenterView?: boolean;
}) => {
    const [visibleTrainers, setVisibleTrainers] = useState(new Set(trainers.map((t: any) => t.id)));
    const [showCenterTotal, setShowCenterTotal] = useState(false);
    const [chartCat, setChartCat] = useState(CHART_CATEGORIES[0].cat);
    const [chartMetric, setChartMetric] = useState(CHART_CATEGORIES[0].metrics[0].key);
    const [chartHoverIdx, setChartHoverIdx] = useState<number | null>(null);
    const chartCatObj = CHART_CATEGORIES.find((c) => c.cat === chartCat);
    const chartMetricConf = chartCatObj?.metrics.find((m) => m.key === chartMetric);

    const [centerRevHoverIdx, setCenterRevHoverIdx] = useState<number | null>(null);

    // 센터 매출 추이 전용 period/date (강사 랭킹과 독립)
    const [centerRevPeriod, setCenterRevPeriod] = useState('monthly');
    const [centerRevBase, setCenterRevBase] = useState(new Date());
    const setCenterRevPeriodStable = (v: string) => { const sy = window.scrollY; setCenterRevPeriod(v); requestAnimationFrame(() => window.scrollTo(0, sy)); };
    const setCenterRevBaseStable = (v: Date) => { const sy = window.scrollY; setCenterRevBase(v); requestAnimationFrame(() => window.scrollTo(0, sy)); };

    // 강사 추이 전용 period/date (센터 매출 추이, 강사 랭킹과 독립)
    const [trainerChartPeriod, setTrainerChartPeriod] = useState('monthly');
    const [trainerChartBase, setTrainerChartBase] = useState(new Date());
    const setTrainerChartPeriodStable = (v: string) => { const sy = window.scrollY; setTrainerChartPeriod(v); requestAnimationFrame(() => window.scrollTo(0, sy)); };
    const setTrainerChartBaseStable = (v: Date) => { const sy = window.scrollY; setTrainerChartBase(v); requestAnimationFrame(() => window.scrollTo(0, sy)); };

    const authObject = getItemWithExpiration('authObject');
    const superAdminId = authObject?.superAdminId || authObject?.adminId || '';
    const centerId = center.centerId || '';
    const baseDateParam = isSameMonth(baseDate) ? undefined : baseDateToParam(baseDate);
    const centerRevBaseParam = isSameMonth(centerRevBase) ? undefined : baseDateToParam(centerRevBase);
    const trainerChartBaseParam = isSameMonth(trainerChartBase) ? undefined : baseDateToParam(trainerChartBase);

    // 센터 상세 강사 랭킹 전용 API (rankingPeriod + baseDate 연동)
    const { data: centerDetailRes, isFetching: isCenterDetailFetching } = useQuery({
        ...fetchSuperAdminCenterDetail(superAdminId, centerId, rankingPeriod, baseDateParam, rankingPeriod === 'weekly' ? selectedWeek : undefined, rankingPeriod === 'daily' ? selectedDay : undefined, includeGroup),
        enabled: !!superAdminId && !!centerId,
        staleTime: 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true,
    });
    const cdTrainers = useMemo(() => {
        const apiTrainers = centerDetailRes?.data?.data?.trainers;
        if (!apiTrainers?.length) return trainers;
        return apiTrainers.map((t: any, i: number) => {
            const pf = t.periodFiltered || {};
            const snap = t.snapshot || {};
            const degraded = readDegraded(t);
            return {
                id: i + 1,
                coachId: t.coachId,
                name: t.name,
                center: center.name,
                revenue: pf.revenue || 0,
                prevRevenue: pf.prevRevenue || 0,
                totalClasses: countOrMissing(pf.totalClasses),
                noShowCount: countOrMissing(pf.noShowCount),
                journalCount: countOrMissing(pf.journalCount),
                newPayMembers: pf.newPayMembers || 0,
                reRegPayMembers: pf.reRegPayMembers || 0,
                // 집계 실패면 빈 값(null) — 0 으로 그리지 않는다
                sameDayCancelCount: countOrMissing(pf.sameDayCancelCount),
                sameDayChangeCount: countOrMissing(pf.sameDayChangeCount),
                avgPricePerClass: avgPriceOrMissing(pf.avgPricePerClass, degraded),
                journalWriteRate: pf.journalWriteRate ?? null,
                totalMembers: countOrMissing(snap.totalMembers),
                degraded,
                undeliveredRevenue: snap.undeliveredRevenue || 0,
                reviewAvg: snap.reviewAvg || 0,
            };
        });
    }, [centerDetailRes, trainers, center.name]);

    // 센터 매출 추이 전용 API (독립 centerRevPeriod + centerRevBase 사용)
    const { data: centerRevTrendRes, isFetching: isCenterRevFetching, isError: isCenterRevError, refetch: refetchCenterRev } = useQuery({
        ...fetchSuperAdminCenterTrend(superAdminId, centerId, centerRevPeriod, 'revenue', centerRevBaseParam, includeGroup),
        queryKey: ['centerRevTrend', superAdminId, centerId, centerRevPeriod, centerRevBaseParam || '', includeGroup ?? false],
        enabled: !!superAdminId && !!centerId,
        staleTime: 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true,
    });
    const centerRevTrendData = centerRevTrendRes?.data?.data;

    // 강사별 추이 전용 API (trainerChartPeriod + trainerChartBase 사용)
    const { data: trendRes, isFetching: isTrendFetching, isError: isTrendError, refetch: refetchTrend } = useQuery({
        ...fetchSuperAdminCenterTrend(superAdminId, centerId, trainerChartPeriod, chartMetric, trainerChartBaseParam, includeGroup),
        enabled: !!superAdminId && !!centerId,
        staleTime: 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true,
    });
    const centerTrendData = trendRes?.data?.data;

    const handleChartCatChange = (cat: string) => {
        setChartCat(cat);
        setChartMetric(CHART_CATEGORIES.find((c) => c.cat === cat)?.metrics[0]?.key || '');
    };

    // trainers가 바뀌면 visibleTrainers 동기화
    useEffect(() => {
        setVisibleTrainers(new Set(trainers.map((t: any) => t.id)));
    }, [trainers]);

    const toggleTrainer = (id: number) =>
        setVisibleTrainers((p) => {
            const n = new Set(p);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    const toggleAllTrainers = () => {
        visibleTrainers.size === trainers.length ? setVisibleTrainers(new Set()) : setVisibleTrainers(new Set(trainers.map((t: any) => t.id)));
    };

    // 차트 데이터: 서버 추이만 그린다. 없으면 빈 상태 — 예전의 임의 배율 가짜 추이(genMetricSeries)는 없앴다.
    //   없는 점(null)·시리즈에 없는 강사는 0 으로 채우지 않고 선을 끊는다. 센터 합산도 웹이 더하지 않는다.
    const labels: string[] = centerTrendData?.labels?.length ? centerTrendData.labels : [];
    const useTrendApi = labels.length > 0;

    const getTrainerChartData = (t: any): TrendPoint[] =>
        toTrendPoints(
            centerTrendData?.series?.find((s: any) => s.coachId === t.coachId || s.name === t.name)?.data,
            labels.length
        );
    const getCenterChartData = (): TrendPoint[] => toTrendPoints(centerTrendData?.totalData, labels.length);

    let allV: number[] = [];
    if (showCenterTotal) allV.push(...presentValues(getCenterChartData()));
    trainers.forEach((t: any) => {
        if (visibleTrainers.has(t.id)) allV.push(...presentValues(getTrainerChartData(t)));
    });
    if (!allV.length) allV = presentValues(getCenterChartData());
    const hasTrainerTrend = useTrendApi && allV.length > 0;

    const maxR = allV.length ? Math.max(...allV) : 1;
    const minR = allV.length ? Math.min(...allV) : 0;
    const padV = (maxR - minR) * 0.18 || maxR * 0.1;
    const yMax = maxR + padV,
        yMin = Math.max(0, minR - padV);

    const W = 1100,
        H = 340,
        px = 64,
        py = 24,
        pb = 40,
        cW = W - px - 100,
        cH = H - py - pb;
    const getY = (v: number) => (yMax === yMin ? py + cH / 2 : py + cH - ((v - yMin) / (yMax - yMin)) * cH);
    const getX = (i: number) => px + (i / Math.max(labels.length - 1, 1)) * cW;
    const gridN = 5;
    const gridV = Array.from({ length: gridN + 1 }, (_, i) => yMin + (i / gridN) * (yMax - yMin));
    const pathD = (d: TrendPoint[]) => buildLinePath(d, getX, getY);
    const centerTotal = getCenterChartData();
    const centerTotalLast = lastPresentPoint(centerTotal);
    const yFmt = (v: number) => (chartMetricConf?.unit === 'money' ? fmt(v) : Math.round(v));

    // KPI 날짜 라벨용
    const _now = new Date();
    const _ws = new Date(_now); _ws.setDate(_now.getDate() - _now.getDay());
    if (_ws.getMonth() < _now.getMonth() || _ws.getFullYear() < _now.getFullYear()) { _ws.setFullYear(_now.getFullYear()); _ws.setMonth(_now.getMonth()); _ws.setDate(1); }

    return (
        <>
            {/* Breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                <button
                    onClick={onBack}
                    style={{
                        background: T.card,
                        border: `1px solid ${T.border}`,
                        borderRadius: 8,
                        padding: '6px 8px',
                        cursor: 'pointer',
                        color: T.navy,
                        display: 'flex',
                        alignItems: 'center',
                        boxShadow: T.cardShadow,
                    }}
                >
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <path d="M14 5L7 11L14 17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                <span onClick={onBack} style={{ color: T.navy, fontSize: 22, fontWeight: 800, cursor: 'pointer' }}>{center.name}</span>
                {canOpenCenterView && (
                <button
                    type="button"
                    onClick={() => onOpenCenterView(center)}
                    disabled={isOpeningCenterView}
                    style={{
                        marginLeft: 'auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 13px',
                        borderRadius: 8,
                        border: `1px solid ${T.blueBorder}`,
                        background: T.blue,
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: isOpeningCenterView ? 'wait' : 'pointer',
                        opacity: isOpeningCenterView ? 0.65 : 1,
                    }}
                >
                    <span aria-hidden="true">↗</span>
                    {isOpeningCenterView ? '여는 중' : '관리자 화면 보기'}
                </button>
                )}
                {/* period date label removed — each section shows its own dates */}
            </div>

            {/* KPI summary — 11칸 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 22 }}>
                {[
                    { label: `${_now.getMonth()+1}/${_now.getDate()}(${['일','월','화','수','목','금','토'][_now.getDay()]}) 매출`, value: fmtFull(center.dayRevenue || 0), color: T.blue },
                    { label: `주간 매출 (${_ws.getMonth()+1}/${_ws.getDate()}~${_now.getMonth()+1}/${_now.getDate()})`, value: fmtFull(center.weekRevenue || 0), color: T.gold },
                    { label: `${_now.getMonth()+1}월 매출 (${_now.getMonth()+1}/1~${_now.getMonth()+1}/${_now.getDate()})`, value: fmtFull(center.monthRevenue || 0), color: T.success },
                    { label: '총 미수업금액', sub: '부채', value: withDegraded(fmtFull(center.undeliveredRevenue || 0), center.degraded, 'undeliveredRevenue'), color: T.error, isDebt: true },
                    { label: '강사 수', value: `${center.trainers || 0}명`, color: '#9E76F3' },
                    { label: '활성 회원', value: missingOr(center.totalMembers, center.degraded, 'totalMembers', () => `${center.totalMembers || 0}명`), color: T.blue },
                    { label: `신규등록 (${_now.getMonth()+1}월)`, value: `${center.newPayMembers || 0}건 · ${fmtFull(center.newPayRevenue || 0)}`, color: '#17B5CB' },
                    { label: `재등록 (${_now.getMonth()+1}월)`, value: `${center.reRegPayMembers || 0}건 · ${fmtFull(center.reRegPayRevenue || 0)}`, color: '#C4963C' },
                    { label: `평균 회당단가 PT (${_now.getMonth()+1}월)`, value: missingOr(center.avgPricePerClass, center.degraded, 'avgPricePerClass', () => fmtFull(center.avgPricePerClass || 0)), color: '#FF8777' },
                    { label: `일지 작성률 (${_now.getMonth()+1}월)`, value: center.journalWriteRate != null ? `${center.journalWriteRate.toFixed(1)}%` : '-', color: '#34d399' },
                    {
                        label: `${RE_REGISTRATION_SUCCESS_NAMES.label} (${reRegistrationSuccessMonthLabel(center.reRegistrationSuccessCohortMonth, _now.getMonth() + 1)}월)`,
                        // 🔴 null(구 서버·집계 실패)은 '-'. 0% 로 적지 않는다. 분모가 있으면 「N명 중 M명 · 」 병기
                        value: center.reRegistrationSuccessRate != null ? (
                            <>
                                {formatReRegistrationSuccessRatio(center) && (
                                    <span style={{ fontSize: 13, color: T.text3, fontWeight: 600, marginRight: 6 }}>{formatReRegistrationSuccessRatio(center)}</span>
                                )}
                                {`${Number(center.reRegistrationSuccessRate).toFixed(1)}%`}
                            </>
                        ) : '-',
                        color: T.gold,
                    },
                ].map((k: any, i) => (
                    <div
                        key={i}
                        style={{
                            background: T.card,
                            borderRadius: 12,
                            padding: '14px 16px',
                            border: `1px solid ${T.cardBorder}`,
                            boxShadow: T.cardShadow,
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'default',
                            transition: 'box-shadow 0.15s, border-color 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = T.cardShadowHover; e.currentTarget.style.borderColor = k.color; }}
                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = T.cardShadow; e.currentTarget.style.borderColor = T.cardBorder; }}
                    >
                        <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: k.color }} />
                        <div style={{ fontSize: 14, color: T.text2, fontWeight: 600, marginBottom: 8 }}>{k.label}{k.sub && <span style={{ fontSize: 13, color: T.error, fontWeight: 600, background: T.errorBg, padding: '1px 5px', borderRadius: 3, marginLeft: 4 }}>{k.sub}</span>}</div>
                        <div style={{ fontSize: k.isDebt ? 22 : 20, fontWeight: 800, color: k.isDebt ? T.error : T.navy, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
                    </div>
                ))}
            </div>

            {/* Center Revenue Trend Chart */}
            <div style={{ background: T.card, borderRadius: 14, padding: '24px 28px', border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow, marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: T.gold }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <path d="M3 18L8 11L12 14L19 4" stroke={T.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="19" cy="4" r="2" fill={T.blue}/>
                        </svg>
                        <span style={{ fontSize: 18, fontWeight: 700, color: T.navy }}>매출 추이</span>
                        {readDegraded(centerRevTrendData).includes('brandScope') && <DegradedBadge />}
                        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 6 }} className="sa-chart-tooltip-wrap">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ cursor: 'pointer', color: T.text3 }}>
                                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
                                <text x="8" y="12" textAnchor="middle" fontSize="10" fill="currentColor" fontWeight="700">i</text>
                            </svg>
                            <div className="sa-chart-tooltip-box" style={{
                                display: 'none', position: 'absolute', top: 24, left: '50%', transform: 'translateX(-50%)',
                                background: '#1A305D', color: '#fff', fontSize: 12, fontWeight: 400,
                                padding: '8px 12px', borderRadius: 8, whiteSpace: 'nowrap', zIndex: 100,
                                boxShadow: '0 4px 12px rgba(26,48,93,0.2)', lineHeight: 1.6,
                            }}>
                                미리 등록된 매출이 포함될 수 있습니다.
                                <div style={{ position: 'absolute', top: -5, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: '5px solid #1A305D' }} />
                            </div>
                            <style>{`.sa-chart-tooltip-wrap:hover .sa-chart-tooltip-box { display: block !important; }`}</style>
                        </div>
                    </div>
                    <PeriodToggle value={centerRevPeriod} onChange={setCenterRevPeriodStable} loading={isCenterRevFetching} baseDate={centerRevBase} onBaseDateChange={setCenterRevBaseStable} />
                </div>
                {(() => {
                    const ctW = 1100, ctH = 340, ctPx = 64, ctPy = 24, ctPb = 40, ctCW = ctW - ctPx - 100, ctCH = ctH - ctPy - ctPb;
                    // 서버 매출 추이만 그린다. 예전에는 없으면 강사별 추이(가짜 추이 포함)의 합산을 대신 그렸다.
                    const revLabels: string[] = centerRevTrendData?.labels?.length ? centerRevTrendData.labels : [];
                    const ctData: TrendPoint[] = toTrendPoints(centerRevTrendData?.totalData, revLabels.length);
                    const ctValues = presentValues(ctData);
                    if (!revLabels.length || !ctValues.length) {
                        return (
                            <TrendEmptyState
                                loading={!centerRevTrendData && isCenterRevFetching}
                                failed={isCenterRevError && !isCenterRevFetching && !revLabels.length}
                                onRetry={() => refetchCenterRev()}
                            />
                        );
                    }
                    const ctMax = Math.max(...ctValues);
                    const ctMin = Math.min(...ctValues);
                    const ctPadV = (ctMax - ctMin) * 0.18 || ctMax * 0.1 || 1;
                    const ctYMax = ctMax + ctPadV, ctYMin = Math.max(0, ctMin - ctPadV);
                    const ctGetY = (v: number) => (ctYMax === ctYMin ? ctPy + ctCH / 2 : ctPy + ctCH - ((v - ctYMin) / (ctYMax - ctYMin)) * ctCH);
                    const ctGetX = (i: number) => ctPx + (i / Math.max(revLabels.length - 1, 1)) * ctCW;
                    const ctGridN = 5;
                    const ctGridV = Array.from({ length: ctGridN + 1 }, (_, i) => ctYMin + (i / ctGridN) * (ctYMax - ctYMin));
                    const ctPathD = buildLinePath(ctData, ctGetX, ctGetY);
                    const ctLast = lastPresentPoint(ctData);
                    const hi = centerRevHoverIdx;
                    return (
                        <div style={{ position: 'relative' }}>
                            <svg viewBox={`0 0 ${ctW} ${ctH}`} style={{ width: '100%', display: 'block' }}
                                onMouseLeave={() => setCenterRevHoverIdx(null)}
                                onMouseMove={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const x = (e.clientX - rect.left) / rect.width * ctW;
                                    if (revLabels.length === 0) return;
                                    if (revLabels.length === 1) { setCenterRevHoverIdx(0); return; }
                                    const step = ctCW / (revLabels.length - 1);
                                    const idx = Math.round((x - ctPx) / step);
                                    if (idx >= 0 && idx < revLabels.length) setCenterRevHoverIdx(idx);
                                }}
                            >
                                {ctGridV.map((v, i) => {
                                    const y = ctGetY(v);
                                    return (
                                        <g key={i}>
                                            <line x1={ctPx} x2={ctPx + ctCW} y1={y} y2={y} stroke={T.borderLight} strokeWidth={1} />
                                            <text x={ctPx - 10} y={y + 4} textAnchor="end" fill={T.text3} fontSize={11} fontFamily="inherit">{fmt(v)}</text>
                                        </g>
                                    );
                                })}
                                {revLabels.map((l: string, i: number) => (
                                    <text key={i} x={ctGetX(i)} y={ctH - 10} textAnchor="middle" fill={hi === i ? T.navy : T.text3} fontSize={11} fontWeight={hi === i ? 700 : 400} fontFamily="inherit">{l}</text>
                                ))}
                                {hi !== null && (
                                    <line x1={ctGetX(hi)} x2={ctGetX(hi)} y1={ctPy} y2={ctPy + ctCH} stroke={T.blue} strokeWidth={1} strokeDasharray="4 3" opacity={0.5} />
                                )}
                                <path d={ctPathD} fill="none" stroke={T.navy} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
                                {ctData.map((v: TrendPoint, i: number) =>
                                    v === null ? null : (
                                        <circle key={i} cx={ctGetX(i)} cy={ctGetY(v)} r={hi === i ? 5 : 3.5} fill={hi === i ? T.navy : T.card} stroke={T.navy} strokeWidth={2} style={{ transition: 'r 0.15s' }} />
                                    )
                                )}
                                {ctLast && hi === null && (
                                    <text x={ctGetX(ctLast.index) + 10} y={ctGetY(ctLast.value) + 4} fill={T.navy} fontSize={11} fontWeight={700} fontFamily="inherit">{fmtFull(ctLast.value)}</text>
                                )}
                            </svg>
                            {/* 호버 툴팁 */}
                            {hi !== null && hi < revLabels.length && (
                                <div style={{
                                    position: 'absolute', top: ctPy, zIndex: 10, pointerEvents: 'none',
                                    left: `${(ctGetX(hi) / ctW) * 100}%`, transform: 'translateX(-50%)',
                                    background: T.card, border: `1px solid ${T.border}`, borderRadius: 10,
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', padding: '10px 14px', minWidth: 120,
                                }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: T.navy, marginBottom: 6 }}>{revLabels[hi]}</div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: T.navy, fontVariantNumeric: 'tabular-nums' }}>{ctData[hi] === null ? MISSING_VALUE_TEXT : fmtFull(ctData[hi] as number)}</div>
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>

            {/* Trainer Trend Chart */}
            <div style={{ background: T.card, borderRadius: 14, padding: '24px 28px', border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow, marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#9E76F3' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <path d="M3 17L7 10L11 13L15 7L19 4" stroke="#9E76F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="7" cy="10" r="2" fill="#9E76F3" opacity="0.6"/>
                            <circle cx="15" cy="7" r="2" fill="#9E76F3" opacity="0.6"/>
                        </svg>
                        <span style={{ fontSize: 18, fontWeight: 700, color: T.navy }}>강사별 추이 — {chartMetricConf?.label || ''}</span>
                        {readDegraded(centerTrendData).includes('brandScope') && <DegradedBadge />}
                    </div>
                    <PeriodToggle value={trainerChartPeriod} onChange={setTrainerChartPeriodStable} loading={isTrendFetching} baseDate={trainerChartBase} onBaseDateChange={setTrainerChartBaseStable} />
                </div>
                {/* Category tabs */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    {CHART_CATEGORIES.map((cat) => (
                        <button
                            key={cat.cat}
                            onClick={() => handleChartCatChange(cat.cat)}
                            style={{
                                padding: '5px 14px',
                                borderRadius: 6,
                                border: chartCat === cat.cat ? `1px solid ${T.border}` : '1px solid transparent',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: chartCat === cat.cat ? 700 : 500,
                                background: chartCat === cat.cat ? T.card : 'transparent',
                                color: chartCat === cat.cat ? T.navy : T.text3,
                            }}
                        >
                            {cat.cat}
                        </button>
                    ))}
                </div>
                {/* Metric buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 2, background: T.inputBg, borderRadius: 8, padding: 2, border: `1px solid ${T.borderLight}` }}>
                        {chartCatObj?.metrics.map((m) => (
                            <button
                                key={m.key}
                                onClick={() => setChartMetric(m.key)}
                                style={{
                                    padding: '5px 14px',
                                    borderRadius: 6,
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    background: chartMetric === m.key ? T.card : 'transparent',
                                    color: chartMetric === m.key ? T.navy : T.text3,
                                    boxShadow: chartMetric === m.key ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                                }}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                </div>

                {!hasTrainerTrend ? (
                    <TrendEmptyState
                        loading={!centerTrendData && isTrendFetching}
                        failed={isTrendError && !isTrendFetching && !useTrendApi}
                        onRetry={() => refetchTrend()}
                    />
                ) : (
                <div style={{ position: 'relative' }}>
                    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }} onMouseLeave={() => setChartHoverIdx(null)}>
                        {gridV.map((v, i) => {
                            const y = getY(v);
                            return (
                                <g key={i}>
                                    <line x1={px} x2={px + cW} y1={y} y2={y} stroke={T.borderLight} strokeWidth={1} />
                                    <text x={px - 10} y={y + 4} textAnchor="end" fill={T.text3} fontSize={11} fontFamily="inherit">
                                        {yFmt(v)}
                                    </text>
                                </g>
                            );
                        })}
                        {labels.map((l: string, i: number) => (
                            <text key={i} x={getX(i)} y={H - 10} textAnchor="middle" fill={chartHoverIdx === i ? T.navy : T.text3} fontSize={11} fontWeight={chartHoverIdx === i ? 700 : 400} fontFamily="inherit">
                                {l}
                            </text>
                        ))}
                        {chartHoverIdx !== null && (
                            <line x1={getX(chartHoverIdx)} x2={getX(chartHoverIdx)} y1={py} y2={py + cH} stroke={T.blue} strokeWidth={1} strokeDasharray="4 3" opacity={0.5} />
                        )}
                        {showCenterTotal && (
                            <g>
                                <path d={pathD(centerTotal)} fill="none" stroke={T.navy} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
                                {centerTotal.map((v: TrendPoint, i: number) =>
                                    v === null ? null : (
                                        <circle key={i} cx={getX(i)} cy={getY(v)} r={chartHoverIdx === i ? 5 : 3.5} fill={chartHoverIdx === i ? T.navy : T.card} stroke={T.navy} strokeWidth={2} style={{ transition: 'r 0.15s' }} />
                                    )
                                )}
                                {centerTotalLast && (
                                    <text x={px + cW + 10} y={getY(centerTotalLast.value) + 4} fill={T.navy} fontSize={11} fontWeight={700} fontFamily="inherit">
                                        {chartMetric === 'avgPricePerClass' || chartMetric === 'journalWriteRate' ? '평균' : '합산'} {yFmt(centerTotalLast.value)}
                                    </text>
                                )}
                            </g>
                        )}
                        {trainers.map((t: any, ti: number) => {
                            if (!visibleTrainers.has(t.id)) return null;
                            const cl = TRAINER_COLORS[ti % TRAINER_COLORS.length];
                            const d = getTrainerChartData(t);
                            const last = lastPresentPoint(d);
                            if (!last) return null;
                            return (
                                <g key={t.id}>
                                    <path d={pathD(d)} fill="none" stroke={cl} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
                                    {d.map((v: TrendPoint, i: number) =>
                                        v === null ? null : (
                                            <circle key={i} cx={getX(i)} cy={getY(v)} r={chartHoverIdx === i ? 4.5 : 3} fill={chartHoverIdx === i ? cl : T.card} stroke={cl} strokeWidth={2} style={{ transition: 'r 0.15s' }} />
                                        )
                                    )}
                                    <text x={px + cW + 10} y={getY(last.value) + 4} fill={cl} fontSize={10} fontWeight={600} fontFamily="inherit">
                                        {t.name}
                                    </text>
                                </g>
                            );
                        })}
                        {/* 호버 감지 영역 */}
                        {labels.map((_: string, i: number) => {
                            const colWidth = labels.length > 1 ? cW / (labels.length - 1) : cW;
                            return (
                                <rect key={i} x={getX(i) - colWidth / 2} y={py} width={colWidth} height={cH} fill="transparent" onMouseEnter={() => setChartHoverIdx(i)} style={{ cursor: 'pointer' }} />
                            );
                        })}
                    </svg>
                    {/* 툴팁 */}
                    {chartHoverIdx !== null && (() => {
                        const totalItems: { name: string; value: TrendPoint; color: string }[] = [];
                        const trainerItems: { name: string; value: TrendPoint; color: string }[] = [];
                        if (showCenterTotal) totalItems.push({ name: chartMetric === 'avgPricePerClass' || chartMetric === 'journalWriteRate' ? '센터 평균' : '센터 합산', value: centerTotal[chartHoverIdx], color: T.navy });
                        trainers.forEach((t: any, ti: number) => {
                            if (visibleTrainers.has(t.id)) {
                                trainerItems.push({ name: t.name, value: getTrainerChartData(t)[chartHoverIdx], color: TRAINER_COLORS[ti % TRAINER_COLORS.length] });
                            }
                        });
                        trainerItems.sort((a, b) => compareTrendValueDesc(a.value, b.value));
                        const items = [...totalItems, ...trainerItems];
                        const xPct = ((getX(chartHoverIdx)) / W) * 100;
                        const isRight = xPct > 60;
                        const valFmt = (v: TrendPoint) => v === null ? MISSING_VALUE_TEXT : chartMetricConf?.unit === 'money' ? fmtFull(v) : String(Math.round(v));
                        return (
                            <div style={{
                                position: 'absolute', top: 20,
                                left: isRight ? undefined : `${xPct + 2}%`,
                                right: isRight ? `${100 - xPct + 2}%` : undefined,
                                background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10,
                                padding: '12px 16px', boxShadow: '0 4px 16px rgba(26,48,93,0.12)',
                                zIndex: 10, minWidth: 180, pointerEvents: 'none',
                            }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.navy, marginBottom: 8, borderBottom: `1px solid ${T.borderLight}`, paddingBottom: 6 }}>
                                    {labels[chartHoverIdx]} — {chartMetricConf?.label}
                                </div>
                                {items.map((item, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                                            <span style={{ fontSize: 13, color: T.text2 }}>{item.name}</span>
                                        </div>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: T.navy, fontVariantNumeric: 'tabular-nums', marginLeft: 16 }}>
                                            {valFmt(item.value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14, alignItems: 'center' }}>
                    <button
                        onClick={() => setShowCenterTotal((t) => !t)}
                        style={{
                            padding: '5px 14px',
                            borderRadius: 6,
                            border: showCenterTotal ? `1px solid ${T.navy}` : `1px solid ${T.border}`,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 700,
                            background: showCenterTotal ? T.navy : 'transparent',
                            color: showCenterTotal ? '#fff' : T.text3,
                        }}
                    >
                        {chartMetric === 'avgPricePerClass' || chartMetric === 'journalWriteRate' ? '센터 평균' : '센터 합산'}
                    </button>
                    <div style={{ width: 1, height: 18, background: T.border, margin: '0 6px' }} />
                    <button
                        onClick={toggleAllTrainers}
                        style={{
                            padding: '5px 12px',
                            borderRadius: 6,
                            border: `1px solid ${T.border}`,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            background: 'transparent',
                            color: T.text3,
                        }}
                    >
                        {visibleTrainers.size === trainers.length ? '전체 해제' : '전체 선택'}
                    </button>
                    {trainers.map((t: any, i: number) => (
                        <button
                            key={t.id}
                            onClick={() => toggleTrainer(t.id)}
                            style={{
                                padding: '5px 12px',
                                borderRadius: 6,
                                border: `1px solid ${visibleTrainers.has(t.id) ? TRAINER_COLORS[i % TRAINER_COLORS.length] : T.border}`,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                                background: visibleTrainers.has(t.id) ? `${TRAINER_COLORS[i % TRAINER_COLORS.length]}14` : 'transparent',
                                color: visibleTrainers.has(t.id) ? TRAINER_COLORS[i % TRAINER_COLORS.length] : T.text4,
                            }}
                        >
                            {t.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Trainer Ranking — center-detail API 연동 (rankingPeriod/baseDate 변경 시 갱신) */}
            <RankingSection
                title={`${center.name} 강사 랭킹`}
                data={cdTrainers}
                loading={isCenterDetailFetching}
                nameKey="name"
                rankingPeriod={rankingPeriod}
                onRankingPeriodChange={onRankingPeriodChange}
                baseDate={baseDate}
                onBaseDateChange={onBaseDateChange}
                selectedWeek={selectedWeek}
                onWeekChange={onWeekChange}
                selectedDay={selectedDay}
                onDayChange={onDayChange}
                colorFn={(item: any, i: number) => TRAINER_COLORS[i % TRAINER_COLORS.length]}
            />
        </>
    );
};

/* ── MAIN ── */
export default function RappoSuperAdminDashboard() {
    const navigate = useNavigate();
    const location = useLocation();

    // URL 경로에서 화면 상태 파생
    const getScreenFromPath = (): string => {
        const path = location.pathname.replace('/superadmin', '').replace(/^\//, '');
        if (path.startsWith('centers/')) return 'centerDetail';
        if (path === 'centers') return 'centerPicker';
        if (path === 'detail') return 'detail';
        if (path === 'refunds') return 'refunds';
        if (path === 'teams') return 'teams';
        if (path === 'opportunities') return 'opportunities';
        return 'overview';
    };
    const screen = getScreenFromPath();

    // URL에서 centerId 추출 (센터 상세용)
    const urlCenterId = location.pathname.match(/\/centers\/(.+)/)?.[1];

    const [selectedCenter, setSelectedCenter] = useState<any>(null);
    // 재등록 성공률 산정 근거 모달 대상 (지점 랭킹·강사 랭킹 막대 클릭)
    const [reRegEvidenceTarget, setReRegEvidenceTarget] = useState<SuperAdminReRegistrationEvidenceTarget | null>(null);
    const [prevScreen, setPrevScreen] = useState('overview');
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const profileDropdownRef = useRef<HTMLDivElement>(null);
    // 센터 지표 검색 & 정렬
    const [centerSearch, setCenterSearch] = useState('');
    const [centerSort, setCenterSort] = useState<'name' | 'revenue' | 'totalMembers' | 'trainers' | 'undeliveredRevenue'>('name');
    const [chartPeriod, setChartPeriod] = useState('monthly');

    const setScreen = (next: string, centerId?: string) => {
        if (next === 'overview') navigate('/superadmin');
        else if (next === 'centerPicker') navigate('/superadmin/centers');
        else if (next === 'centerDetail' && centerId) navigate(`/superadmin/centers/${centerId}`);
        else if (next === 'detail') navigate('/superadmin/detail');
        else if (next === 'refunds') navigate('/superadmin/refunds');
        else if (next === 'teams') navigate('/superadmin/teams');
        else if (next === 'opportunities') navigate('/superadmin/opportunities');
    };
    // 지점 랭킹 전용 state
    const [centerRankPeriod, setCenterRankPeriod] = useState('monthly');
    const [centerRankBase, setCenterRankBase] = useState(new Date());
    const [centerRankWeek, setCenterRankWeek] = useState<number | undefined>(undefined);
    const [centerRankDay, setCenterRankDay] = useState<number | undefined>(undefined);
    const [newOpportunityBaseDate, setNewOpportunityBaseDate] = useState(new Date());
    const [newOpportunityCoachPage, setNewOpportunityCoachPage] = useState(1);
    const [newOpportunityCenterId, setNewOpportunityCenterId] = useState('');
    const [newOpportunityCoachId, setNewOpportunityCoachId] = useState('');
    const [newOpportunitySearchInput, setNewOpportunitySearchInput] = useState('');
    const [newOpportunitySearchKeyword, setNewOpportunitySearchKeyword] = useState('');
    const [newOpportunityCoachSort, setNewOpportunityCoachSort] = useState<{
        key: NewOpportunityCoachSortKey;
        direction: SortDirection;
    }>({ key: 'closingRate', direction: 'desc' });

    // 강사 랭킹 전용 state
    const [trainerRankPeriod, setTrainerRankPeriod] = useState('monthly');
    const [trainerRankBase, setTrainerRankBase] = useState(new Date());
    const [trainerRankWeek, setTrainerRankWeek] = useState<number | undefined>(undefined);
    const [trainerRankDay, setTrainerRankDay] = useState<number | undefined>(undefined);

    const [chartBaseDate, setChartBaseDate] = useState(new Date());

    // 센터 상세 강사 랭킹 전용 state (총괄 강사 랭킹과 독립)
    const [cdTrainerRankPeriod, setCdTrainerRankPeriod] = useState('monthly');
    const [cdTrainerRankBase, setCdTrainerRankBase] = useState(new Date());
    const [cdTrainerRankWeek, setCdTrainerRankWeek] = useState<number | undefined>(undefined);
    const [cdTrainerRankDay, setCdTrainerRankDay] = useState<number | undefined>(undefined);

    // 스크롤 위치 보존 래퍼
    const withScrollLock = (setter: (v: string) => void) => (v: string) => {
        const scrollY = window.scrollY;
        setter(v);
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
    };
    const withScrollLockDate = (setter: (v: Date) => void) => (v: Date) => {
        const scrollY = window.scrollY;
        setter(v);
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
    };
    const setChartPeriodStable = withScrollLock(setChartPeriod);
    const setChartBaseDateStable = withScrollLockDate(setChartBaseDate);
    const setCenterRankBaseStable = withScrollLockDate(setCenterRankBase);
    const setNewOpportunityBaseDateStable = withScrollLockDate(setNewOpportunityBaseDate);
    const setTrainerRankBaseStable = withScrollLockDate(setTrainerRankBase);
    const setCenterRankPeriodStable = withScrollLock((v: string) => { setCenterRankPeriod(v); setCenterRankWeek(v === 'weekly' ? 1 : undefined); setCenterRankDay(v === 'daily' ? 1 : undefined); });
    const setTrainerRankPeriodStable = withScrollLock((v: string) => { setTrainerRankPeriod(v); setTrainerRankWeek(v === 'weekly' ? 1 : undefined); setTrainerRankDay(v === 'daily' ? 1 : undefined); });
    const setCdTrainerRankBaseStable = withScrollLockDate(setCdTrainerRankBase);
    const setCdTrainerRankPeriodStable = withScrollLock((v: string) => { setCdTrainerRankPeriod(v); setCdTrainerRankWeek(v === 'weekly' ? 1 : undefined); setCdTrainerRankDay(v === 'daily' ? 1 : undefined); });
    const [detailTab, setDetailTab] = useState('center');
    const [showTotal, setShowTotal] = useState(false);
    const [visibleCenters, setVisibleCenters] = useState<Set<number>>(new Set());
    const [trainerCenterFilter, setTrainerCenterFilter] = useState<Set<string>>(new Set<string>());

    // 세부 지표 전용 state
    const [detailPeriod, setDetailPeriod] = useState('monthly');
    const [detailBaseDate, setDetailBaseDate] = useState(new Date());
    const [detailWeek, setDetailWeek] = useState<number | undefined>(undefined);
    const [detailDay, setDetailDay] = useState<number | undefined>(undefined);
    const setDetailPeriodStable = withScrollLock((v: string) => { setDetailPeriod(v); setDetailWeek(v === 'weekly' ? 1 : undefined); setDetailDay(v === 'daily' ? 1 : undefined); });
    const setDetailBaseDateStable = withScrollLockDate(setDetailBaseDate);
    const [detailTrainerFilter, setDetailTrainerFilter] = useState<Set<string>>(new Set());
    const [detailSearch, setDetailSearch] = useState('');

    // 환불 분석 전용 state
    const [refundPeriod, setRefundPeriod] = useState('monthly');
    const [refundBaseDate, setRefundBaseDate] = useState(new Date());
    const [refundWeek, setRefundWeek] = useState<number | undefined>(undefined);
    const [refundDay, setRefundDay] = useState<number | undefined>(undefined);
    const setRefundPeriodStable = withScrollLock((v: string) => { setRefundPeriod(v); setRefundWeek(v === 'weekly' ? 1 : undefined); setRefundDay(v === 'daily' ? 1 : undefined); });
    const setRefundBaseDateStable = withScrollLockDate(setRefundBaseDate);

    // 그룹수업 매출 포함 ON/OFF (localStorage 유지)
    const queryClient = useQueryClient();
    const [includeGroup, setIncludeGroup] = useState(() => {
        try { return localStorage.getItem('sa_includeGroup') === 'true'; } catch { return false; }
    });
    const handleIncludeGroupToggle = () => {
        setIncludeGroup(prev => {
            const next = !prev;
            try {
                localStorage.setItem('sa_includeGroup', String(next));
                // sa_ 캐시 전부 삭제 (stale 데이터 방지)
                Object.keys(localStorage).filter(k => k.startsWith('sa_') && k !== 'sa_includeGroup').forEach(k => localStorage.removeItem(k));
            } catch {}
            return next;
        });
        // 모든 슈퍼관리자 쿼리 무효화 → 즉시 re-fetch
        queryClient.invalidateQueries();
    };

    // ── API 연동 ──
    const authObject = getItemWithExpiration('authObject');
    const superAdminId = authObject?.superAdminId || authObject?.adminId || '';
    const canOpenCenterView = isCenterViewEnabledFor(String(superAdminId));
    const [openingCenterViewId, setOpeningCenterViewId] = useState<string | null>(null);
    const centerViewMonitorIdsRef = useRef<number[]>([]);

    useEffect(
        () => () => {
            centerViewMonitorIdsRef.current.forEach((monitorId) => window.clearInterval(monitorId));
        },
        [],
    );

    const openCenterView = async (center: any) => {
        const centerId = String(center?.centerId || center?.id || '');
        if (!centerId || openingCenterViewId) return;

        // 비동기 코드 발급 뒤에도 팝업 차단이 발생하지 않도록 클릭 순간 빈 탭을 먼저 연다.
        const centerViewWindow = window.open('', '_blank');
        if (!centerViewWindow) {
            window.alert('새 탭을 열 수 없습니다. 브라우저의 팝업 차단을 해제해주세요.');
            return;
        }
        centerViewWindow.document.title = '센터 관리자 화면 준비 중';
        centerViewWindow.document.body.innerHTML =
            '<div style="font-family:-apple-system,BlinkMacSystemFont,sans-serif;padding:40px;color:#1A305D">센터 관리자 화면을 준비하고 있습니다...</div>';

        setOpeningCenterViewId(centerId);
        try {
            const authorization = getItemWithExpiration('authorization');
            const response = await createCenterViewSessionAPI(centerId, authorization);
            const payload = response?.data?.data ?? response?.data ?? {};
            const oneTimeCode = payload?.oneTimeCode ?? payload?.code;
            if (!oneTimeCode) throw new Error('일회용 열람 코드가 응답에 없습니다.');

            centerViewWindow.location.replace(`/center-view?code=${encodeURIComponent(oneTimeCode)}`);
            const viewSessionId = payload?.viewSessionId ?? payload?.viewSession?.id;
            if (viewSessionId) {
                const monitorId = window.setInterval(() => {
                    if (!centerViewWindow.closed) return;

                    window.clearInterval(monitorId);
                    centerViewMonitorIdsRef.current = centerViewMonitorIdsRef.current.filter(
                        (currentId) => currentId !== monitorId,
                    );
                    void endCenterViewSessionAPI(viewSessionId, authorization, 'tab_closed').catch(() => undefined);
                }, 1000);
                centerViewMonitorIdsRef.current.push(monitorId);
            }
        } catch (error: any) {
            centerViewWindow.close();
            window.alert(
                error?.response?.data?.message ??
                    error?.message ??
                    '센터 관리자 화면을 열 수 없습니다. 잠시 후 다시 시도해주세요.',
            );
        } finally {
            setOpeningCenterViewId(null);
        }
    };

    // ── localStorage 캐시 (새로고침 시 즉시 표시, 5분 유효) ──
    const baseDateParam = isSameMonth(centerRankBase) ? undefined : baseDateToParam(centerRankBase);
    const chartBaseDateParam = isSameMonth(chartBaseDate) ? undefined : baseDateToParam(chartBaseDate);
    const weekDayKey = centerRankPeriod === 'weekly' && centerRankWeek != null ? `_w${centerRankWeek}` : centerRankPeriod === 'daily' && centerRankDay != null ? `_d${centerRankDay}` : '';
    const groupKey = includeGroup ? '_g' : '';
    const overviewCacheKey = `sa_overview_${superAdminId}_${centerRankPeriod}_${baseDateParam || 'current'}${weekDayKey}${groupKey}`;
    const trendCacheKey = `sa_trend_${superAdminId}_${chartPeriod}_${chartBaseDateParam || 'current'}${groupKey}`;

    const getCached = (key: string) => {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return undefined;
            const parsed = JSON.parse(raw);
            if (parsed.expiry && Date.now() > parsed.expiry) {
                localStorage.removeItem(key);
                return undefined;
            }
            return parsed.data;
        } catch { return undefined; }
    };
    const setCache = (key: string, data: any, minutes = 1) => {
        try {
            localStorage.setItem(key, JSON.stringify({ data, expiry: Date.now() + minutes * 60 * 1000 }));
        } catch { /* quota exceeded 등 무시 */ }
    };

    // 총괄 대시보드 데이터 조회
    const { data: overviewRes, isLoading: isOverviewLoading, isFetching: isOverviewFetching } = useQuery({
        ...fetchSuperAdminOverview(superAdminId, centerRankPeriod, baseDateParam, centerRankPeriod === 'weekly' ? centerRankWeek : undefined, centerRankPeriod === 'daily' ? centerRankDay : undefined, includeGroup),
        enabled: !!superAdminId,
        staleTime: 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        retry: false,
        keepPreviousData: true,
        initialData: getCached(overviewCacheKey),
        onSuccess: (res: any) => setCache(overviewCacheKey, res),
    });

    // 강사 랭킹 전용 API (지점 랭킹과 독립적 기간/주/일)
    const trainerBaseDateParam = isSameMonth(trainerRankBase) ? undefined : baseDateToParam(trainerRankBase);
    const trainerWeekDayKey = trainerRankPeriod === 'weekly' && trainerRankWeek != null ? `_w${trainerRankWeek}` : trainerRankPeriod === 'daily' && trainerRankDay != null ? `_d${trainerRankDay}` : '';
    const trainerCacheKey = `sa_trainer_${superAdminId}_${trainerRankPeriod}_${trainerBaseDateParam || 'current'}${trainerWeekDayKey}${groupKey}`;

    const { data: trainerOverviewRes, isFetching: isTrainerFetching } = useQuery({
        ...fetchSuperAdminOverview(superAdminId, trainerRankPeriod, trainerBaseDateParam, trainerRankPeriod === 'weekly' ? trainerRankWeek : undefined, trainerRankPeriod === 'daily' ? trainerRankDay : undefined, includeGroup),
        queryKey: ['superAdminTrainerOverview', superAdminId, trainerRankPeriod, trainerBaseDateParam || '', trainerRankWeek ?? '', trainerRankDay ?? '', includeGroup],
        enabled: !!superAdminId,
        staleTime: 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        retry: false,
        keepPreviousData: true,
        initialData: getCached(trainerCacheKey),
        onSuccess: (res: any) => setCache(trainerCacheKey, res),
    });

    // 매출 추이 데이터 조회
    const { data: trendRes, isFetching: isTrendFetching, isError: isTrendError, refetch: refetchTrend } = useQuery({
        ...fetchSuperAdminRevenueTrend(superAdminId, chartPeriod, chartBaseDateParam, includeGroup),
        enabled: !!superAdminId,
        staleTime: 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        retry: false,
        keepPreviousData: true,
        initialData: getCached(trendCacheKey),
        onSuccess: (res: any) => setCache(trendCacheKey, res),
    });

    const opportunityStartDate = new Date(newOpportunityBaseDate.getFullYear(), newOpportunityBaseDate.getMonth(), 1);
    const opportunityEndDate = new Date(newOpportunityBaseDate.getFullYear(), newOpportunityBaseDate.getMonth() + 1, 1);
    const opportunityStartTime = opportunityStartDate.toISOString();
    const opportunityEndTime = opportunityEndDate.toISOString();
    const opportunityMonthScopeLabel = isSameMonth(newOpportunityBaseDate)
        ? `이번달(${newOpportunityBaseDate.getMonth() + 1}월)`
        : `${newOpportunityBaseDate.getFullYear()}년 ${newOpportunityBaseDate.getMonth() + 1}월`;
    const {
        data: newOpportunityFilterOptionsRes,
        isError: isNewOpportunityFilterOptionsError,
        isFetching: isNewOpportunityFilterOptionsFetching,
        refetch: refetchNewOpportunityFilterOptions,
    } = useQuery({
        queryKey: ['superAdminNewOpportunityFilterOptions', superAdminId, opportunityStartTime, opportunityEndTime],
        queryFn: async () => {
            const authorization = getItemWithExpiration('authorization');
            return await getSuperAdminNewOpportunitySummaryAPI(superAdminId, authorization, {
                startTime: opportunityStartTime,
                endTime: opportunityEndTime,
                includeActiveMembers: 'true',
                includeRows: 'false',
            });
        },
        enabled: !!superAdminId,
        staleTime: 60 * 1000,
        retry: false,
    });
    const {
        data: newOpportunitySummaryRes,
        isError: isNewOpportunitySummaryError,
        isFetching: isNewOpportunitySummaryFetching,
        refetch: refetchNewOpportunitySummary,
    } = useQuery({
        queryKey: [
            'superAdminNewOpportunitySummary',
            superAdminId,
            opportunityStartTime,
            opportunityEndTime,
            'includeActiveMembers',
            'aggregateOnly',
            newOpportunityCenterId,
            newOpportunityCoachId,
        ],
        queryFn: async () => {
            const authorization = getItemWithExpiration('authorization');
            return await getSuperAdminNewOpportunitySummaryAPI(superAdminId, authorization, {
                centerId: newOpportunityCenterId || undefined,
                coachId: newOpportunityCoachId || undefined,
                startTime: opportunityStartTime,
                endTime: opportunityEndTime,
                includeActiveMembers: 'true',
                includeRows: 'false',
            });
        },
        enabled: !!superAdminId,
        staleTime: 60 * 1000,
        retry: false,
    });
    const newOpportunitySummaryData = newOpportunitySummaryRes?.data?.data;
    const newOpportunitySummary = newOpportunitySummaryData?.summary || {};
    const newOpportunityTopCoaches = useMemo(
        () => newOpportunitySummaryData?.byCoach || [],
        [newOpportunitySummaryData?.byCoach]
    );
    const newOpportunityFilterOptionCoaches = useMemo(
        () => newOpportunityFilterOptionsRes?.data?.data?.byCoach || [],
        [newOpportunityFilterOptionsRes?.data?.data?.byCoach]
    );
    const sortedNewOpportunityCoaches = useMemo(() => {
        const normalizedSearch = newOpportunitySearchKeyword.trim().toLowerCase();
        const searchedCoaches = normalizedSearch
            ? newOpportunityTopCoaches.filter((coach: any) =>
                  [coach.centerName, coach.coachName]
                      .filter(Boolean)
                      .some((value) => String(value).toLowerCase().includes(normalizedSearch))
              )
            : newOpportunityTopCoaches;
        const getValue = (coach: any, key: NewOpportunityCoachSortKey) => {
            if (key === 'centerName') return coach.centerName || '';
            if (key === 'coachName') return coach.coachName || '';
            return Number(coach[key] || 0);
        };
        return [...searchedCoaches].sort((a: any, b: any) => {
            const aValue = getValue(a, newOpportunityCoachSort.key);
            const bValue = getValue(b, newOpportunityCoachSort.key);
            const compareResult =
                typeof aValue === 'string' || typeof bValue === 'string'
                    ? String(aValue).localeCompare(String(bValue), 'ko-KR')
                    : Number(aValue) - Number(bValue);
            return newOpportunityCoachSort.direction === 'asc' ? compareResult : -compareResult;
        });
    }, [newOpportunitySearchKeyword, newOpportunityTopCoaches, newOpportunityCoachSort]);
    const newOpportunitySortColumns: { key: NewOpportunityCoachSortKey; label: string }[] = [
        { key: 'centerName', label: '소속 센터' },
        { key: 'coachName', label: '강사' },
        { key: 'opportunityCount', label: '신규기회' },
        { key: 'leadMemberCount', label: '대상 회원' },
        { key: 'closedMemberCount', label: '전환 회원' },
        { key: 'closingRate', label: '클로징률' },
    ];
    const handleNewOpportunityCoachSort = (key: NewOpportunityCoachSortKey) => {
        setNewOpportunityCoachSort((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return {
                key,
                direction: key === 'centerName' || key === 'coachName' ? 'asc' : 'desc',
            };
        });
    };
    const newOpportunityCoachPageSize = 20;
    const newOpportunityCoachPageCount = Math.max(Math.ceil(sortedNewOpportunityCoaches.length / newOpportunityCoachPageSize), 1);
    const newOpportunityCoachDisplayPage = Math.min(newOpportunityCoachPage, newOpportunityCoachPageCount);
    const newOpportunityCoachRows = sortedNewOpportunityCoaches.slice(
        (newOpportunityCoachDisplayPage - 1) * newOpportunityCoachPageSize,
        newOpportunityCoachDisplayPage * newOpportunityCoachPageSize
    );
    const newOpportunityCoachStartIndex =
        sortedNewOpportunityCoaches.length > 0 ? (newOpportunityCoachDisplayPage - 1) * newOpportunityCoachPageSize + 1 : 0;
    const newOpportunityCoachEndIndex =
        sortedNewOpportunityCoaches.length > 0
            ? Math.min(newOpportunityCoachDisplayPage * newOpportunityCoachPageSize, sortedNewOpportunityCoaches.length)
            : 0;
    const isNewOpportunityCurrentMonth = isSameMonth(newOpportunityBaseDate);

    useEffect(() => {
        setNewOpportunityCoachPage(1);
    }, [opportunityStartTime, opportunityEndTime, newOpportunityCenterId, newOpportunityCoachId, newOpportunitySearchKeyword]);

    useEffect(() => {
        if (newOpportunityCoachPage > newOpportunityCoachPageCount) {
            setNewOpportunityCoachPage(newOpportunityCoachPageCount);
        }
    }, [newOpportunityCoachPage, newOpportunityCoachPageCount]);

    // 세부 지표 전용 API
    const detailBaseDateParam = isSameMonth(detailBaseDate) ? undefined : baseDateToParam(detailBaseDate);
    const detailWeekDayKey = detailPeriod === 'weekly' && detailWeek != null ? `_w${detailWeek}` : detailPeriod === 'daily' && detailDay != null ? `_d${detailDay}` : '';
    const detailCacheKey = `sa_detail_${superAdminId}_${detailPeriod}_${detailBaseDateParam || 'current'}${detailWeekDayKey}${groupKey}`;

    const { data: detailOverviewRes, isFetching: isDetailFetching } = useQuery({
        ...fetchSuperAdminOverview(superAdminId, detailPeriod, detailBaseDateParam, detailPeriod === 'weekly' ? detailWeek : undefined, detailPeriod === 'daily' ? detailDay : undefined, includeGroup),
        queryKey: ['superAdminDetailOverview', superAdminId, detailPeriod, detailBaseDateParam || '', detailWeek ?? '', detailDay ?? '', includeGroup],
        enabled: !!superAdminId && screen === 'detail',
        staleTime: 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true,
        initialData: getCached(detailCacheKey),
        onSuccess: (res: any) => setCache(detailCacheKey, res),
    });

    // 환불 분석 전용 API
    const refundBaseDateParam = isSameMonth(refundBaseDate) ? undefined : baseDateToParam(refundBaseDate);
    const refundWeekDayKey = refundPeriod === 'weekly' && refundWeek != null ? `_w${refundWeek}` : refundPeriod === 'daily' && refundDay != null ? `_d${refundDay}` : '';
    const refundCacheKey = `sa_refund_${superAdminId}_${refundPeriod}_${refundBaseDateParam || 'current'}${refundWeekDayKey}${groupKey}`;
    const { data: refundAnalysisRes, isFetching: isRefundFetching, isLoading: isRefundLoading } = useQuery({
        ...fetchSuperAdminRefundAnalysis(superAdminId, refundPeriod, refundBaseDateParam, refundPeriod === 'weekly' ? refundWeek : undefined, refundPeriod === 'daily' ? refundDay : undefined, includeGroup),
        enabled: !!superAdminId && screen === 'refunds',
        staleTime: 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        retry: false,
        keepPreviousData: true,
        initialData: getCached(refundCacheKey),
        onSuccess: (res: any) => setCache(refundCacheKey, res),
    });

    // KPI 전용 API (항상 현재 기준, period/week 무관)
    const { data: kpiRes, isLoading: isKpiLoading } = useQuery({
        ...fetchSuperAdminOverview(superAdminId, 'monthly', undefined, undefined, undefined, includeGroup),
        queryKey: ['superAdminKpi', superAdminId, includeGroup],
        enabled: !!superAdminId,
        staleTime: 60 * 1000,
        cacheTime: 10 * 60 * 1000,
    });

    // API 데이터 (더미 데이터는 없다 — 없으면 빈 목록)
    const apiData = overviewRes?.data?.data;
    const apiKpis = kpiRes?.data?.data?.kpis || apiData?.kpis;
    const kpiCentersRaw = kpiRes?.data?.data?.centers; // KPI용 센터별 데이터 (항상 현재 기준)
    // 센터 지표 카드용 (항상 현재 월, 랭킹 필터 무관)
    const kpiCenters = kpiCentersRaw; // liveCenter 머지에서 사용
    const pickerCenters = useMemo(() => {
        if (!kpiCentersRaw?.length) return [];
        return kpiCentersRaw.map((c: any, i: number) => {
            const pf = c.periodFiltered || {};
            const snap = c.snapshot || {};
            return {
                id: i + 1,
                centerId: c.centerId,
                name: c.centerName,
                monthRevenue: c.monthRevenue || 0,
                revenue: pf.revenue || 0,
                totalMembers: countOrMissing(snap.totalMembers),
                trainers: snap.trainers || 0,
                undeliveredRevenue: snap.undeliveredRevenue ?? 0,
                degraded: readDegraded(c),
            };
        });
    }, [kpiCentersRaw]);
    const apiCenters = apiData?.centers;
    const apiTrainers = trainerOverviewRes?.data?.data?.trainers || apiData?.trainers;

    const trendData = trendRes?.data?.data;

    // 센터 데이터: API 만. 없으면 빈 목록
    const activeCenters = useMemo(() => {
        if (apiCenters?.length) {
            return apiCenters.map((c: any, i: number) => {
                const pf = c.periodFiltered || {};
                const snap = c.snapshot || {};
                const degraded = readDegraded(c);
                return {
                    id: i + 1,
                    centerId: c.centerId,
                    name: c.centerName,
                    // KPI 카드용 매출 (항상 일/주/월)
                    monthRevenue: c.monthRevenue || 0,
                    prevMonthRevenue: c.prevMonthRevenue || 0,
                    weekRevenue: c.weekRevenue || 0,
                    prevWeekRevenue: c.prevWeekRevenue || 0,
                    dayRevenue: c.dayRevenue || 0,
                    prevDayRevenue: c.prevDayRevenue || 0,
                    // 기간별 필터 가능 메트릭 (rankingPeriod에 따라 변동)
                    revenue: pf.revenue || 0,
                    prevRevenue: pf.prevRevenue || 0,
                    totalClasses: pf.totalClasses || 0,
                    noShowCount: pf.noShowCount || 0,
                    journalCount: pf.journalCount || 0,
                    newPayMembers: pf.newPayMembers || 0,
                    reRegPayMembers: pf.reRegPayMembers || 0,
                    newPayRevenue: pf.newPayRevenue || 0,
                    reRegPayRevenue: pf.reRegPayRevenue || 0,
                    // 스냅샷 (기간 무관)
                    undeliveredRevenue: snap.undeliveredRevenue ?? 0,
                    totalMembers: countOrMissing(snap.totalMembers),
                    trainers: snap.trainers || 0,
                    degraded,
                    // 신규 메트릭
                    sameDayCancelCount: countOrMissing(pf.sameDayCancelCount),
                    sameDayChangeCount: countOrMissing(pf.sameDayChangeCount),
                    avgPricePerClass: avgPriceOrMissing(pf.avgPricePerClass, degraded),
                    journalWriteRate: pf.journalWriteRate ?? null,
                    // 재등록 성공률 (월 단위 — baseDate 월). 필드가 없는 구 서버면 null
                    ...toCenterReRegistrationSuccessFields(c.reRegistrationSuccess),
                };
            });
        }
        return [];
    }, [apiCenters]);
    const newOpportunityCenterOptions = useMemo(
        () =>
            activeCenters
                .map((center: any) => ({
                    centerId: center.centerId,
                    centerName: center.name,
                }))
                .filter((center: any) => center.centerId && center.centerName)
                .sort((a: any, b: any) => String(a.centerName).localeCompare(String(b.centerName), 'ko-KR')),
        [activeCenters]
    );
    const newOpportunityCoachOptions = useMemo(() => {
        const optionMap = new Map<string, any>();
        newOpportunityFilterOptionCoaches.forEach((coach: any) => {
            if (newOpportunityCenterId && coach.centerId !== newOpportunityCenterId) return;
            const key = `${coach.centerId || ''}_${coach.coachId || coach.coachName || ''}`;
            if (!key.trim() || optionMap.has(key)) return;
            optionMap.set(key, {
                centerId: coach.centerId || '',
                centerName: coach.centerName || '',
                coachId: coach.coachId || '',
                coachName: coach.coachName || '이름 없음',
            });
        });
        return Array.from(optionMap.values()).sort((a: any, b: any) => {
            const centerCompare = String(a.centerName || '').localeCompare(String(b.centerName || ''), 'ko-KR');
            if (centerCompare !== 0) return centerCompare;
            return String(a.coachName || '').localeCompare(String(b.coachName || ''), 'ko-KR');
        });
    }, [newOpportunityCenterId, newOpportunityFilterOptionCoaches]);
    const selectedNewOpportunityCenterName =
        newOpportunityCenterOptions.find((center: any) => center.centerId === newOpportunityCenterId)?.centerName || '';
    const selectedNewOpportunityCoachName =
        newOpportunityCoachOptions.find((coach: any) => coach.coachId === newOpportunityCoachId)?.coachName || '';
    const hasNewOpportunityFilter =
        !!newOpportunityCenterId || !!newOpportunityCoachId || !!newOpportunitySearchKeyword || !!newOpportunitySearchInput;
    const applyNewOpportunitySearch = () => {
        setNewOpportunitySearchKeyword(newOpportunitySearchInput.trim());
    };
    const resetNewOpportunityFilters = () => {
        setNewOpportunityCenterId('');
        setNewOpportunityCoachId('');
        setNewOpportunitySearchInput('');
        setNewOpportunitySearchKeyword('');
    };

    useEffect(() => {
        if (!newOpportunityCoachId) return;
        const hasCoach = newOpportunityCoachOptions.some((coach: any) => coach.coachId === newOpportunityCoachId);
        if (!hasCoach) setNewOpportunityCoachId('');
    }, [newOpportunityCoachId, newOpportunityCoachOptions]);

    // 강사 데이터: API 만. 없으면 빈 목록
    const activeTrainers = useMemo(() => {
        if (apiTrainers?.length) {
            return apiTrainers.map((t: any, i: number) => {
                const pf = t.periodFiltered || {};
                const snap = t.snapshot || {};
                const degraded = readDegraded(t);
                return {
                    id: i + 1,
                    coachId: t.coachId,
                    name: t.name,
                    center: t.centerName,
                    // 기간별 필터 가능 (랭킹 key와 일치시킴)
                    revenue: pf.revenue || 0,
                    prevRevenue: pf.prevRevenue || 0,
                    monthRevenue: pf.revenue || 0, // 테이블 호환
                    prevMonthRevenue: pf.prevRevenue || 0,
                    totalClasses: countOrMissing(pf.totalClasses),
                    noShowCount: countOrMissing(pf.noShowCount),
                    journalCount: countOrMissing(pf.journalCount),
                    newPayMembers: pf.newPayMembers || 0,
                    reRegPayMembers: pf.reRegPayMembers || 0,
                    // 스냅샷
                    totalMembers: countOrMissing(snap.totalMembers),
                    undeliveredRevenue: snap.undeliveredRevenue ?? 0,
                    reviewAvg: snap.reviewAvg || 0,
                    degraded,
                    // 신규 메트릭
                    sameDayCancelCount: countOrMissing(pf.sameDayCancelCount),
                    sameDayChangeCount: countOrMissing(pf.sameDayChangeCount),
                    avgPricePerClass: avgPriceOrMissing(pf.avgPricePerClass, degraded),
                    journalWriteRate: pf.journalWriteRate ?? null,
                    // 재등록 성공률 (월 단위). 여러 지점 강사는 서버 합산값 — 지점 분해는 근거 모달 탭용.
                    // 필드가 없는 구 서버면 null → 「-」·맨 뒤·등수 없음
                    ...toTrainerReRegistrationSuccessFields(t.reRegistrationSuccess),
                };
            });
        }
        return [];
    }, [apiTrainers]);

    // 세부 지표 전용 데이터
    const detailData = detailOverviewRes?.data?.data;
    const detailCenters = useMemo(() => {
        const centers = detailData?.centers;
        if (!centers?.length) return activeCenters;
        return centers.map((c: any, i: number) => {
            const pf = c.periodFiltered || {};
            const snap = c.snapshot || {};
            const degraded = readDegraded(c);
            return {
                id: i + 1,
                centerId: c.centerId,
                name: c.centerName,
                monthRevenue: c.monthRevenue || 0,
                weekRevenue: c.weekRevenue || 0,
                dayRevenue: c.dayRevenue || 0,
                revenue: pf.revenue || 0,
                prevRevenue: pf.prevRevenue || 0,
                totalClasses: pf.totalClasses || 0,
                noShowCount: pf.noShowCount || 0,
                journalCount: pf.journalCount || 0,
                newPayMembers: pf.newPayMembers || 0,
                reRegPayMembers: pf.reRegPayMembers || 0,
                newPayRevenue: pf.newPayRevenue || 0,
                reRegPayRevenue: pf.reRegPayRevenue || 0,
                sameDayCancelCount: countOrMissing(pf.sameDayCancelCount),
                sameDayChangeCount: countOrMissing(pf.sameDayChangeCount),
                avgPricePerClass: avgPriceOrMissing(pf.avgPricePerClass, degraded),
                journalWriteRate: pf.journalWriteRate ?? null,
                ...toCenterReRegistrationSuccessFields(c.reRegistrationSuccess),
                undeliveredRevenue: snap.undeliveredRevenue ?? 0,
                totalMembers: countOrMissing(snap.totalMembers),
                trainers: snap.trainers || 0,
                degraded,
            };
        });
    }, [detailData, activeCenters]);

    const detailTrainers = useMemo(() => {
        const trainers = detailData?.trainers;
        if (!trainers?.length) return activeTrainers;
        return trainers.map((t: any, i: number) => {
            const pf = t.periodFiltered || {};
            const snap = t.snapshot || {};
            const degraded = readDegraded(t);
            return {
                id: i + 1,
                coachId: t.coachId,
                name: t.name,
                center: t.centerName,
                revenue: pf.revenue || 0,
                monthRevenue: pf.revenue || 0,
                prevRevenue: pf.prevRevenue || 0,
                prevMonthRevenue: pf.prevRevenue || 0,
                totalClasses: countOrMissing(pf.totalClasses),
                noShowCount: countOrMissing(pf.noShowCount),
                journalCount: countOrMissing(pf.journalCount),
                newPayMembers: pf.newPayMembers || 0,
                reRegPayMembers: pf.reRegPayMembers || 0,
                newPayRevenue: pf.newPayRevenue || 0,
                reRegPayRevenue: pf.reRegPayRevenue || 0,
                sameDayCancelCount: countOrMissing(pf.sameDayCancelCount),
                sameDayChangeCount: countOrMissing(pf.sameDayChangeCount),
                avgPricePerClass: avgPriceOrMissing(pf.avgPricePerClass, degraded),
                journalWriteRate: pf.journalWriteRate ?? null,
                totalMembers: countOrMissing(snap.totalMembers),
                undeliveredRevenue: snap.undeliveredRevenue ?? 0,
                reviewAvg: snap.reviewAvg || 0,
                degraded,
            };
        });
    }, [detailData, activeTrainers]);

    // 환불 분석 전용 데이터
    const refundData = refundAnalysisRes?.data?.data;
    const refundSummary = refundData?.summary || {};
    const refundCenters = useMemo(() => {
        const centers = refundData?.centers;
        if (!centers?.length) return [];
        return centers.map((c: any, i: number) => {
            const pf = c.periodFiltered || {};
            const snap = c.snapshot || {};
            const source = c.sourceBreakdown || {};
            return {
                id: i + 1,
                centerId: c.centerId,
                name: c.centerName || c.name || '센터 정보 없음',
                grossSales: toNumber(pf.grossSales),
                refundAmount: toNumber(pf.refundAmount),
                netSales: toNumber(pf.netSales),
                refundRate: toNullableNumber(pf.refundRate),
                refundCount: toNumber(pf.refundCount),
                refundMemberCount: toNumber(pf.refundMemberCount),
                fullRefundAmount: toNumber(pf.fullRefundAmount),
                fullRefundCount: toNumber(pf.fullRefundCount),
                partialRefundAmount: toNumber(pf.partialRefundAmount),
                partialRefundCount: toNumber(pf.partialRefundCount),
                salesCount: toNumber(pf.salesCount),
                salesMemberCount: toNumber(pf.salesMemberCount),
                ptRefundAmount: toNumber(source.pt?.refundAmount),
                ptRefundCount: toNumber(source.pt?.refundCount),
                groupRefundAmount: toNumber(source.group?.refundAmount),
                groupRefundCount: toNumber(source.group?.refundCount),
                facilityRefundAmount: toNumber(source.facility?.refundAmount),
                facilityRefundCount: toNumber(source.facility?.refundCount),
                trainers: toNumber(snap.trainers),
                totalMembers: toNumber(snap.totalMembers),
            };
        });
    }, [refundData]);

    const refundTrainers = useMemo(() => {
        const trainers = refundData?.trainers;
        if (!trainers?.length) return [];
        return trainers.map((t: any, i: number) => {
            const pf = t.periodFiltered || {};
            const snap = t.snapshot || {};
            const source = t.sourceBreakdown || {};
            return {
                id: i + 1,
                centerId: t.centerId,
                coachId: t.coachId,
                name: t.name || t.coachName || '이름 없음',
                center: t.centerName || '센터 정보 없음',
                grossSales: toNumber(pf.grossSales),
                refundAmount: toNumber(pf.refundAmount),
                netSales: toNumber(pf.netSales),
                refundRate: toNullableNumber(pf.refundRate),
                refundCount: toNumber(pf.refundCount),
                refundMemberCount: toNumber(pf.refundMemberCount),
                fullRefundAmount: toNumber(pf.fullRefundAmount),
                fullRefundCount: toNumber(pf.fullRefundCount),
                partialRefundAmount: toNumber(pf.partialRefundAmount),
                partialRefundCount: toNumber(pf.partialRefundCount),
                salesCount: toNumber(pf.salesCount),
                salesMemberCount: toNumber(pf.salesMemberCount),
                ptRefundAmount: toNumber(source.pt?.refundAmount),
                ptRefundCount: toNumber(source.pt?.refundCount),
                groupRefundAmount: toNumber(source.group?.refundAmount),
                groupRefundCount: toNumber(source.group?.refundCount),
                totalMembers: toNumber(snap.totalMembers),
            };
        });
    }, [refundData]);

    const detailFilterInitialized = React.useRef(false);
    useEffect(() => {
        if (detailCenters.length > 0 && !detailFilterInitialized.current) {
            setDetailTrainerFilter(new Set(detailCenters.map((c: any) => c.name)));
            detailFilterInitialized.current = true;
        }
    }, [detailCenters]);

    const detailFilteredCenters = useMemo(() => {
        let filtered = detailTrainerFilter.size === 0 ? [] : detailCenters.filter((c: any) => detailTrainerFilter.has(c.name));
        if (detailSearch) filtered = filtered.filter((c: any) => c.name.toLowerCase().includes(detailSearch.toLowerCase()));
        return filtered;
    }, [detailCenters, detailTrainerFilter, detailSearch]);
    const detailFilteredTrainers = useMemo(() => {
        let filtered = detailTrainerFilter.size === 0 ? [] : detailTrainers.filter((t: any) => detailTrainerFilter.has(t.center));
        if (detailSearch) filtered = filtered.filter((t: any) => t.name.toLowerCase().includes(detailSearch.toLowerCase()) || t.center?.toLowerCase().includes(detailSearch.toLowerCase()));
        return filtered;
    }, [detailTrainers, detailTrainerFilter, detailSearch]);

    // 빈 Set = 전체 선택 상태
    // activeCenters 로드 시 강사 필터를 전체 선택으로 초기화 (최초 1회만)
    const activeCenterNames = useMemo(() => activeCenters.map((c: any) => c.name), [activeCenters]);
    const trainerFilterInitialized = React.useRef(false);
    useEffect(() => {
        if (activeCenterNames.length > 0 && !trainerFilterInitialized.current) {
            setTrainerCenterFilter(new Set(activeCenterNames));
            trainerFilterInitialized.current = true;
        }
    }, [activeCenterNames]);

    const isAllCentersVisible = visibleCenters.size === 0;
    const isCenterVisible = (id: number) => isAllCentersVisible || visibleCenters.has(id);

    const toggleCenter = (id: number) => {
        setVisibleCenters((p) => {
            if (isAllCentersVisible) {
                // 전체에서 하나 끄기: 전체 ID 넣고 해당 ID만 제거
                const all = new Set<number>(activeCenters.map((c: any) => c.id));
                all.delete(id);
                return all;
            }
            const n = new Set<number>(p);
            n.has(id) ? n.delete(id) : n.add(id);
            // 모두 선택되면 빈 Set으로 복귀
            if (n.size === activeCenters.length) return new Set<number>();
            return n;
        });
    };
    const toggleAll = () => {
        // 전체 선택(빈 Set) ↔ 전체 해제(빈... 아닌 상태)
        isAllCentersVisible ? setVisibleCenters(new Set([-1])) : setVisibleCenters(new Set());
    };

    // KPI: API 합계 우선, 없으면 API 센터 값의 합
    const totalDayRev = apiKpis?.dayRevenue ?? activeCenters.reduce((s: number, c: any) => s + c.dayRevenue, 0);
    const totalPrevDayRev = apiKpis?.prevDayRevenue ?? activeCenters.reduce((s: number, c: any) => s + c.prevDayRevenue, 0);
    const totalWeekRev = apiKpis?.weekRevenue ?? activeCenters.reduce((s: number, c: any) => s + c.weekRevenue, 0);
    const totalPrevWeekRev = apiKpis?.prevWeekRevenue ?? activeCenters.reduce((s: number, c: any) => s + c.prevWeekRevenue, 0);
    const totalMonthRev = apiKpis?.monthRevenue ?? activeCenters.reduce((s: number, c: any) => s + c.monthRevenue, 0);
    const totalPrevRev = apiKpis?.prevMonthRevenue ?? activeCenters.reduce((s: number, c: any) => s + c.prevMonthRevenue, 0);
    const totalUncompleted = apiKpis?.totalUndeliveredRevenue ?? activeCenters.reduce((s: number, c: any) => s + (c.undeliveredRevenue || 0), 0);

    const now = new Date();
    // API 라벨 우선, 없으면 프론트 계산 폴백
    const kpiLabels = apiKpis;
    const dayLabel = kpiLabels?.dayLabel || `${now.getMonth()+1}/${now.getDate()}`;
    const prevDayLabel = kpiLabels?.prevDayLabel || '';
    const weekLabel = kpiLabels?.weekLabel || (() => { const ws = new Date(now); ws.setDate(now.getDate() - now.getDay()); if (ws.getMonth() < now.getMonth() || ws.getFullYear() < now.getFullYear()) { ws.setFullYear(now.getFullYear()); ws.setMonth(now.getMonth()); ws.setDate(1); } return `${ws.getMonth()+1}/${ws.getDate()}~${now.getMonth()+1}/${now.getDate()}`; })();
    const prevWeekLabel = kpiLabels?.prevWeekLabel || '';
    const monthLabel = kpiLabels?.monthLabel || `${now.getMonth()+1}/1~${now.getMonth()+1}/${now.getDate()}`;
    const prevMonthLabel = kpiLabels?.prevMonthLabel || '';
    const kpis = [
        { label: `오늘 매출 (${dayLabel})`, value: fmtFull(totalDayRev), change: pctChange(totalDayRev, totalPrevDayRev), changeSub: prevDayLabel ? `전일(${prevDayLabel}) 대비` : '전일 대비', color: T.blue },
        { label: `이번주 매출 (${weekLabel.replace(/~[^~]+$/, '~오늘')})`, value: fmtFull(totalWeekRev), change: pctChange(totalWeekRev, totalPrevWeekRev), changeSub: prevWeekLabel ? `전주(${prevWeekLabel}) 대비` : '전주 대비', color: T.gold },
        { label: `${now.getMonth()+1}월 매출 (${monthLabel})`, value: fmtFull(totalMonthRev), change: pctChange(totalMonthRev, totalPrevRev), changeSub: prevMonthLabel ? `전월(${prevMonthLabel}) 대비` : '전월 대비', color: T.success },
        // 서버가 새 합계를 못 내면 숫자는 유지하고 kpis.degraded 로 알린다 → 「집계 지연」
        { label: '총 미수업금액', value: withDegraded(fmtFull(totalUncompleted), readDegraded(apiKpis), 'undeliveredRevenue'), sub: '부채', color: T.error, isDebt: true, tooltip: '회원이 선결제한 수강권 중 아직 소화하지 않은 수업의 잔여 금액 합계입니다.\n(남은 횟수 × 회당 단가)' },
    ];

    const trainerFilterAllSelected = trainerCenterFilter.size >= activeCenters.length;
    const filteredTrainers = trainerCenterFilter.size === 0 ? [] : activeTrainers.filter((t: any) => trainerCenterFilter.has(t.center));
    const showTrainerSub = trainerFilterAllSelected || trainerCenterFilter.size > 1;

    const periodLabel = ({ yearly: '연', monthly: '월', weekly: '주', daily: '일' } as Record<string,string>)[centerRankPeriod] || '월';

    const centerColumns = [
        { key: 'name', label: '지점명', render: (v: string, r: any) => (
            <span
                style={{ fontWeight: 600, color: T.blue, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                onClick={() => { setSelectedCenter(r); setPrevScreen('detail'); setScreen('centerDetail', r.centerId || r.id); }}
            >{v}</span>
        ) },
        { key: 'trainers', label: '강사수', align: 'right' },
        { key: 'revenue', label: `${periodLabel}매출`, align: 'right', render: (v: number) => fmt(v || 0) },
        { key: 'revGrowth', label: '전기대비', align: 'right', sortValue: (r: any) => pctChange(r.revenue, r.prevRevenue), render: (_: any, r: any) => <Badge value={pctChange(r.revenue, r.prevRevenue)} /> },
        { key: 'totalMembers', label: '활성 회원', align: 'right', render: (v: number | null, r: any) => missingOr(v, r.degraded, 'totalMembers', () => v) },
        { key: 'newPayMembers', label: '신규결제', align: 'right', render: (v: number) => <span style={{ color: T.blue }}>+{v || 0}</span> },
        { key: 'reRegPayMembers', label: '재등록', align: 'right', render: (v: number) => <span style={{ color: '#9E76F3' }}>{v || 0}</span> },
        { key: 'undeliveredRevenue', label: '미수업금액', align: 'right', render: (v: number, r: any) => withDegraded(v ? <span style={{ color: T.error, fontWeight: 600 }}>{fmt(v)}</span> : <span style={{ color: T.text4 }}>-</span>, r.degraded, 'undeliveredRevenue') },
        { key: 'totalClasses', label: '진행한 수업수', align: 'right', render: (v: number, r: any) => withDegraded(v, r.degraded, 'totalClasses') },
        { key: 'noShowCount', label: '노쇼', align: 'right', render: (v: number, r: any) => withDegraded(v, r.degraded, 'noShowCount') },
        { key: 'journalCount', label: '일지', align: 'right', render: (v: number, r: any) => withDegraded(v, r.degraded, 'journalCount') },
        { key: 'journalWriteRate', label: '일지율', align: 'right', render: (v: number, r: any) => withDegraded(v != null ? <span style={{ color: v >= 90 ? '#00A793' : v >= 70 ? '#C4963C' : '#FF3E4A' }}>{v.toFixed(1)}%</span> : <span style={{ color: '#A5B1C5' }}>-</span>, r.degraded, 'journalWriteRate') },
        { key: 'avgPricePerClass', label: '회당단가 (PT)', align: 'right', render: (v: number | null, r: any) => missingOr(v, r.degraded, 'avgPricePerClass', () => v ? <span title={`${(v).toLocaleString('ko-KR')}원`} style={{ cursor: 'default' }}>{(v / 10000).toFixed(1)}만원</span> : '-') },
        { key: 'sameDayCancelCount', label: '당일취소', align: 'right', render: (v: number | null, r: any) => missingOr(v, r.degraded, 'sameDayCancelCount', () => <span style={{ color: (v ?? 0) >= 5 ? '#FF3E4A' : (v ?? 0) > 0 ? '#1A305D' : '#00A793' }}>{v || 0}건</span>) },
        { key: 'sameDayChangeCount', label: '당일변경', align: 'right', render: (v: number | null, r: any) => missingOr(v, r.degraded, 'sameDayChangeCount', () => <span style={{ color: (v ?? 0) >= 5 ? '#FF3E4A' : (v ?? 0) > 0 ? '#1A305D' : '#00A793' }}>{v || 0}건</span>) },
    ];
    const trainerColumns = [
        { key: 'name', label: '강사명', render: (v: string) => <span style={{ fontWeight: 600, color: T.navy }}>{v}</span> },
        { key: 'center', label: '소속' },
        { key: 'revenue', label: `${periodLabel}매출`, align: 'right', render: (v: number) => fmt(v || 0) },
        { key: 'revGrowth', label: '전기대비', align: 'right', sortValue: (r: any) => pctChange(r.revenue, r.prevRevenue), render: (_: any, r: any) => <Badge value={pctChange(r.revenue, r.prevRevenue)} /> },
        { key: 'totalMembers', label: '담당회원', align: 'right', render: (v: number | null, r: any) => missingOr(v, r.degraded, 'totalMembers', () => v) },
        { key: 'totalClasses', label: '진행한 수업수', align: 'right', render: (v: number | null, r: any) => missingOr(v, r.degraded, 'totalClasses', () => v) },
        {
            key: 'journalCount',
            label: '일지',
            align: 'right',
            render: (v: number | null, r: any) => missingOr(v, r.degraded, 'journalCount', () => <span style={{ color: (v||0) >= 280 ? T.success : (v||0) >= 220 ? T.gold : T.error }}>{v || 0}건</span>),
        },
        { key: 'journalWriteRate', label: '일지율', align: 'right', render: (v: number, r: any) => withDegraded(v != null ? <span style={{ color: v >= 90 ? '#00A793' : v >= 70 ? '#C4963C' : '#FF3E4A' }}>{v.toFixed(1)}%</span> : <span style={{ color: '#A5B1C5' }}>-</span>, r.degraded, 'journalWriteRate') },
        { key: 'avgPricePerClass', label: '회당단가 (PT)', align: 'right', render: (v: number | null, r: any) => missingOr(v, r.degraded, 'avgPricePerClass', () => v ? <span title={`${(v).toLocaleString('ko-KR')}원`} style={{ cursor: 'default' }}>{(v / 10000).toFixed(1)}만원</span> : '-') },
        { key: 'sameDayCancelCount', label: '당일취소', align: 'right', render: (v: number | null, r: any) => missingOr(v, r.degraded, 'sameDayCancelCount', () => <span style={{ color: (v ?? 0) >= 5 ? '#FF3E4A' : (v ?? 0) > 0 ? '#1A305D' : '#00A793' }}>{v || 0}건</span>) },
        { key: 'sameDayChangeCount', label: '당일변경', align: 'right', render: (v: number | null, r: any) => missingOr(v, r.degraded, 'sameDayChangeCount', () => <span style={{ color: (v ?? 0) >= 5 ? '#FF3E4A' : (v ?? 0) > 0 ? '#1A305D' : '#00A793' }}>{v || 0}건</span>) },
        {
            key: 'reviewAvg',
            label: '리뷰평점',
            align: 'right',
            render: (v: number) => <span style={{ color: v >= 4.5 ? T.success : v >= 4.0 ? T.gold : T.error }}>★ {v}</span>,
        },
    ];

    // URL에서 진입 시 selectedCenter 복원
    useEffect(() => {
        if (screen === 'centerDetail' && !selectedCenter && urlCenterId && activeCenters.length > 0) {
            const found = activeCenters.find((c: any) => c.centerId === urlCenterId || String(c.id) === urlCenterId);
            if (found) setSelectedCenter(found);
        }
    }, [screen, urlCenterId, activeCenters, selectedCenter]);

    // 프로필 드롭다운 바깥 클릭 시 닫기
    useEffect(() => {
        if (!profileDropdownOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target as Node)) {
                setProfileDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [profileDropdownOpen]);

    return (
        <div style={{ minHeight: '100vh', background: T.bg, color: T.text1, fontFamily: "'Pretendard','Apple SD Gothic Neo',-apple-system,sans-serif" }}>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
            `}</style>
            {passwordModalVisible && (
                <ModalSuperAdminPasswordChange
                    onClose={() => setPasswordModalVisible(false)}
                    authorization={getItemWithExpiration('authorization') || ''}
                    superAdminId={superAdminId}
                />
            )}
            {/* Header */}
            <header
                style={{
                    padding: '14px 20px',
                    borderBottom: `1px solid ${T.headerBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    background: T.headerBg,
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    boxShadow: '0 1px 3px rgba(26,48,93,0.04)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <div
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: 9,
                            background: `linear-gradient(135deg,${T.navy},${T.blue})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <svg viewBox="0 0 100 60" width="22" height="14" fill="none">
                            <rect x="7" y="4" rx="26" ry="26" width="86" height="52" stroke="#fff" strokeWidth="5.5" fill="none" />
                            <circle cx="26" cy="42" r="11" fill="#fff" />
                            <circle cx="74" cy="18" r="11" fill="#fff" />
                        </svg>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: -0.5, color: T.navy, whiteSpace: 'nowrap' }}>Rappo 슈퍼 관리자</div>
                </div>
                <div style={{ display: 'flex', gap: 2, background: T.inputBg, borderRadius: 10, padding: 3, border: `1px solid ${T.border}`, flexShrink: 0 }}>
                        {[
                            { key: 'overview', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="8" width="3" height="6" rx="0.5" fill="currentColor" opacity="0.6"/><rect x="6" y="4" width="3" height="10" rx="0.5" fill="currentColor" opacity="0.8"/><rect x="11" y="1" width="3" height="13" rx="0.5" fill="currentColor"/></svg>, label: '총괄 대시보드' },
                            { key: 'centerPicker', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 2h10a1 1 0 011 1v11H2V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" fill="none"/><rect x="5" y="5" width="2" height="2" rx="0.3" fill="currentColor"/><rect x="9" y="5" width="2" height="2" rx="0.3" fill="currentColor"/><rect x="5" y="9" width="2" height="2" rx="0.3" fill="currentColor"/><rect x="9" y="9" width="2" height="2" rx="0.3" fill="currentColor"/></svg>, label: screen === 'centerDetail' ? '센터 지표' : screen === 'centerPicker' ? '센터 목록' : '센터 지표' },
                            { key: 'detail', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="2" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/><line x1="1" y1="6" x2="15" y2="6" stroke="currentColor" strokeWidth="1"/><line x1="6" y1="6" x2="6" y2="14" stroke="currentColor" strokeWidth="1"/></svg>, label: '세부 지표' },
                            { key: 'refunds', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 2h8a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5"/><path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>, label: '환불 분석' },
                            { key: 'opportunities', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" fill="none"/><circle cx="8" cy="8" r="2.2" fill="currentColor"/><path d="M8 1.5V3M8 13v1.5M14.5 8H13M3 8H1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>, label: '신규기회 클로징' },
                            { key: 'teams', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="5" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.4" fill="none"/><circle cx="11" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.4" fill="none"/><path d="M1.5 13c.4-1.8 1.9-3 3.5-3s3.1 1.2 3.5 3M7.5 13c.4-1.8 1.9-3 3.5-3s3.1 1.2 3.5 3" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round"/></svg>, label: 'PT팀 랭킹' },
                        ].map((t) => {
                        const isActive = screen === t.key || (t.key === 'centerPicker' && screen === 'centerDetail');
                        return (
                        <button
                            key={t.key}
                            onClick={() => setScreen(t.key)}
                            style={{
                                padding: '9px 14px',
                                borderRadius: 8,
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 600,
                                transition: 'all 0.2s',
                                background: isActive ? T.blue : 'transparent',
                                color: isActive ? '#fff' : T.text3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            {t.icon}
                            {t.label}
                        </button>
                        );
                    })}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                    {/* 그룹수업 매출 ON/OFF */}
                    <div
                        onClick={handleIncludeGroupToggle}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '6px 14px', borderRadius: 10,
                            backgroundColor: includeGroup ? '#065F46' : T.inputBg,
                            border: `1px solid ${includeGroup ? '#10B981' : T.border}`,
                            cursor: 'pointer', transition: 'all 0.2s', userSelect: 'none',
                        }}
                    >
                        <div style={{
                            width: 32, height: 17, borderRadius: 9,
                            backgroundColor: includeGroup ? '#10B981' : '#D1D5DB',
                            position: 'relative', transition: 'background-color 0.2s', flexShrink: 0,
                        }}>
                            <div style={{
                                width: 13, height: 13, borderRadius: '50%', backgroundColor: '#fff',
                                position: 'absolute', top: 2,
                                left: includeGroup ? 17 : 2, transition: 'left 0.2s',
                            }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: includeGroup ? '#fff' : T.text2, whiteSpace: 'nowrap' }}>
                            그룹수업 {includeGroup ? 'ON' : 'OFF'}
                        </span>
                    </div>
                    {/* 프로필 드롭다운 */}
                    <div ref={profileDropdownRef} style={{ position: 'relative' }}>
                        <button
                            onClick={() => setProfileDropdownOpen(v => !v)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '6px 12px', borderRadius: 8,
                                border: `1px solid ${profileDropdownOpen ? T.blue : T.border}`,
                                background: profileDropdownOpen ? '#ECF1FC' : T.card,
                                color: T.text2, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="5" r="3" stroke={T.text2} strokeWidth="1.5"/>
                                <path d="M2 13c0-2.2 2.7-4 6-4s6 1.8 6 4" stroke={T.text2} strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{authObject?.brandName || authObject?.name || ''}</span>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                                style={{ transform: profileDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                                <path d="M2 4l4 4 4-4" stroke={T.text3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>

                        {profileDropdownOpen && (
                            <div style={{
                                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                                background: T.card, borderRadius: 10, border: `1px solid ${T.border}`,
                                boxShadow: '0 4px 16px rgba(26,48,93,0.1)', minWidth: 180, zIndex: 200,
                                overflow: 'hidden',
                            }}>
                                <button
                                    onClick={() => { setProfileDropdownOpen(false); setPasswordModalVisible(true); }}
                                    style={{
                                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '12px 16px', background: 'none', border: 'none',
                                        color: T.navy, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                        textAlign: 'left',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.background = T.borderLight || '#F0F3F7')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                >
                                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                                        <rect x="1" y="7" width="13" height="7" rx="1.5" stroke={T.navy} strokeWidth="1.4"/>
                                        <path d="M4 7V5a3.5 3.5 0 017 0v2" stroke={T.navy} strokeWidth="1.4" strokeLinecap="round"/>
                                    </svg>
                                    비밀번호 변경
                                </button>

                                <div style={{ height: 1, background: T.border, margin: '0 10px' }} />

                                <button
                                    onClick={() => { setProfileDropdownOpen(false); window.open('http://pf.kakao.com/_ICxhcb/chat', '_blank'); }}
                                    style={{
                                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '12px 16px', background: 'none', border: 'none',
                                        color: T.navy, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                        textAlign: 'left',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.background = T.borderLight || '#F0F3F7')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                >
                                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                                        <path d="M7.5 1.5C4.186 1.5 1.5 3.686 1.5 6.4c0 1.72 1.02 3.23 2.57 4.15L3.4 13l2.58-1.4c.48.1.99.15 1.52.15 3.314 0 6-2.186 6-4.9S10.814 1.5 7.5 1.5z" stroke={T.navy} strokeWidth="1.4" strokeLinejoin="round"/>
                                    </svg>
                                    라포에 문의하기
                                </button>

                                <div style={{ height: 1, background: T.border, margin: '0 10px' }} />

                                <button
                                    onClick={() => {
                                        setProfileDropdownOpen(false);
                                        Object.keys(localStorage).filter(k => k.startsWith('sa_')).forEach(k => localStorage.removeItem(k));
                                        clearLocalStorage();
                                        window.location.href = '/signIn';
                                    }}
                                    style={{
                                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '12px 16px', background: 'none', border: 'none',
                                        color: T.error, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                        textAlign: 'left',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.background = T.errorBg || '#FFE8E9')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                >
                                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                                        <path d="M5.5 7.5H13m0 0l-2.5-2.5M13 7.5l-2.5 2.5" stroke={T.error} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M9 4.5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v9a1 1 0 001 1h5a1 1 0 001-1v-1.5" stroke={T.error} strokeWidth="1.4" strokeLinecap="round"/>
                                    </svg>
                                    로그아웃
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main style={{ padding: '24px 28px 120px', maxWidth: 1400, margin: '0 auto', zoom: 1.05 }}>
                {screen === 'teams' && <SuperAdminTeamAnalyticsPage includeGroup={includeGroup} />}
                {screen === 'overview' && (
                    <>



                        {/* Error state */}
                        {!apiData && !isOverviewLoading && (
                            <div style={{ textAlign: 'center', padding: '60px 0', color: '#5E7093' }}>
                                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>데이터를 불러올 수 없습니다</div>
                                <div style={{ fontSize: 13 }}>잠시 후 다시 시도해주세요</div>
                            </div>
                        )}

                        {/* KPI Cards */}
                        {!apiData && isOverviewLoading ? <SkeletonKpiCards /> : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
                            {kpis.map((k, i) => (
                                <div
                                    key={i}
                                    style={{
                                        background: T.card,
                                        borderRadius: 14,
                                        padding: '20px 22px',
                                        border: k.isDebt ? `1px solid ${T.error}20` : `1px solid ${T.cardBorder}`,
                                        boxShadow: T.cardShadow,
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: 4,
                                            height: '100%',
                                            background: k.color,
                                            borderRadius: '14px 0 0 14px',
                                        }}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                        <span style={{ fontSize: 15, color: T.text2, fontWeight: 600 }}>{k.label}</span>
                                        {k.sub && (
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    color: T.error,
                                                    fontWeight: 600,
                                                    background: T.errorBg,
                                                    padding: '1px 6px',
                                                    borderRadius: 4,
                                                }}
                                            >
                                                {k.sub}
                                            </span>
                                        )}
                                        {k.tooltip && (
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    color: T.text4,
                                                    fontWeight: 700,
                                                    width: 18, height: 18,
                                                    borderRadius: '50%',
                                                    border: `1px solid ${T.border}`,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'help',
                                                    position: 'relative',
                                                }}
                                                onMouseEnter={(e) => {
                                                    const tip = e.currentTarget.querySelector('[data-tip]') as HTMLElement;
                                                    if (tip) tip.style.display = 'block';
                                                }}
                                                onMouseLeave={(e) => {
                                                    const tip = e.currentTarget.querySelector('[data-tip]') as HTMLElement;
                                                    if (tip) tip.style.display = 'none';
                                                }}
                                            >
                                                ?
                                                <div
                                                    data-tip=""
                                                    style={{
                                                        display: 'none',
                                                        position: 'absolute',
                                                        top: 'calc(100% + 8px)',
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        background: T.navy,
                                                        color: '#fff',
                                                        fontSize: 13,
                                                        fontWeight: 500,
                                                        padding: '10px 14px',
                                                        borderRadius: 8,
                                                        whiteSpace: 'pre-line',
                                                        width: 260,
                                                        lineHeight: 1.5,
                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                                                        zIndex: 100,
                                                        pointerEvents: 'none',
                                                    }}
                                                >
                                                    {k.tooltip}
                                                </div>
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 26,
                                            fontWeight: 800,
                                            color: k.isDebt ? T.error : T.navy,
                                            letterSpacing: -1,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {k.value}
                                    </div>
                                    {!k.isDebt && (
                                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Badge value={k.change ?? 0} />
                                            <span style={{ fontSize: 13, color: T.text4 }}>{k.changeSub}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        )}

                        <div
                            onClick={() => setScreen('opportunities')}
                            style={{
                                background: T.card,
                                border: `1px solid ${T.cardBorder}`,
                                borderRadius: 14,
                                padding: '22px 24px',
                                marginBottom: 24,
                                cursor: 'pointer',
                                boxShadow: T.cardShadow,
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = T.cardShadowHover; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = T.cardShadow; }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontSize: 13, color: T.text3, marginBottom: 4 }}>{opportunityMonthScopeLabel} 신규 기회 클로징</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
                                        <span style={{ fontSize: 24, fontWeight: 800, color: T.navy }}>
                                            {isNewOpportunitySummaryError
                                                ? '조회 실패'
                                                : !newOpportunitySummaryData
                                                ? '조회 중'
                                                : formatOpportunityRate(
                                                      newOpportunitySummary.closingRate,
                                                      newOpportunitySummary.rateUnit
                                                  )}
                                        </span>
                                        <span style={{ fontSize: 13, color: T.text2 }}>
                                            {isNewOpportunitySummaryError ? (
                                                <button
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        refetchNewOpportunitySummary();
                                                    }}
                                                    disabled={isNewOpportunitySummaryFetching}
                                                    style={{
                                                        minHeight: 32,
                                                        border: '1px solid #FCA5A5',
                                                        borderRadius: 8,
                                                        background: '#fff',
                                                        color: '#B91C1C',
                                                        padding: '0 10px',
                                                        fontWeight: 850,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    다시 시도
                                                </button>
                                            ) : newOpportunitySummaryData ? (
                                                <>
                                                    클로징 {fmt(newOpportunitySummary.closedMemberCount || 0)}명 / 신규 {fmt(newOpportunitySummary.leadMemberCount || 0)}명
                                                </>
                                            ) : (
                                                '신규기회 분석을 불러오는 중입니다.'
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <span style={{ color: T.blue, fontSize: 14, fontWeight: 700 }}>강사별 자세히 보기 →</span>
                            </div>
                        </div>

                        {/* Revenue Chart */}
                        {!trendData && isTrendFetching ? <SkeletonChart /> : (
                        <div style={{ background: T.card, borderRadius: 14, padding: '24px 28px', border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow, marginBottom: 28, position: 'relative', overflow: 'hidden' }}>
                            {/* 상단 컬러 바 */}
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: T.gold }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                                        <path d="M3 18L8 11L12 14L19 4" stroke={T.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <circle cx="19" cy="4" r="2" fill={T.blue}/>
                                    </svg>
                                    <span style={{ fontSize: 18, fontWeight: 700, color: T.navy }}>매출 추이</span>
                                    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 6 }} className="sa-chart-tooltip-wrap">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ cursor: 'pointer', color: T.text3 }}>
                                            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
                                            <text x="8" y="12" textAnchor="middle" fontSize="10" fill="currentColor" fontWeight="700">i</text>
                                        </svg>
                                        <div className="sa-chart-tooltip-box" style={{
                                            display: 'none', position: 'absolute', top: 24, left: '50%', transform: 'translateX(-50%)',
                                            background: '#1A305D', color: '#fff', fontSize: 12, fontWeight: 400,
                                            padding: '8px 12px', borderRadius: 8, whiteSpace: 'nowrap', zIndex: 100,
                                            boxShadow: '0 4px 12px rgba(26,48,93,0.2)', lineHeight: 1.6,
                                        }}>
                                            미리 등록된 매출이 포함될 수 있습니다.
                                            <div style={{ position: 'absolute', top: -5, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: '5px solid #1A305D' }} />
                                        </div>
                                        <style>{`.sa-chart-tooltip-wrap:hover .sa-chart-tooltip-box { display: block !important; }`}</style>
                                    </div>
                                </div>
                                <PeriodToggle value={chartPeriod} onChange={setChartPeriodStable} loading={isTrendFetching} baseDate={chartBaseDate} onBaseDateChange={setChartBaseDateStable} />
                            </div>
                            <MultiLineChart centers={activeCenters} visibleCenters={isAllCentersVisible ? new Set(activeCenters.map((c: any) => c.id)) : visibleCenters} showTotal={showTotal} trendData={trendData} failed={isTrendError && !isTrendFetching} onRetry={() => refetchTrend()} />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14, alignItems: 'center' }}>
                                <button
                                    onClick={() => setShowTotal((t) => !t)}
                                    style={{
                                        padding: '5px 14px',
                                        borderRadius: 6,
                                        border: showTotal ? `1px solid ${T.navy}` : `1px solid ${T.border}`,
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        fontWeight: 700,
                                        background: showTotal ? T.navy : 'transparent',
                                        color: showTotal ? '#fff' : T.text3,
                                    }}
                                >
                                    전체 합산
                                </button>
                                <div style={{ width: 1, height: 18, background: T.border, margin: '0 6px' }} />
                                <button
                                    onClick={toggleAll}
                                    style={{
                                        padding: '5px 12px',
                                        borderRadius: 6,
                                        border: `1px solid ${T.border}`,
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        fontWeight: 500,
                                        background: 'transparent',
                                        color: T.text3,
                                    }}
                                >
                                    {isAllCentersVisible ? '전체 해제' : '전체 선택'}
                                </button>
                                {activeCenters.map((c: any, i: number) => {
                                    const visible = isCenterVisible(c.id);
                                    const cl = COLORS[i % COLORS.length];
                                    return (
                                        <button
                                            key={c.id}
                                            onClick={() => toggleCenter(c.id)}
                                            style={{
                                                padding: '5px 12px',
                                                borderRadius: 6,
                                                border: `1px solid ${visible ? cl : T.border}`,
                                                cursor: 'pointer',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                background: visible ? `${cl}14` : 'transparent',
                                                color: visible ? cl : T.text4,
                                            }}
                                        >
                                            {c.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        )}

                        {/* Center Ranking */}
                        {!apiData && isOverviewLoading ? <SkeletonRanking /> : (
                        <RankingSection
                            title="지점 랭킹"
                            data={activeCenters}
                            nameKey="name"
                            categories={CENTER_RANK_CATEGORIES}
                            rankingPeriod={centerRankPeriod}
                            onRankingPeriodChange={setCenterRankPeriodStable}
                            loading={isOverviewFetching}
                            baseDate={centerRankBase}
                            onBaseDateChange={setCenterRankBaseStable}
                            selectedWeek={centerRankWeek}
                            onWeekChange={setCenterRankWeek}
                            selectedDay={centerRankDay}
                            onDayChange={setCenterRankDay}
                            colorFn={(item: any) => COLORS[activeCenters.findIndex((x: any) => x.id === item.id) % COLORS.length]}
                            onNameClick={(center) => {
                                setSelectedCenter(center);
                                setPrevScreen('overview');
                                setScreen('centerDetail', center.centerId || center.id);
                            }}
                            onReRegistrationSuccessEvidence={(center: any) => {
                                if (!center?.centerId) return;
                                setReRegEvidenceTarget({
                                    kind: 'center',
                                    centerId: String(center.centerId),
                                    centerName: center.name,
                                    cohortMonth: center.reRegistrationSuccessCohortMonth,
                                    rate: center.reRegistrationSuccessRate,
                                    succeededMemberCount: center.reRegistrationSucceededMemberCount,
                                    endedMemberCount: center.reRegistrationEndedMemberCount,
                                    notYetMemberCount: center.reRegistrationNotYetMemberCount,
                                    dataQuality: center.reRegistrationSuccessDataQuality,
                                });
                            }}
                        />
                        )}

                        {/* Trainer Ranking */}
                        {!apiData && isOverviewLoading ? <SkeletonRanking /> : (
                        <RankingSection
                            title="강사 랭킹"
                            data={filteredTrainers}
                            nameKey="name"
                            subKey={showTrainerSub ? 'center' : undefined}
                            categories={TRAINER_RANK_CATEGORIES}
                            rankingPeriod={trainerRankPeriod}
                            onRankingPeriodChange={setTrainerRankPeriodStable}
                            loading={isTrainerFetching}
                            baseDate={trainerRankBase}
                            onBaseDateChange={setTrainerRankBaseStable}
                            selectedWeek={trainerRankWeek}
                            onWeekChange={setTrainerRankWeek}
                            selectedDay={trainerRankDay}
                            onDayChange={setTrainerRankDay}
                            filterComponent={<CenterMultiSelect centers={activeCenters} selected={trainerCenterFilter} onChange={setTrainerCenterFilter} />}
                            onReRegistrationSuccessEvidence={(trainer: any) => {
                                if (!trainer?.coachId || !trainer.reRegistrationSuccessCenters?.length) return;
                                setReRegEvidenceTarget({
                                    kind: 'coach',
                                    coachId: String(trainer.coachId),
                                    coachName: trainer.name,
                                    cohortMonth: trainer.reRegistrationSuccessCohortMonth,
                                    centers: trainer.reRegistrationSuccessCenters,
                                    dataQuality: trainer.reRegistrationSuccessDataQuality,
                                });
                            }}
                        />
                        )}
                        {reRegEvidenceTarget && (
                            <SuperAdminReRegistrationEvidenceModal
                                superAdminId={String(superAdminId)}
                                target={reRegEvidenceTarget}
                                onClose={() => setReRegEvidenceTarget(null)}
                            />
                        )}
                    </>
                )}

                {screen === 'opportunities' && (
                        <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, marginBottom: 24 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                                            <circle cx="11" cy="11" r="8" stroke={T.blue} strokeWidth="2" />
                                            <circle cx="11" cy="11" r="3" fill={T.blue} />
                                            <path d="M11 3V1.5M11 20.5V19M19 11H20.5M1.5 11H3" stroke={T.blue} strokeWidth="2" strokeLinecap="round" />
                                        </svg>
                                        <span style={{ fontSize: 18, fontWeight: 700, color: T.navy }}>신규기회 클로징</span>
                                    </div>
                                    <div style={{ fontSize: 13, color: T.text3, marginTop: 4 }}>선택 월 기준, 실제 PT/그룹 결제 기반 자동 집계</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {!isNewOpportunityCurrentMonth && (
                                        <button
                                            type="button"
                                            onClick={() => setNewOpportunityBaseDateStable(new Date())}
                                            style={{ ...navBtnStyle, fontSize: 13, padding: '3px 9px', color: T.blue, borderColor: T.blueBorder }}
                                        >
                                            이번달
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        style={navBtnStyle}
                                        onClick={() => setNewOpportunityBaseDateStable(shiftBaseDate(newOpportunityBaseDate, 'monthly', -1))}
                                    >
                                        ◀
                                    </button>
                                    <div style={{ minWidth: 92, textAlign: 'center', fontSize: 13, color: isNewOpportunityCurrentMonth ? T.text3 : T.blue, fontWeight: 800 }}>
                                        {newOpportunityBaseDate.getFullYear()}년 {newOpportunityBaseDate.getMonth() + 1}월
                                    </div>
                                    <button
                                        type="button"
                                        style={navBtnStyle}
                                        onClick={() => setNewOpportunityBaseDateStable(shiftBaseDate(newOpportunityBaseDate, 'monthly', 1))}
                                    >
                                        ▶
                                    </button>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 0.9fr) minmax(190px, 1fr) minmax(280px, 1.35fr) auto', gap: 10, alignItems: 'center', padding: 14, marginBottom: 14, border: `1px solid ${T.borderLight}`, borderRadius: 12, background: T.inputBg }}>
                                <select
                                    value={newOpportunityCenterId}
                                    onChange={(event) => {
                                        setNewOpportunityCenterId(event.target.value);
                                        setNewOpportunityCoachId('');
                                    }}
                                    style={{ height: 38, border: `1px solid ${T.border}`, borderRadius: 9, background: '#fff', color: T.navy, padding: '0 10px', fontSize: 13, fontWeight: 800, outline: 'none', minWidth: 0 }}
                                >
                                    <option value="">전체 센터</option>
                                    {newOpportunityCenterOptions.map((center: any) => (
                                        <option key={center.centerId} value={center.centerId}>
                                            {center.centerName}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={newOpportunityCoachId}
                                    onChange={(event) => setNewOpportunityCoachId(event.target.value)}
                                    disabled={newOpportunityCoachOptions.length === 0}
                                    style={{ height: 38, border: `1px solid ${T.border}`, borderRadius: 9, background: '#fff', color: T.navy, padding: '0 10px', fontSize: 13, fontWeight: 800, outline: 'none', minWidth: 0, opacity: newOpportunityCoachOptions.length === 0 ? 0.55 : 1 }}
                                >
                                    <option value="">전체 강사</option>
                                    {newOpportunityCoachOptions.map((coach: any) => (
                                        <option key={`${coach.centerId}-${coach.coachId || coach.coachName}`} value={coach.coachId}>
                                            {newOpportunityCenterId ? coach.coachName : `${coach.centerName} · ${coach.coachName}`}
                                        </option>
                                    ))}
                                </select>
                                <div style={{ height: 38, display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${T.border}`, borderRadius: 9, background: '#fff', padding: '0 10px', minWidth: 0 }}>
                                    <span style={{ color: T.text4, fontSize: 13 }}>검색</span>
                                    <input
                                        value={newOpportunitySearchInput}
                                        placeholder="센터명 또는 강사명"
                                        onChange={(event) => setNewOpportunitySearchInput(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') applyNewOpportunitySearch();
                                        }}
                                        style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', color: T.navy, fontSize: 13, fontWeight: 800, background: 'transparent' }}
                                    />
                                    {(newOpportunitySearchInput || newOpportunitySearchKeyword) && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setNewOpportunitySearchInput('');
                                                setNewOpportunitySearchKeyword('');
                                            }}
                                            style={{ width: 22, height: 22, border: 0, borderRadius: 999, background: T.borderLight, color: T.text3, cursor: 'pointer', fontSize: 12, fontWeight: 900 }}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                                    <button
                                        type="button"
                                        onClick={applyNewOpportunitySearch}
                                        style={{ height: 38, padding: '0 14px', border: 0, borderRadius: 9, background: T.navy, color: '#fff', fontSize: 13, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        검색
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!hasNewOpportunityFilter}
                                        onClick={resetNewOpportunityFilters}
                                        style={{ height: 38, padding: '0 12px', border: `1px solid ${T.border}`, borderRadius: 9, background: '#fff', color: T.text2, fontSize: 13, fontWeight: 900, cursor: hasNewOpportunityFilter ? 'pointer' : 'default', opacity: hasNewOpportunityFilter ? 1 : 0.45, whiteSpace: 'nowrap' }}
                                    >
                                        초기화
                                    </button>
                                </div>
                                {(selectedNewOpportunityCenterName || selectedNewOpportunityCoachName || newOpportunitySearchKeyword) && (
                                    <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 2 }}>
                                        <span style={{ color: T.text3, fontSize: 12, fontWeight: 800 }}>적용 중</span>
                                        {selectedNewOpportunityCenterName && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 9px', borderRadius: 999, background: T.blueBg, color: T.blue, fontSize: 12, fontWeight: 900 }}>
                                                센터: {selectedNewOpportunityCenterName}
                                            </span>
                                        )}
                                        {selectedNewOpportunityCoachName && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 9px', borderRadius: 999, background: T.successBg, color: T.success, fontSize: 12, fontWeight: 900 }}>
                                                강사: {selectedNewOpportunityCoachName}
                                            </span>
                                        )}
                                        {newOpportunitySearchKeyword && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 9px', borderRadius: 999, background: '#fff', border: `1px solid ${T.border}`, color: T.text2, fontSize: 12, fontWeight: 900 }}>
                                                검색: {newOpportunitySearchKeyword}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            {isNewOpportunityFilterOptionsError && (
                                <div
                                    role="alert"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        border: '1px solid #FCD34D',
                                        borderRadius: 10,
                                        background: '#FFFBEB',
                                        color: '#92400E',
                                        padding: '10px 13px',
                                        marginBottom: 14,
                                        fontSize: 13,
                                        fontWeight: 800,
                                    }}
                                >
                                    <span>
                                        센터·강사 필터 선택지를 일부 불러오지 못했습니다.
                                        기존 분석 수치는 유지합니다.
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            refetchNewOpportunityFilterOptions()
                                        }
                                        disabled={
                                            isNewOpportunityFilterOptionsFetching
                                        }
                                        style={{
                                            minHeight: 34,
                                            border: '1px solid #F59E0B',
                                            borderRadius: 8,
                                            background: '#fff',
                                            color: '#92400E',
                                            padding: '0 12px',
                                            fontWeight: 900,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        다시 시도
                                    </button>
                                </div>
                            )}
                            {isNewOpportunitySummaryError ? (
                                <div
                                    role="alert"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 16,
                                        minHeight: 72,
                                        border: '1px solid #FECACA',
                                        borderRadius: 12,
                                        background: '#FEF2F2',
                                        color: '#B91C1C',
                                        padding: '14px 16px',
                                        fontSize: 13,
                                        fontWeight: 800,
                                    }}
                                >
                                    <span>
                                        신규기회 분석을 불러오지 못했습니다. 오류를 0건으로
                                        표시하지 않습니다.
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => refetchNewOpportunitySummary()}
                                        disabled={isNewOpportunitySummaryFetching}
                                        style={{
                                            minHeight: 36,
                                            border: '1px solid #FCA5A5',
                                            borderRadius: 8,
                                            background: '#fff',
                                            color: '#B91C1C',
                                            padding: '0 13px',
                                            fontWeight: 900,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {isNewOpportunitySummaryFetching
                                            ? '다시 불러오는 중'
                                            : '다시 시도'}
                                    </button>
                                </div>
                            ) : isNewOpportunitySummaryFetching &&
                              !newOpportunitySummaryData ? (
                                <div
                                    role="status"
                                    style={{
                                        minHeight: 72,
                                        border: `1px solid ${T.borderLight}`,
                                        borderRadius: 12,
                                        background: T.inputBg,
                                        color: T.text3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 13,
                                        fontWeight: 800,
                                    }}
                                >
                                    신규기회 분석을 불러오는 중입니다.
                                </div>
                            ) : newOpportunitySummaryData ? (
                                <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
                                {[
                                    ['신규기회 회원', `${fmt(newOpportunitySummary.leadMemberCount || 0)}명`],
                                    ['클로징 회원', `${fmt(newOpportunitySummary.closedMemberCount || 0)}명`],
                                    ['클로징률', formatOpportunityRate(newOpportunitySummary.closingRate, newOpportunitySummary.rateUnit)],
                                    ['총 기회 수', `${fmt(newOpportunitySummary.opportunityCount || 0)}건`],
                                    ['회원당 평균', `${newOpportunitySummary.avgOpportunitiesPerMember || 0}건`],
                                    [
                                        '신규기회 일지 작성률',
                                        newOpportunitySummary.journalWriteTarget
                                            ? `${formatOpportunityRate(newOpportunitySummary.journalWriteRate, newOpportunitySummary.rateUnit)} (${fmt(newOpportunitySummary.journalWriteWritten || 0)}/${fmt(newOpportunitySummary.journalWriteTarget)})`
                                            : '- (대상 없음)',
                                    ],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ border: `1px solid ${T.borderLight}`, borderRadius: 10, padding: '14px 16px', background: T.inputBg }}>
                                        <div style={{ fontSize: 12, color: T.text3, marginBottom: 6 }}>{label}</div>
                                        <div style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>{value}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ border: `1px solid ${T.borderLight}`, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '13px 16px', background: T.inputBg, borderBottom: `1px solid ${T.borderLight}` }}>
                                    <div style={{ color: T.navy, fontSize: 14, fontWeight: 900 }}>강사별 클로징 테이블</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        {[
                                            ['집계 강사', `${fmt(newOpportunityTopCoaches.length)}명`],
                                            [
                                                '표시',
                                                sortedNewOpportunityCoaches.length > 0
                                                    ? `${fmt(newOpportunityCoachStartIndex)}-${fmt(newOpportunityCoachEndIndex)}명`
                                                    : '0명',
                                            ],
                                            ['전환 회원', `${fmt(newOpportunitySummary.closedMemberCount || 0)}명`],
                                            ...(newOpportunitySearchKeyword ? [['검색 결과', `${fmt(sortedNewOpportunityCoaches.length)}명`]] : []),
                                        ].map(([label, value]) => (
                                            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 9px', borderRadius: 999, background: '#fff', border: `1px solid ${T.borderLight}`, color: T.text3, fontSize: 12, fontWeight: 800 }}>
                                                {label}
                                                <b style={{ color: T.navy, fontWeight: 900 }}>{value}</b>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(210px, 1.15fr) minmax(150px, 0.85fr) minmax(100px, 0.52fr) minmax(100px, 0.52fr) minmax(100px, 0.52fr) minmax(220px, 1.2fr)',
                                        gap: 12,
                                        padding: '9px 16px',
                                        background: '#FBFCFE',
                                        color: T.text3,
                                        fontSize: 12,
                                        fontWeight: 800,
                                    }}
                                >
                                    {newOpportunitySortColumns.map((column) => {
                                        const isActiveSort = newOpportunityCoachSort.key === column.key;
                                        return (
                                            <button
                                                key={column.key}
                                                type="button"
                                                onClick={() => handleNewOpportunityCoachSort(column.key)}
                                                style={{
                                                    border: 0,
                                                    background: 'transparent',
                                                    padding: 0,
                                                    textAlign: 'left',
                                                    color: isActiveSort ? T.blue : T.text3,
                                                    fontSize: 12,
                                                    fontWeight: 900,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {column.label}
                                                <span style={{ marginLeft: 5, color: isActiveSort ? T.blue : T.text4 }}>
                                                    {isActiveSort ? (newOpportunityCoachSort.direction === 'asc' ? '▲' : '▼') : '↕'}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {newOpportunityCoachRows.length > 0 ? (
                                    newOpportunityCoachRows.map((coach: any, index: number) => {
                                        const rate =
                                            normalizeOpportunityRatePercent(
                                                coach.closingRate,
                                                coach.rateUnit
                                            ) || 0;
                                        const rateWidth = `${Math.min(Math.max(rate, 0), 100)}%`;
                                        return (
                                            <div
                                                key={`${coach.centerId || ''}-${coach.coachId || coach.coachName || index}`}
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(210px, 1.15fr) minmax(150px, 0.85fr) minmax(100px, 0.52fr) minmax(100px, 0.52fr) minmax(100px, 0.52fr) minmax(220px, 1.2fr)',
                                                    gap: 12,
                                                    alignItems: 'center',
                                                    padding: '13px 16px',
                                                    borderTop: `1px solid ${T.borderLight}`,
                                                    fontSize: 13,
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                    <span
                                                        style={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: 8,
                                                            background: T.blueBg,
                                                            color: T.blue,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontWeight: 900,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {newOpportunityCoachStartIndex + index}
                                                    </span>
                                                    <span style={{ color: T.text2, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {coach.centerName || '센터 정보 없음'}
                                                    </span>
                                                </div>
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ color: T.text2, fontWeight: 700 }}>
                                                        {coach.coachName || '이름 없음'}
                                                    </div>
                                                    <div style={{ color: T.text3, fontSize: 11, fontWeight: 800, marginTop: 3, lineHeight: 1.45 }}>
                                                        일지 {fmt(coach.journalWriteWritten || 0)}/{fmt(coach.journalWriteTarget || 0)}건
                                                        {' · '}
                                                        {coach.journalWriteTarget
                                                            ? formatOpportunityRate(coach.journalWriteRate, coach.rateUnit)
                                                            : '-'}
                                                        {' · 앱 '}
                                                        {coach.appMemberCount
                                                            ? formatOpportunityRate(coach.appLinkedRate, coach.rateUnit)
                                                            : '-'}
                                                        {' · 생성시 미연결→현재 '}
                                                        {coach.appLinkTargetCount
                                                            ? formatOpportunityRate(coach.appLinkedConversionRate, coach.rateUnit)
                                                            : '-'}
                                                    </div>
                                                </div>
                                                <span style={{ color: T.navy, fontWeight: 800 }}>
                                                    {fmt(coach.opportunityCount || 0)}건
                                                </span>
                                                <span style={{ color: T.navy, fontWeight: 800 }}>
                                                    {fmt(coach.leadMemberCount || 0)}명
                                                </span>
                                                <span style={{ color: T.navy, fontWeight: 800 }}>
                                                    {fmt(coach.closedMemberCount || 0)}명
                                                </span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                    <span style={{ width: 52, color: T.navy, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>{rate}%</span>
                                                    <div style={{ flex: 1, height: 8, borderRadius: 999, background: T.borderLight, overflow: 'hidden' }}>
                                                        <div style={{ width: rateWidth, height: '100%', borderRadius: 999, background: T.blue }} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div style={{ padding: '18px 16px', color: T.text3, fontSize: 13, fontWeight: 700, borderTop: `1px solid ${T.borderLight}` }}>
                                        집계할 신규기회가 없습니다.
                                    </div>
                                )}
                                {sortedNewOpportunityCoaches.length > newOpportunityCoachPageSize && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: 14, borderTop: `1px solid ${T.borderLight}`, background: T.inputBg }}>
                                        <span style={{ color: T.text3, fontSize: 13, fontWeight: 800 }}>
                                            {fmt(sortedNewOpportunityCoaches.length)}명 중 {fmt(newOpportunityCoachStartIndex)}-{fmt(newOpportunityCoachEndIndex)}
                                        </span>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                            <button
                                                type="button"
                                                disabled={newOpportunityCoachDisplayPage <= 1}
                                                onClick={() => setNewOpportunityCoachPage((prev) => Math.max(prev - 1, 1))}
                                                style={{ height: 36, padding: '0 14px', borderRadius: 9, border: `1px solid ${T.blueBorder}`, background: '#fff', color: T.blue, fontSize: 13, fontWeight: 900, cursor: newOpportunityCoachDisplayPage <= 1 ? 'default' : 'pointer', opacity: newOpportunityCoachDisplayPage <= 1 ? 0.45 : 1 }}
                                            >
                                                이전
                                            </button>
                                            <span style={{ minWidth: 58, textAlign: 'center', color: T.navy, fontSize: 13, fontWeight: 900 }}>
                                                {fmt(newOpportunityCoachDisplayPage)} / {fmt(newOpportunityCoachPageCount)}
                                            </span>
                                            <button
                                                type="button"
                                                disabled={newOpportunityCoachDisplayPage >= newOpportunityCoachPageCount}
                                                onClick={() => setNewOpportunityCoachPage((prev) => Math.min(prev + 1, newOpportunityCoachPageCount))}
                                                style={{ height: 36, padding: '0 14px', borderRadius: 9, border: `1px solid ${T.blueBorder}`, background: '#fff', color: T.blue, fontSize: 13, fontWeight: 900, cursor: newOpportunityCoachDisplayPage >= newOpportunityCoachPageCount ? 'default' : 'pointer', opacity: newOpportunityCoachDisplayPage >= newOpportunityCoachPageCount ? 0.45 : 1 }}
                                            >
                                                다음
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                                </>
                            ) : null}
                        </div>
                )}

                {screen === 'refunds' && (
                    <RefundAnalysisView
                        summary={refundSummary}
                        centers={refundCenters}
                        trainers={refundTrainers}
                        degraded={readDegraded(refundData)}
                        loading={isRefundLoading || isRefundFetching}
                        period={refundPeriod}
                        onPeriodChange={setRefundPeriodStable}
                        baseDate={refundBaseDate}
                        onBaseDateChange={setRefundBaseDateStable}
                        selectedWeek={refundWeek}
                        onWeekChange={setRefundWeek}
                        selectedDay={refundDay}
                        onDayChange={setRefundDay}
                    />
                )}

                {/* Center Picker Screen */}
                {screen === 'centerPicker' && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ fontSize: 18, fontWeight: 700, color: T.navy }}>센터 선택</div>
                                <span style={{ fontSize: 13, color: T.text3 }}>{pickerCenters.length}개 센터</span>
                                <span style={{ fontSize: 13, color: T.blue, fontWeight: 600, background: T.blueBg, padding: '3px 10px', borderRadius: 6 }}>
                                    매출: {new Date().getFullYear()}년 {new Date().getMonth() + 1}월 기준 · 활성 회원/강사/미수업금액: 실시간
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                            <input
                                type="text"
                                value={centerSearch}
                                onChange={(e) => setCenterSearch(e.target.value)}
                                placeholder="센터 이름 검색"
                                style={{
                                    padding: '7px 14px', borderRadius: 8, border: `1px solid ${T.border}`,
                                    fontSize: 14, color: T.text1, background: T.card, outline: 'none', width: 200,
                                }}
                            />
                            <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                                {([
                                    { key: 'name', label: '이름순' },
                                    { key: 'revenue', label: '매출순' },
                                    { key: 'totalMembers', label: '활성 회원순' },
                                    { key: 'trainers', label: '강사순' },
                                    { key: 'undeliveredRevenue', label: '미수업금액순' },
                                ] as const).map((opt) => (
                                    <button
                                        key={opt.key}
                                        onClick={() => setCenterSort(opt.key)}
                                        style={{
                                            padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                            border: centerSort === opt.key ? `1.5px solid ${T.blue}` : `1px solid ${T.border}`,
                                            background: centerSort === opt.key ? T.blueBg : T.card,
                                            color: centerSort === opt.key ? T.blue : T.text3,
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {isKpiLoading && !pickerCenters.length ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <div key={i} style={{
                                        background: T.card, borderRadius: 12, padding: '18px 20px',
                                        border: `1px solid ${T.cardBorder}`, position: 'relative', overflow: 'hidden',
                                    }}>
                                        <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: T.border, borderRadius: '12px 0 0 12px' }} />
                                        <div style={{ height: 22, width: '60%', background: T.bg, borderRadius: 6, marginBottom: 14, animation: 'pulse 1.5s ease-in-out infinite' }} />
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px 16px' }}>
                                            {[1,2,3,4].map(j => (
                                                <div key={j}>
                                                    <div style={{ height: 13, width: '50%', background: T.bg, borderRadius: 4, marginBottom: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
                                                    <div style={{ height: 18, width: '70%', background: T.bg, borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                            {[...pickerCenters]
                                .filter((c: any) => !centerSearch || c.name.toLowerCase().includes(centerSearch.toLowerCase()))
                                .sort((a: any, b: any) => {
                                    if (centerSort === 'name') return a.name.localeCompare(b.name, 'ko');
                                    // 빈 값(집계 실패)은 0 으로 섞지 않고 맨 뒤로
                                    if (a[centerSort] === null || b[centerSort] === null) {
                                        if (a[centerSort] === b[centerSort]) return 0;
                                        return a[centerSort] === null ? 1 : -1;
                                    }
                                    const aVal = a[centerSort] || 0;
                                    const bVal = b[centerSort] || 0;
                                    return bVal - aVal;
                                })
                                .map((c: any, i: number) => (
                                <div
                                    key={c.id}
                                    onClick={() => { setSelectedCenter(c); setPrevScreen('centerPicker'); setScreen('centerDetail', c.centerId || c.id); }}
                                    style={{
                                        background: T.card, borderRadius: 12, padding: '18px 20px',
                                        border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow,
                                        cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s',
                                        position: 'relative', overflow: 'hidden',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = T.cardShadowHover; e.currentTarget.style.borderColor = COLORS[i % COLORS.length]; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = T.cardShadow; e.currentTarget.style.borderColor = T.cardBorder; }}
                                >
                                    <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: COLORS[i % COLORS.length], borderRadius: '12px 0 0 12px' }} />
                                    <div style={{ fontSize: 18, fontWeight: 700, color: T.navy, marginBottom: 14 }}>{c.name}</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px 16px' }}>
                                        <div>
                                            <div style={{ color: T.text3, marginBottom: 4, fontSize: 13 }}>{new Date().getMonth() + 1}월 매출</div>
                                            <div style={{ color: T.text1, fontWeight: 700, fontSize: 16, fontVariantNumeric: 'tabular-nums' }}>{fmt(c.revenue || c.monthRevenue || 0)}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: T.text3, marginBottom: 4, fontSize: 13 }}>활성 회원</div>
                                            <div style={{ color: T.text1, fontWeight: 700, fontSize: 16 }}>{missingOr(c.totalMembers, c.degraded, 'totalMembers', () => `${c.totalMembers || 0}명`)}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: T.text3, marginBottom: 4, fontSize: 13 }}>강사</div>
                                            <div style={{ color: T.text1, fontWeight: 700, fontSize: 16 }}>{c.trainers || 0}명</div>
                                        </div>
                                        <div>
                                            <div style={{ color: T.text3, marginBottom: 4, fontSize: 13 }}>미수업금액</div>
                                            <div style={{ color: c.undeliveredRevenue > 0 ? T.error : T.text4, fontWeight: 700, fontSize: 16, fontVariantNumeric: 'tabular-nums' }}>{withDegraded(c.undeliveredRevenue > 0 ? fmt(c.undeliveredRevenue) : '-', c.degraded, 'undeliveredRevenue')}</div>
                                        </div>
                                    </div>
                                    {canOpenCenterView && (
                                    <button
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            openCenterView(c);
                                        }}
                                        disabled={openingCenterViewId === String(c.centerId || c.id)}
                                        style={{
                                            width: '100%',
                                            marginTop: 15,
                                            padding: '8px 10px',
                                            borderRadius: 8,
                                            border: `1px solid ${T.blueBorder}`,
                                            background: T.blueBg,
                                            color: T.blue,
                                            fontSize: 13,
                                            fontWeight: 700,
                                            cursor: openingCenterViewId ? 'wait' : 'pointer',
                                        }}
                                    >
                                        {openingCenterViewId === String(c.centerId || c.id) ? '여는 중...' : '관리자 화면 보기 ↗'}
                                    </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        )}
                    </>
                )}

                {/* Center Detail Screen — loading skeleton while restoring from URL */}
                {screen === 'centerDetail' && !selectedCenter && urlCenterId && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                            <div style={{ height: 32, width: 120, background: T.bg, borderRadius: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                            <div style={{ height: 16, width: 8, background: T.bg, borderRadius: 4 }} />
                            <div style={{ height: 20, width: 180, background: T.bg, borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 22 }}>
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} style={{ background: T.card, borderRadius: 12, padding: '14px 16px', border: `1px solid ${T.cardBorder}`, overflow: 'hidden', position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: T.border }} />
                                    <div style={{ height: 13, width: '50%', background: T.bg, borderRadius: 4, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                                    <div style={{ height: 20, width: '70%', background: T.bg, borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
                                </div>
                            ))}
                        </div>
                        <div style={{ background: T.card, borderRadius: 14, padding: '24px 28px', border: `1px solid ${T.cardBorder}`, marginBottom: 20, height: 400 }}>
                            <div style={{ height: 22, width: 120, background: T.bg, borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        </div>
                    </div>
                )}
                {screen === 'centerDetail' && !selectedCenter && !urlCenterId && (
                    <div style={{ textAlign: 'center', padding: '60px 0', color: T.text3 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>센터를 선택해주세요</div>
                        <button onClick={() => setScreen('centerPicker')} style={{ padding: '8px 20px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.card, color: T.blue, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>센터 지표로 이동</button>
                    </div>
                )}
                {screen === 'centerDetail' && selectedCenter && (() => {
                    // KPI용 실시간 데이터 (항상 현재 월 기준, 랭킹 필터 무관)
                    const kpiCenter = kpiCentersRaw?.find((c: any) => c.centerId === selectedCenter.centerId || c.centerName === selectedCenter.name);
                    const baseCenter = pickerCenters.find((c: any) => c.centerId === selectedCenter.centerId || c.name === selectedCenter.name) || selectedCenter;
                    const pf = kpiCenter?.periodFiltered || {};
                    const snap = kpiCenter?.snapshot || {};
                    const liveDegraded: string[] = kpiCenter ? readDegraded(kpiCenter) : baseCenter.degraded || [];
                    const liveCenter = {
                        ...baseCenter,
                        dayRevenue: kpiCenter?.dayRevenue ?? baseCenter.dayRevenue ?? 0,
                        weekRevenue: kpiCenter?.weekRevenue ?? baseCenter.weekRevenue ?? 0,
                        monthRevenue: kpiCenter?.monthRevenue ?? baseCenter.monthRevenue ?? 0,
                        prevDayRevenue: kpiCenter?.prevDayRevenue ?? 0,
                        prevWeekRevenue: kpiCenter?.prevWeekRevenue ?? 0,
                        prevMonthRevenue: kpiCenter?.prevMonthRevenue ?? 0,
                        undeliveredRevenue: snap.undeliveredRevenue ?? baseCenter.undeliveredRevenue ?? 0,
                        // 빈 값(집계 실패)은 빈 값 그대로 — `??` 로 다른 값이나 0 에 떨어뜨리지 않는다
                        totalMembers:
                            snap.totalMembers !== undefined
                                ? countOrMissing(snap.totalMembers)
                                : baseCenter.totalMembers === null
                                ? null
                                : baseCenter.totalMembers ?? 0,
                        trainers: snap.trainers ?? baseCenter.trainers ?? 0,
                        newPayMembers: pf.newPayMembers ?? 0,
                        newPayRevenue: pf.newPayRevenue ?? 0,
                        reRegPayMembers: pf.reRegPayMembers ?? 0,
                        reRegPayRevenue: pf.reRegPayRevenue ?? 0,
                        avgPricePerClass: avgPriceOrMissing(pf.avgPricePerClass, liveDegraded),
                        journalWriteRate: pf.journalWriteRate ?? null,
                        degraded: liveDegraded,
                        // 재등록 성공률 — KPI 는 항상 이번 달(overview monthly·baseDate 없음) 기준
                        ...toCenterReRegistrationSuccessFields(kpiCenter?.reRegistrationSuccess),
                    };
                    return (
                    <CenterDetailView
                        center={liveCenter}
                        trainers={activeTrainers.filter((t: any) => t.center === liveCenter.name)}
                        onBack={() => setScreen(prevScreen)}
                        backLabel={prevScreen === 'detail' ? '세부 지표' : prevScreen === 'centerPicker' ? '센터 지표' : '총괄 대시보드'}
                        rankingPeriod={cdTrainerRankPeriod}
                        onRankingPeriodChange={setCdTrainerRankPeriodStable}
                        baseDate={cdTrainerRankBase}
                        onBaseDateChange={setCdTrainerRankBaseStable}
                        selectedWeek={cdTrainerRankWeek}
                        onWeekChange={setCdTrainerRankWeek}
                        selectedDay={cdTrainerRankDay}
                        onDayChange={setCdTrainerRankDay}
                        includeGroup={includeGroup}
                        canOpenCenterView={canOpenCenterView}
                        onOpenCenterView={openCenterView}
                        isOpeningCenterView={openingCenterViewId === String(liveCenter.centerId || liveCenter.id)}
                    />
                    );
                })()}

                {screen === 'detail' && (() => {
                    const detailPeriodLabel = ({ yearly: '연', monthly: '월', weekly: '주', daily: '일' } as Record<string,string>)[detailPeriod] || '월';
                    return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', rowGap: 12, columnGap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap', rowGap: 10 }}>
                                <div style={{ display: 'flex', gap: 2, background: T.card, borderRadius: 8, padding: 3, border: `1px solid ${T.border}`, flexShrink: 0 }}>
                                    {[
                                        { key: 'center', label: '지점별' },
                                        { key: 'trainer', label: '강사별' },
                                    ].map((t) => (
                                        <button
                                            key={t.key}
                                            onClick={() => setDetailTab(t.key)}
                                            style={{
                                                padding: '7px 20px',
                                                borderRadius: 6,
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: 14,
                                                fontWeight: 600,
                                                background: detailTab === t.key ? T.blue : 'transparent',
                                                color: detailTab === t.key ? '#fff' : T.text3,
                                            }}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                                <CenterMultiSelect centers={detailCenters} selected={detailTrainerFilter} onChange={setDetailTrainerFilter} />
                                <input
                                    type="text"
                                    value={detailSearch}
                                    onChange={(e) => setDetailSearch(e.target.value)}
                                    placeholder={detailTab === 'center' ? '지점명 검색' : '지점명 · 강사명 검색'}
                                    style={{
                                        padding: '7px 14px', borderRadius: 8, border: `1px solid ${T.border}`,
                                        fontSize: 14, color: T.text1, background: T.card, outline: 'none', width: 200,
                                    }}
                                />
                            </div>
                            <PeriodToggle value={detailPeriod} onChange={setDetailPeriodStable} loading={isDetailFetching} baseDate={detailBaseDate} onBaseDateChange={setDetailBaseDateStable} />
                        </div>
                        <div
                            style={{
                                marginBottom: 16,
                                padding: '10px 16px',
                                background: T.card,
                                borderRadius: 10,
                                border: `1px solid ${T.cardBorder}`,
                                boxShadow: T.cardShadow,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}
                        >
                            <span style={{ fontSize: 13, color: T.blue, fontWeight: 600 }}>
                                {detailPeriod === 'yearly' ? `${detailBaseDate.getFullYear()}년 · 연간` : `${detailBaseDate.getFullYear()}년 ${detailBaseDate.getMonth()+1}월 · ${detailPeriodLabel}간`}
                                {detailPeriod === 'weekly' && detailWeek != null && ` ${detailWeek}주차`}
                                {detailPeriod === 'daily' && detailDay != null && ` ${detailDay}일`}
                            </span>
                            <span style={{ fontSize: 13, color: T.text4 }}>|</span>
                            <span style={{ fontSize: 13, color: T.text3 }}>{detailTab === 'center' ? `${detailFilteredCenters.length}개 지점` : `${detailFilteredTrainers.length}명 강사`}</span>
                            <span style={{ fontSize: 13, color: T.text4 }}>|</span>
                            <span style={{ fontSize: 13, color: T.text4 }}>컬럼 클릭으로 정렬 · 활성 회원/미수업금액은 실시간 기준</span>
                        </div>
                        {/* 주별 서브탭 */}
                        {detailPeriod === 'weekly' && (() => {
                            const weeks = getWeeksOfMonth(detailBaseDate.getFullYear(), detailBaseDate.getMonth() + 1);
                            return weeks.length > 0 && (
                                <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
                                    {weeks.map((w) => {
                                        const isActive = detailWeek === w.week;
                                        const isLoading = isActive && isDetailFetching;
                                        return (
                                            <button key={w.week} onClick={() => setDetailWeek(w.week)} style={{
                                                padding: '5px 12px', borderRadius: 6, border: `1px solid ${isActive ? T.blue : T.border}`,
                                                cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                                background: isActive ? T.blueBg : 'transparent',
                                                color: isActive ? T.blue : T.text3,
                                                display: 'flex', alignItems: 'center', gap: 5,
                                            }}>
                                                {isLoading && <div style={{ width: 10, height: 10, border: `1.5px solid ${T.blueBorder}`, borderTopColor: T.blue, borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />}
                                                {w.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        {/* 일별 서브탭 */}
                        {detailPeriod === 'daily' && (() => {
                            const days = new Date(detailBaseDate.getFullYear(), detailBaseDate.getMonth() + 1, 0).getDate();
                            return (
                                <div style={{ display: 'flex', gap: 3, marginBottom: 14, flexWrap: 'wrap' }}>
                                    {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                                        const isActive = detailDay === d;
                                        const isLoading = isActive && isDetailFetching;
                                        return (
                                            <button key={d} onClick={() => setDetailDay(d)} style={{
                                                padding: '4px 8px', borderRadius: 6, border: `1px solid ${isActive ? T.blue : T.border}`,
                                                cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 500,
                                                background: isActive ? T.blueBg : 'transparent',
                                                color: isActive ? T.blue : T.text3, minWidth: 28, textAlign: 'center',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                                            }}>
                                                {isLoading && <div style={{ width: 8, height: 8, border: `1.5px solid ${T.blueBorder}`, borderTopColor: T.blue, borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />}
                                                {d}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        {detailTab === 'center' ? (
                            <SortableTable columns={centerColumns} data={detailFilteredCenters} defaultSort="revenue" />
                        ) : (
                            <SortableTable columns={trainerColumns} data={detailFilteredTrainers} defaultSort="revenue" />
                        )}
                    </>
                    );
                })()}
            </main>
        </div>
    );
}
